import {
  Activity,
  AlertTriangle,
  Beaker,
  BookOpenText,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  CircleDot,
  Copy,
  Dna,
  Download,
  FileText,
  FolderKanban,
  GitBranch,
  GripVertical,
  LayoutPanelLeft,
  LoaderCircle,
  LogOut,
  Maximize2,
  MessageSquareMore,
  Minimize2,
  Moon,
  Network,
  Palette,
  PanelRightClose,
  Plus,
  RefreshCw,
  Search,
  Send,
  Sparkles,
  Sun,
  Table2,
  X,
} from "lucide-react";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";
import { api } from "../../../web/src/api";
import type {
  ResearchStateConflict,
  ResearchStateCurrentEntry,
  ResearchStateEvidenceRef,
  ResearchStateNegativeFinding,
  ResearchStateProvenance,
  ResearchStateSection,
  ResearchStateStagingView,
  ResearchStateTraceEvent,
  ResearchStateView,
  UploadDocumentSelection,
} from "../../../web/src/api/types";
import { ScientificArtifactStudio } from "./ScientificArtifactStudio";
import {
  CadenceCommandPalette,
  type CadenceCommandPaletteAction,
} from "./CadenceCommandPalette";
import { CadenceOnboardingTour } from "./CadenceOnboardingTour";
import { DocumentReader } from "./DocumentReader";
import { DelegatedAgentsTrack } from "./DelegatedAgentsTrack";
import {
  ProjectWorkspaceTabs,
  useProjectWorkspaceTabs,
} from "./ProjectWorkspaceTabs";
import { ProjectBranchSwitcher } from "./ProjectBranchSwitcher";
import {
  ScientificUiKernel,
  scientificUiRowsFromViewArtifact,
} from "./ScientificUiKernel";
import {
  OpenScienceConversationTurn,
  type OpenSciencePublicAgentReply,
} from "./OpenScienceConversation";
import { OpenScienceComposer } from "./OpenScienceComposer";
import { controlledCommandInput } from "./composerSyntax";
import { ProjectInteractionPrompt } from "./ProjectInteractionPrompt";
import {
  isConceptualIllustrationArtifact,
  isCoordinateStructureArtifact,
  isImageArtifact,
} from "./adapter";
import {
  compareOrganizationAgentEvents,
  safeCollaborationPreview,
} from "./collaborationModel";
import type {
  AgentStatus,
  CadenceAgentEvent,
  ArtifactOpenMode,
  CadenceAgent,
  CadenceAttachment,
  CadenceArtifact,
  CadenceArtifactInquiryRequest,
  CadenceBioAppProps,
  CadenceBTWResponse,
  CadenceComposerCapability,
  CadenceComposerIntent,
  CadenceComposerReference,
  CadenceCollaborationInteraction,
  CadenceDelegatedAgent,
  CadenceContext,
  CadenceEvidence,
  CadenceProject,
  CadencePermissionRequest,
  CadencePublishedArtifactContent,
  CadencePublishedCodeContent,
  CadencePublishedArtifactDeliveryState,
  CadencePublishedDecisionContent,
  CadencePublishedTableContent,
  CadencePublishedArtifactPresentation,
  CadenceResidue,
  CadenceStableResidue,
  CadenceStructureFormat,
  CadenceTab,
  CadenceExternalEvidenceCandidateSource,
  CadenceExternalEvidenceExecutionOutcome,
  CadenceExternalEvidenceRetrievalStatus,
  CadenceTurn,
  CadenceTurnStatus,
  ScientificArtifactDocument,
  ScientificOperationResult,
  ScientificSelection,
  ScientificUiRow,
  StructureScientificSelection,
  TextScientificSelection,
  ReportTextReplacementProposal,
} from "./types";
import { scientificUiArtifactInquirySelection } from "./types";
import { useCollaborationPlayback } from "./useCollaborationPlayback";
import "./cadence.css";

const PdfEvidenceViewer = lazy(() => import("./PdfEvidenceViewer"));
const MolstarStructureViewer = lazy(() => import("./MolstarStructureViewer"));
const DocumentInspectionViewer = lazy(async () => {
  const module = await import("./DocumentInspectionViewer");
  return { default: module.DocumentInspectionViewer };
});

const researchPromptStarters = [
  {
    label: "剂量反应与数据质控",
    prompt:
      "请基于当前项目和已上传的剂量-反应数据，检查数据质量与异常点，报告关键药效参数，区分可支持的结论与不确定性，并提出下一轮最有判别力的实验。",
  },
  {
    label: "靶点证据三角验证",
    prompt:
      "请从遗传学、组学/功能学和临床转化三个维度评估当前靶点证据，列出一致与冲突信号、关键证据缺口，以及可以证伪当前假设的下一步验证。",
  },
  {
    label: "耐药机制与下一步实验",
    prompt:
      "请整合当前项目中的药理、组学和文献证据，提出按优先级排序的耐药机制假设；为每个假设给出支持与反对证据、区分实验和停止条件。",
  },
] as const;

const demoProjects: CadenceProject[] = [
  {
    id: "adc-03",
    name: "ADC-03 机制研究项目",
    subtitle: "ADC payload resistance",
    status: "running",
    owner: "张研究员",
    updatedAt: "2 分钟前",
  },
  {
    id: "her2",
    name: "HER2 突变体结构分析",
    subtitle: "Binding pocket review",
    status: "running",
    owner: "李研究员",
    updatedAt: "18 分钟前",
  },
  {
    id: "biomarker",
    name: "Biomarker 筛选项目",
    subtitle: "Candidate prioritisation",
    status: "paused",
    owner: "王研究员",
    updatedAt: "昨天",
  },
  {
    id: "model-c",
    name: "模型 C 对照实验",
    subtitle: "Cohort comparison",
    status: "complete",
    owner: null,
    updatedAt: "3 天前",
  },
];

const demoAgentSeeds: CadenceAgent[] = [
  {
    id: "literature-agent",
    name: "文献研究员",
    role: "Literature Analyst",
    status: "working",
    summary: "收集 ABCB1 与 ADC 耐药相关文献",
    finding: "发现 23 篇高相关来源，5 篇提及 ABCB1 个介导时序",
    accent: "var(--cadence-data-blue)",
  },
  {
    id: "genomics-agent",
    name: "数据分析员",
    role: "Data Analyst",
    status: "working",
    summary: "整合 RNA-seq 与表型数据",
    finding: "模型 B 中 ABCB1 上调 4.7 倍",
    accent: "var(--cadence-data-green)",
  },
  {
    id: "molecular-agent",
    name: "机制研究员",
    role: "Mechanism Analyst",
    status: "working",
    summary: "分析耐药机制与关键通路",
    finding: "ABCB1 上调与药物外排增强相关",
    accent: "var(--cadence-brand)",
  },
  {
    id: "protein-agent",
    name: "实验设计员",
    role: "Experimental Designer",
    status: "waiting",
    summary: "等待验证用药组合的实验约束",
    finding: "需要确认 payload resistance 的 readout",
    accent: "var(--cadence-data-ochre)",
  },
  {
    id: "data-agent",
    name: "证据策展人",
    role: "Evidence Curator",
    status: "complete",
    summary: "整理来源、证据质量与冲突",
    finding: "4 个来源已通过 provenance 检查",
    accent: "var(--cadence-data-violet)",
  },
  {
    id: "experiment-agent",
    name: "同行评审员",
    role: "Peer Reviewer",
    status: "paused",
    summary: "准备检查结论可复现性",
    finding: "等待结果文档进入版本队列",
    accent: "var(--cadence-data-rose)",
  },
  {
    id: "verification-agent",
    name: "蛋白组分析员",
    role: "Proteomics Analyst",
    status: "working",
    summary: "比对 residue-level signal",
    finding: "P-gp 口袋附近有 3 个候选位点",
    accent: "var(--cadence-data-blue)",
  },
  {
    id: "safety-agent",
    name: "统计顾问",
    role: "Statistical Advisor",
    status: "waiting",
    summary: "检查 cohort 分层假设",
    finding: "等待样本量与缺失值说明",
    accent: "var(--cadence-data-green)",
  },
  {
    id: "clinical-agent",
    name: "临床翻译员",
    role: "Translational Scientist",
    status: "working",
    summary: "映射机制发现至临床证据",
    finding: "尚未形成外推结论",
    accent: "var(--cadence-brand)",
  },
  {
    id: "assay-agent",
    name: "安全性研究员",
    role: "Safety Scientist",
    status: "complete",
    summary: "标记潜在安全性风险",
    finding: "已记录 2 项需要复核的风险",
    accent: "var(--cadence-data-ochre)",
  },
  {
    id: "review-agent",
    name: "网络分析员",
    role: "Network Analyst",
    status: "working",
    summary: "构建 pathway relationship map",
    finding: "ABCB1 与 5 个节点有直接关联",
    accent: "var(--cadence-data-violet)",
  },
  {
    id: "runtime-agent",
    name: "结论整合员",
    role: "Synthesis Lead",
    status: "waiting",
    summary: "等待全部证据包完成",
    finding: "不会在证据齐备前生成最终结论",
    accent: "var(--cadence-data-rose)",
  },
];

const demoArtifacts: CadenceArtifact[] = [
  {
    id: "rna",
    name: "RNA-seq 差异表达.ipynb",
    type: "Notebook",
    description: "ABCB1 differential expression notebook",
    updatedAt: "2 分钟前",
    size: "2.4 MB",
    owner: null,
    preview: {
      kind: "text",
      content:
        "## Differential expression\n\nABCB1 is elevated in cohort B.\n\n> Demo projection: replace with live artifact content.",
    },
    versions: [
      {
        id: "v3",
        label: "v3",
        createdAt: "2024-05-24 10:30",
        note: "normalized cohort labels",
      },
      { id: "v2", label: "v2", createdAt: "2024-05-23 18:12" },
    ],
    provenance: [
      {
        source: "RNA-seq / cohort-B",
        recordedAt: "2024-05-24 10:26",
        evidenceId: "ev-rna-01",
      },
    ],
  },
  {
    id: "flow",
    name: "Flow 结果汇总.csv",
    type: "CSV",
    description: "Flow cytometry summary table",
    updatedAt: "5 分钟前",
    size: "184 KB",
    owner: "数据分析员",
    preview: {
      kind: "table",
      content:
        "sample,model_a,model_b\\nA-01,8732,3106\\nA-02,9011,3281\\nB-01,8120,2988",
    },
    versions: [{ id: "v5", label: "v5", createdAt: "2024-05-24 10:22" }],
    provenance: [
      { source: "Flow cytometry export", recordedAt: "2024-05-24 10:18" },
    ],
  },
  {
    id: "ihc",
    name: "IHC 图像汇总",
    type: "Image",
    description: "IHC image set with labels",
    updatedAt: "16 分钟前",
    size: "32 MB",
    owner: null,
    preview: {
      kind: "image",
      content: "IHC image preview unavailable in live adapter.",
    },
    provenance: [
      { source: "IHC slide archive", recordedAt: "2024-05-24 09:44" },
    ],
  },
  {
    id: "mechanism-report",
    name: "机制验证方案.docx",
    type: "Docx",
    description: "Mechanism validation protocol",
    updatedAt: "32 分钟前",
    size: "640 KB",
    owner: "机制研究员",
    preview: {
      kind: "text",
      content:
        "Validation plan\\n\\n1. Confirm ABCB1 efflux response.\\n2. Compare payload resistance readout.",
    },
    versions: [{ id: "v1", label: "v1", createdAt: "2024-05-24 09:28" }],
    provenance: [
      { source: "Mechanism Analyst", recordedAt: "2024-05-24 09:27" },
    ],
  },
  {
    id: "evidence",
    name: "evidence-summary.md",
    type: "Report",
    description: "Evidence and provenance summary",
    updatedAt: "刚刚",
    size: "18 KB",
    owner: null,
    preview: {
      kind: "text",
      content:
        "# Evidence summary\n\nSource owners are unknown until an explicit owner field is returned by the API.",
    },
    versions: [
      {
        id: "v2",
        label: "v2",
        createdAt: "2024-05-24 10:34",
        note: "added provenance warnings",
      },
    ],
    provenance: [
      {
        source: "Current project evidence",
        recordedAt: "2024-05-24 10:34",
        evidenceId: "ev-current",
      },
    ],
  },
];

/**
 * Demo records deliberately reuse the ProjectTurn presentation instead of
 * manufacturing a dashboard of scientific conclusions. They are completed,
 * visibly demo-scoped examples: only the Live adapter may show a Runtime's
 * current execution state or scientific facts.
 */
const demoTurns: CadenceTurn[] = [
  {
    id: "demo-turn-evidence",
    requestId: "demo-request-evidence",
    projectId: "adc-03",
    kind: "project_investigation",
    input: "请比较模型 A 与模型 B 的 ADC-03 疗效，并找出最需要验证的耐药解释。",
    output:
      "这是演示记录：ABCB1 相关外排是待验证假设，不代表已经确认的科研结论。下一步应使用受控实验验证其与 ADC-03 疗效下降之间的因果关系。",
    status: "completed",
    createdAt: "2024-05-24T10:04:00Z",
    completedAt: "2024-05-24T10:08:00Z",
    attachments: [],
    routedAgentIds: ["literature-agent", "genomics-agent"],
    activityIds: ["demo-literature-evidence", "demo-data-expression"],
    artifactIds: ["evidence-table", "rna-seq-notebook"],
    evidenceIds: ["demo-evidence-01", "demo-evidence-02"],
  },
  {
    id: "demo-turn-plan",
    requestId: "demo-request-plan",
    projectId: "adc-03",
    kind: "research_plan",
    input: "基于当前示例证据，给出能证伪 ABCB1 外排假设的下一轮验证计划。",
    output:
      "这是演示计划：先定义 payload accumulation、ABCB1 perturbation 和预注册 readout；若缺少对照或结果相互矛盾，应保持假设未证实。",
    status: "completed",
    createdAt: "2024-05-24T10:10:00Z",
    completedAt: "2024-05-24T10:14:00Z",
    attachments: [],
    routedAgentIds: ["molecular-agent", "protein-agent"],
    activityIds: ["demo-mechanism-plan", "demo-designer-plan"],
    artifactIds: ["mechanism-report", "protein-structure"],
    evidenceIds: ["demo-evidence-03"],
    intent: { kind: "research_plan" },
  },
];

const demoAgentEvents: CadenceAgentEvent[] = [
  {
    key: "demo-literature-reasoning",
    seq: 1,
    tsMs: Date.parse("2024-05-24T10:04:20Z"),
    source: "demo_projection",
    projectId: "adc-03",
    agentId: "literature-agent",
    threadId: "demo-thread-literature",
    submissionId: "demo-literature-evidence",
    event: {
      type: "agent_reasoning",
      visibility: "public",
      reasoning_id: "demo-literature-summary",
      stream_state: "completed",
      text: "示例：先区分 ADC 进入、靶点表达和外排相关解释，再检查哪些来源能支持或反驳它们。",
    },
  },
  {
    key: "demo-literature-search-begin",
    seq: 2,
    tsMs: Date.parse("2024-05-24T10:04:26Z"),
    source: "demo_projection",
    projectId: "adc-03",
    agentId: "literature-agent",
    threadId: "demo-thread-literature",
    submissionId: "demo-literature-evidence",
    event: {
      type: "web_search_begin",
      call_id: "demo-literature-search",
      query: "ABCB1 ADC payload resistance evidence",
    },
  },
  {
    key: "demo-literature-search-end",
    seq: 3,
    tsMs: Date.parse("2024-05-24T10:05:11Z"),
    source: "demo_projection",
    projectId: "adc-03",
    agentId: "literature-agent",
    threadId: "demo-thread-literature",
    submissionId: "demo-literature-evidence",
    event: {
      type: "web_search_end",
      call_id: "demo-literature-search",
      query: "ABCB1 ADC payload resistance evidence",
    },
  },
  {
    key: "demo-literature-reply",
    seq: 4,
    tsMs: Date.parse("2024-05-24T10:05:20Z"),
    source: "demo_projection",
    projectId: "adc-03",
    agentId: "literature-agent",
    threadId: "demo-thread-literature",
    submissionId: "demo-literature-evidence",
    event: {
      type: "agent_message",
      message:
        "示例文献路径已整理。ABCB1 外排与 payload resistance 存在关联线索，但当前记录不构成因果证明。",
    },
  },
  {
    key: "demo-data-reasoning",
    seq: 5,
    tsMs: Date.parse("2024-05-24T10:05:28Z"),
    source: "demo_projection",
    projectId: "adc-03",
    agentId: "genomics-agent",
    threadId: "demo-thread-data",
    submissionId: "demo-data-expression",
    event: {
      type: "agent_reasoning",
      visibility: "public",
      reasoning_id: "demo-data-summary",
      stream_state: "completed",
      text: "示例：检查队列差异是否和 RNA-seq 表达及 Flow 表型方向一致。",
    },
  },
  {
    key: "demo-data-command-begin",
    seq: 6,
    tsMs: Date.parse("2024-05-24T10:05:32Z"),
    source: "demo_projection",
    projectId: "adc-03",
    agentId: "genomics-agent",
    threadId: "demo-thread-data",
    submissionId: "demo-data-expression",
    event: {
      type: "exec_command_begin",
      call_id: "demo-data-command",
      command: "summarize_expression --gene ABCB1 --cohorts A,B",
    },
  },
  {
    key: "demo-data-command-end",
    seq: 7,
    tsMs: Date.parse("2024-05-24T10:06:08Z"),
    source: "demo_projection",
    projectId: "adc-03",
    agentId: "genomics-agent",
    threadId: "demo-thread-data",
    submissionId: "demo-data-expression",
    event: {
      type: "exec_command_end",
      call_id: "demo-data-command",
      command: "summarize_expression --gene ABCB1 --cohorts A,B",
      status: "completed",
      stdout: "Demo only: cohort B direction is higher; validate normalization and batch effects.",
    },
  },
  {
    key: "demo-data-reply",
    seq: 8,
    tsMs: Date.parse("2024-05-24T10:06:16Z"),
    source: "demo_projection",
    projectId: "adc-03",
    agentId: "genomics-agent",
    threadId: "demo-thread-data",
    submissionId: "demo-data-expression",
    event: {
      type: "agent_message",
      message:
        "示例数据摘要显示模型 B 的 ABCB1 方向性信号更高；批次效应与样本量仍需由真实 Artifact 复核。",
    },
  },
  {
    key: "demo-mechanism-reasoning",
    seq: 9,
    tsMs: Date.parse("2024-05-24T10:10:18Z"),
    source: "demo_projection",
    projectId: "adc-03",
    agentId: "molecular-agent",
    threadId: "demo-thread-mechanism",
    submissionId: "demo-mechanism-plan",
    event: {
      type: "agent_reasoning",
      visibility: "public",
      reasoning_id: "demo-mechanism-summary",
      stream_state: "completed",
      text: "示例：将相关性拆成可干预的外排、积累和疗效 readout，并预先设置反证条件。",
    },
  },
  {
    key: "demo-mechanism-tool-begin",
    seq: 10,
    tsMs: Date.parse("2024-05-24T10:10:24Z"),
    source: "demo_projection",
    projectId: "adc-03",
    agentId: "molecular-agent",
    threadId: "demo-thread-mechanism",
    submissionId: "demo-mechanism-plan",
    event: {
      type: "mcp_tool_call_begin",
      call_id: "demo-mechanism-tool",
      invocation: { server: "pingmedical", tool: "draft_experiment_tree" },
    },
  },
  {
    key: "demo-mechanism-tool-end",
    seq: 11,
    tsMs: Date.parse("2024-05-24T10:11:02Z"),
    source: "demo_projection",
    projectId: "adc-03",
    agentId: "molecular-agent",
    threadId: "demo-thread-mechanism",
    submissionId: "demo-mechanism-plan",
    event: {
      type: "mcp_tool_call_end",
      call_id: "demo-mechanism-tool",
      invocation: { server: "pingmedical", tool: "draft_experiment_tree" },
      result: { status: "demo draft recorded" },
    },
  },
  {
    key: "demo-mechanism-reply",
    seq: 12,
    tsMs: Date.parse("2024-05-24T10:11:16Z"),
    source: "demo_projection",
    projectId: "adc-03",
    agentId: "molecular-agent",
    threadId: "demo-thread-mechanism",
    submissionId: "demo-mechanism-plan",
    event: {
      type: "agent_message",
      message:
        "示例计划建议并行测量 intracellular payload、ABCB1 perturbation 与疗效 readout；任一关键对照失败都应保留假设未证实。",
    },
  },
  {
    key: "demo-designer-reply",
    seq: 13,
    tsMs: Date.parse("2024-05-24T10:12:04Z"),
    source: "demo_projection",
    projectId: "adc-03",
    agentId: "protein-agent",
    threadId: "demo-thread-designer",
    submissionId: "demo-designer-plan",
    event: {
      type: "agent_message",
      message:
        "示例实验设计已写入验证方案 Artifact。实际执行前仍需补充剂量、对照、样本量与停止条件。",
    },
  },
];

const demoResidues: CadenceResidue[] = [
  { position: 68, aminoAcid: "G", note: "binding pocket" },
  { position: 72, aminoAcid: "F", note: "conserved" },
  { position: 75, aminoAcid: "L" },
  { position: 80, aminoAcid: "V" },
  { position: 84, aminoAcid: "A" },
  { position: 89, aminoAcid: "Y", note: "candidate" },
  { position: 93, aminoAcid: "P" },
  { position: 97, aminoAcid: "D" },
  { position: 103, aminoAcid: "S" },
  { position: 108, aminoAcid: "R", note: "candidate" },
  { position: 112, aminoAcid: "T" },
  { position: 118, aminoAcid: "K" },
];

function useControlledState<T>(
  controlled: T | undefined,
  initial: T,
  onChange?: (value: T) => void,
) {
  const [internal, setInternal] = useState(initial);
  const value = controlled === undefined ? internal : controlled;
  const setValue = (next: T) => {
    if (controlled === undefined) setInternal(next);
    onChange?.(next);
  };
  return [value, setValue] as const;
}

function usePersistedControlledState<T>(
  key: string,
  controlled: T | undefined,
  initial: T,
  onChange?: (value: T) => void,
  persist = true,
) {
  const readPersistedValue = useCallback(
    (storageKey: string): T => {
      if (!persist || typeof window === "undefined") return initial;
      try {
        const saved = window.localStorage.getItem(storageKey);
        return saved === null ? initial : (JSON.parse(saved) as T);
      } catch {
        return initial;
      }
    },
    [initial, persist],
  );
  const [internal, setInternal] = useState<T>(() => {
    if (controlled !== undefined) return initial;
    return readPersistedValue(key);
  });
  const [hydratedKey, setHydratedKey] = useState(key);
  const value = controlled === undefined ? internal : controlled;
  const setValue = (next: T | ((current: T) => T)) => {
    const resolved =
      typeof next === "function"
        ? (next as (current: T) => T)(value)
        : next;
    if (controlled === undefined) setInternal(resolved);
    onChange?.(resolved);
  };
  useEffect(() => {
    if (controlled !== undefined || hydratedKey === key) return;
    setInternal(readPersistedValue(key));
    setHydratedKey(key);
  }, [controlled, hydratedKey, key, readPersistedValue]);
  useEffect(() => {
    if (
      !persist ||
      controlled !== undefined ||
      typeof window === "undefined" ||
      hydratedKey !== key
    ) {
      return;
    }
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage can be unavailable in embedded/private contexts.
    }
  }, [controlled, hydratedKey, key, persist, value]);
  return [value, setValue] as const;
}

const TEAM_WIDTH_STORAGE_KEY = "cadence.team-width";
const CADENCE_THEME_STORAGE_KEY = "cadence.theme.v1";
const CADENCE_THEMES = ["light", "warm", "dark"] as const;
type CadenceTheme = (typeof CADENCE_THEMES)[number];
const CADENCE_THEME_LABELS: Record<CadenceTheme, string> = {
  light: "浅色",
  warm: "暖色",
  dark: "深色",
};
const MIN_TEAM_WIDTH = 300;
const FALLBACK_MAX_TEAM_WIDTH = 540;

function initialCadenceTheme(): CadenceTheme {
  if (typeof window === "undefined") return "light";
  try {
    const saved = JSON.parse(
      window.localStorage.getItem(CADENCE_THEME_STORAGE_KEY) ?? "null",
    );
    if (CADENCE_THEMES.includes(saved)) return saved;
  } catch {
    // A malformed local preference must not prevent the research workspace
    // from mounting; follow the operating system until the user chooses.
  }
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function hasPersistedValue(key: string) {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function measuredTeamWidthMax(layout: HTMLElement | null) {
  const content = layout?.querySelector<HTMLElement>(".cadence-content");
  const team = layout?.querySelector<HTMLElement>(".agent-team");
  if (
    !content ||
    !team ||
    content.clientWidth <= 0 ||
    team.clientWidth <= 0
  ) {
    return undefined;
  }
  return Math.max(
    MIN_TEAM_WIDTH,
    Math.floor((content.clientWidth + team.clientWidth) / 2),
  );
}

const statusLabel: Record<AgentStatus, string> = {
  working: "工作中",
  waiting: "等待中",
  paused: "暂停中",
  complete: "已完成",
  error: "异常",
  unknown: "状态未知",
};

const collaborationStateLabel = {
  idle: "空闲",
  working: "工作中",
  waiting: "等待中",
  communicating: "协作中",
  completed: "已完成",
  failed: "失败",
} as const;

const agentStatusPriority: Record<AgentStatus, number> = {
  error: 0,
  unknown: 1,
  waiting: 2,
  working: 3,
  paused: 4,
  complete: 5,
};

const identityAccents = [
  "var(--cadence-brand)",
  "var(--cadence-data-blue)",
  "var(--cadence-data-green)",
  "var(--cadence-data-ochre)",
  "var(--cadence-data-rose)",
  "var(--cadence-data-violet)",
] as const;

function identityAccent(identityId: string) {
  let hash = 0;
  for (const character of identityId) {
    hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  }
  return identityAccents[hash % identityAccents.length];
}

function AgentRosterCard({
  agent,
  active,
  compact,
  collaborationState,
  collaborationColor,
  onSelect,
}: {
  agent: CadenceAgent;
  active: boolean;
  compact: boolean;
  collaborationState?: "active" | "recent";
  collaborationColor?: string;
  onSelect: (agentId: string) => void;
}) {
  const summary = publicAgentSummary(agent.summary);
  return (
    <button
      data-testid={`agent-detail-${agent.id}`}
      data-agent-status={agent.status}
      data-agent-collaboration-state={agent.collaborationState ?? "idle"}
      data-collaboration-state={collaborationState}
      className={`agent-card ${compact ? "agent-card-compact" : ""} ${active ? "agent-card-active" : ""} ${collaborationState ? "agent-card-collaborating" : ""}`}
      type="button"
      onClick={() => onSelect(agent.id)}
      style={
        collaborationColor
          ? ({ "--collaboration-accent": collaborationColor } as CSSProperties)
          : undefined
      }
    >
      <span
        className="agent-avatar"
        style={{ background: identityAccent(agent.id) }}
      >
        {agent.avatar ?? agent.name.slice(0, 1)}
      </span>
      <span className="agent-card-body">
        <span className="agent-card-top">
          <strong>{agent.name}</strong>
          <span
            data-testid={`agent-status-${agent.id}`}
            className={`agent-status status-${agent.status}`}
            aria-label={`Agent 状态：${statusLabel[agent.status]}`}
          >
            <span className="status-dot" aria-hidden="true" />
          </span>
          {agent.collaborationState && (
            <span
              data-testid={`agent-collaboration-status-${agent.id}`}
              className={`agent-collaboration-status collaboration-state-${agent.collaborationState}`}
            >
              {collaborationStateLabel[agent.collaborationState]}
            </span>
          )}
        </span>
        {summary && <span className="agent-summary">{summary}</span>}
        {agent.progress !== undefined && (
          <span className="agent-progress">
            <span
              style={{
                width: `${Math.max(0, Math.min(100, agent.progress))}%`,
              }}
            />
          </span>
        )}
      </span>
    </button>
  );
}

function ownerLabel(owner?: string | null) {
  const value = owner?.trim();
  return value && value.toLowerCase() !== "unknown" ? value : undefined;
}

function publicAgentSummary(summary: string): string | undefined {
  const preview = sanitizeInternalArtifactReferences(
    safeCollaborationPreview(summary) ?? "",
  );
  return preview && preview !== "运行时摘要未公开" ? preview : undefined;
}

function isAgentAnalysisReport(artifact: CadenceArtifact): boolean {
  return /^assistant-[a-f0-9]{8,}\.md$/iu.test(artifact.name.trim());
}

function artifactDisplayName(artifact: CadenceArtifact): string {
  if (artifact.publishedPresentation?.title) {
    return artifact.publishedPresentation.title;
  }
  const name = artifact.name.trim();
  if (isAgentAnalysisReport(artifact)) return "Agent 分析报告";
  if (name === "research-state.json") return "研究状态记录";
  if (name === "biostat_request.json") return "生物统计分析请求";
  if (artifact.kind?.trim().toLowerCase() === "literature_evidence_bundle") {
    return "文献证据包";
  }
  return name;
}

function artifactPresentationLabel(artifact: CadenceArtifact): string {
  switch (artifact.publishedPresentation?.format) {
    case "document":
      return "Document";
    case "table":
      return "Table";
    case "figure":
      return "Figure";
    case "html":
      return "HTML";
    case "code":
      return "Code";
    case "decision":
      return "Decision";
    default:
      return artifact.type;
  }
}

function artifactDeliveryStateLabel(
  state: CadencePublishedArtifactDeliveryState | undefined,
): string | undefined {
  switch (state) {
    case "draft":
      return "草稿，待完善";
    case "blocked":
      return "受阻说明，未产生研究结果";
    default:
      return undefined;
  }
}

function timelineArtifactDisplayName(artifact: CadenceArtifact): string {
  if (/\.json$/iu.test(artifact.name.trim())) {
    return artifactDisplayName(artifact) === artifact.name.trim()
      ? "结构化研究 Artifact"
      : artifactDisplayName(artifact);
  }
  return artifactDisplayName(artifact);
}

function artifactCanonicalRevision(artifact: CadenceArtifact): number | undefined {
  return [
    artifact.rendererDocument?.revision,
    artifact.scientificDocument?.revision,
    artifact.descriptorRevision,
    artifact.sourceRevision,
  ]
    .map((revision) =>
      typeof revision === "number" &&
      Number.isSafeInteger(revision) &&
      revision >= 1
        ? revision
        : undefined,
    )
    .find((revision): revision is number => revision !== undefined);
}

function sameScientificSelection(
  current: ScientificSelection | undefined,
  next: ScientificSelection,
) {
  return (
    current !== undefined && JSON.stringify(current) === JSON.stringify(next)
  );
}

function hasArtifactInquirySelection(
  selection: ScientificSelection,
  descriptorRevision?: number,
) {
  if (selection.renderer === "report") return Boolean(selection.quote);
  if (selection.renderer === "structure") return selection.ids.length > 0;
  if (selection.renderer === "curve") return selection.ids.length > 0;
  if (selection.renderer === "flow") {
    return selection.ids.length > 0 || Boolean(selection.gateId);
  }
  if (selection.renderer === "scientific-ui") {
    return Boolean(
      scientificUiArtifactInquirySelection(selection, descriptorRevision),
    );
  }
  return false;
}

export function CadenceBioApp({
  className = "",
  mode = "demo",
  projects = mode === "demo" ? demoProjects : [],
  activeProjectId,
  agents: inputAgents,
  agentEvents = mode === "demo" ? demoAgentEvents : [],
  liveResearchActive = false,
  researchStartedAt,
  sessionRunning = false,
  projectRuntimeReady = sessionRunning,
  streamState = "idle",
  collaboration,
  turns = mode === "demo" ? demoTurns : [],
  evidence = [],
  warnings = [],
  permissions = [],
  activeTurnId,
  permissionError,
  artifacts = mode === "demo" ? demoArtifacts : [],
  composerCapabilities = [],
  availableAttachments = [],
  residues = mode === "demo" ? demoResidues : [],
  selectedAgentId,
  selectedArtifactId,
  openArtifactIds,
  besideArtifactId,
  artifactOpenRequest,
  selectedStableResidues,
  selectedResiduePositions,
  projectSidebarCollapsed,
  agentTeamCollapsed,
  agentTeamWidth,
  onProjectSelect,
  onAgentSelect,
  onArtifactOpen,
  onArtifactClose,
  onArtifactDetailRequest,
  artifactDetailStates,
  artifactDetailErrors,
  onArtifactDetailRetry,
  onResidueSelect,
  onStableResidueSelectionChange,
  onResidueSelectionChange,
  onProjectSidebarCollapse,
  onOpenArtifactIdsChange,
  onBesideArtifactChange,
  onAgentTeamCollapse,
  onAgentTeamWidthChange,
  onSendPrompt,
  onSteerPrompt,
  onSendBTW,
  onArtifactComposerSubmit,
  attachments = [],
  uploading = false,
  uploadError,
  onUploadFiles,
  onAttachProjectInput,
  onRemoveAttachment,
  onBTWResponseDismiss,
  btwResponse,
  projectListState = "ready",
  projectListError,
  projectContentState = "idle",
  projectSnapshotError,
  onRetryProjects,
  onRetryProject,
  creatingProject = false,
  createProjectError,
  projectNotice,
  onProjectNoticeDismiss,
  userError,
  onboardingActive = false,
  onboardingStep = 0,
  onOnboardingStepChange,
  onOnboardingDismiss,
  onOnboardingUnavailable,
  onOnboardingComplete,
  onOnboardingReplay,
  requestError,
  organizationError,
  onRetryOrganization,
  onPermissionDecision,
  onCancelTurn,
  turnsLoading = false,
  turnsLoadingOlder = false,
  turnsHasMore = false,
  turnsError,
  onRetryTurns,
  turnsRetryLabel,
  onLoadOlderTurns,
  scientificOperation,
  onRetryUpload,
  onCreateProject,
  localFolderImportAvailable = false,
  onSelectLocalFolder,
  onImportProject,
  onScientificArtifactApply,
  onReportTextReplacementApply,
  readOnly = false,
  composerDisabled = false,
  composerDisabledReason,
  researchStateViews = {},
  researchStateLoadingSections = [],
  researchStateErrors = {},
  onResearchStateOpen,
  onResearchStateSectionLoad,
  onResearchStateRefresh,
  onResearchStateArtifactOpen,
}: CadenceBioAppProps) {
  // Demo defaults are supplied by the prop default above; live mode must preserve
  // an empty runtime roster instead of silently substituting demo projects.
  const projectList = projects;
  const projectScopeId = activeProjectId ?? projectList[0]?.id ?? "none";
  const projectOwnerScope =
    projectList.find((item) => item.id === projectScopeId)?.ownerUserId ??
    projectList.find((item) => item.ownerUserId)?.ownerUserId ??
    "anonymous";
  const [projectId, setProjectId] = usePersistedControlledState(
    "cadence.project-view",
    activeProjectId,
    projectList[0]?.id ?? "",
    onProjectSelect,
  );
  const {
    activeTab: activeWorkspaceTab,
    tabs: workspaceTabs,
    activate: activateWorkspaceTab,
    close: closeWorkspaceTab,
    createDraft: createWorkspaceDraft,
    openArtifact: openWorkspaceArtifact,
  } = useProjectWorkspaceTabs(projectOwnerScope, projectScopeId);
  const [theme, setTheme] = usePersistedControlledState<CadenceTheme>(
    CADENCE_THEME_STORAGE_KEY,
    undefined,
    initialCadenceTheme(),
  );
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const commandPaletteTriggerRef = useRef<HTMLButtonElement>(null);
  // Both Live and Demo use the same ProjectTurn reading surface. Demo may
  // demonstrate completed records, but it must not resurrect the retired
  // dashboard as an alternative product mental model.
  const visibleTab: CadenceTab = "results";
  const [sidebarCollapsed, setSidebarCollapsed] = usePersistedControlledState(
    "cadence.sidebar-collapsed",
    projectSidebarCollapsed,
    false,
    onProjectSidebarCollapse,
  );
  const [teamCollapsed, setTeamCollapsed] = usePersistedControlledState(
    "cadence.team-collapsed",
    agentTeamCollapsed,
    false,
    onAgentTeamCollapse,
  );
  const [teamWidthPersistenceEnabled, setTeamWidthPersistenceEnabled] =
    useState(
      () =>
        agentTeamWidth !== undefined ||
        hasPersistedValue(TEAM_WIDTH_STORAGE_KEY),
    );
  const [teamWidth, setStoredTeamWidth] = usePersistedControlledState(
    TEAM_WIDTH_STORAGE_KEY,
    agentTeamWidth,
    328,
    onAgentTeamWidthChange,
    teamWidthPersistenceEnabled,
  );
  const setTeamWidth = useCallback(
    (next: number) => {
      setTeamWidthPersistenceEnabled(true);
      setStoredTeamWidth(next);
    },
    [setStoredTeamWidth],
  );
  const [rosterSize, setRosterSize] = useState(4);
  const [compactMode, setCompactMode] = usePersistedControlledState(
    "cadence.team-compact",
    undefined,
    false,
  );
  const [activeOnly, setActiveOnly] = usePersistedControlledState(
    "cadence.team-active-only",
    undefined,
    false,
  );
  const [activeAgentId, setActiveAgentId] = useControlledState(
    selectedAgentId,
    null as string | null,
    onAgentSelect,
  );
  const [activeDelegatedAgentId, setActiveDelegatedAgentId] = useState<
    string | undefined
  >();
  // Demo opens one declared coordinate Artifact on arrival so the reading
  // surface demonstrates the real Inspector/renderer handoff. Live Projects
  // remain event-driven: only a later server-associated Artifact may move the
  // Inspector, never a reconstructed history item.
  const demoInitialArtifactId =
    mode === "demo"
      ? artifacts.find((artifact) => isCoordinateStructureArtifact(artifact))
          ?.id
      : undefined;
  const [activeArtifactId, setActiveArtifactId] = usePersistedControlledState(
    `cadence.${projectScopeId}.active-artifact`,
    selectedArtifactId,
    demoInitialArtifactId,
    undefined,
    mode !== "demo",
  );
  const [artifactWorkspaceDismissed, setArtifactWorkspaceDismissed] =
    useState(false);
  const [openIds, setOpenIds] = usePersistedControlledState(
    `cadence.${projectScopeId}.open-artifacts`,
    openArtifactIds,
    demoInitialArtifactId ? [demoInitialArtifactId] : [],
    onOpenArtifactIdsChange,
    mode !== "demo",
  );
  const [besideId, setBesideId] = usePersistedControlledState(
    `cadence.${projectScopeId}.beside-artifact`,
    besideArtifactId,
    undefined as string | undefined,
    onBesideArtifactChange,
  );
  const [residuePositions, setResiduePositions] = usePersistedControlledState(
    `cadence.${projectScopeId}.residue-positions`,
    selectedResiduePositions,
    [] as number[],
    onResidueSelectionChange,
  );
  // Draft text is a local workbench convenience. It remains scoped to the
  // existing Project and never creates a ProjectTurn, Session, or Sandbox
  // before the researcher explicitly sends it.
  const [composerValue, setComposerValue] = usePersistedControlledState(
    `cadence.${projectOwnerScope}.${projectScopeId}.${activeWorkspaceTab.id}.composer-draft`,
    undefined,
    "",
  );
  // Reference chips are draft context, not Project facts. Keeping them with
  // the Project-scoped draft lets the researcher return to the same workbench
  // without silently creating a turn, attachment, or Runtime invocation.
  const [composerReferences, setComposerReferences] =
    usePersistedControlledState<CadenceComposerReference[]>(
      `cadence.${projectOwnerScope}.${projectScopeId}.${activeWorkspaceTab.id}.composer-references`,
      undefined,
      [],
    );
  const [expandedProjectHistories, setExpandedProjectHistories] =
    usePersistedControlledState<Record<string, boolean>>(
      `cadence.${projectOwnerScope}.project-history-expanded`,
      undefined,
      {},
    );
  const [composerIntent, setComposerIntent] = useState<CadenceComposerIntent>({
    kind: "project_investigation",
  });
  const composerInputRef = useRef<HTMLTextAreaElement>(null);
  const [btwValue, setBtwValue] = useState("");
  const [btwSending, setBtwSending] = useState(false);
  const [btwSendError, setBtwSendError] = useState<{
    agentId: string;
    message: string;
  }>();
  const [lastSent, setLastSent] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string>();
  const [permissionAction, setPermissionAction] = useState<string>();
  const [permissionActionError, setPermissionActionError] = useState<string>();
  const [cancellingTurn, setCancellingTurn] = useState(false);
  const [cancelError, setCancelError] = useState<string>();
  const [resizing, setResizing] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createQuestion, setCreateQuestion] = useState("");
  const [createValidationError, setCreateValidationError] = useState<string>();
  const [folderSelection, setFolderSelection] = useState<{
    selectionId: string;
    name: string;
  }>();
  const [selectingFolder, setSelectingFolder] = useState(false);
  const [importingFolder, setImportingFolder] = useState(false);
  const [folderImportError, setFolderImportError] = useState<string>();
  const [mobileProjectMenuOpen, setMobileProjectMenuOpen] = useState(false);
  const [mobileComposerExpanded, setMobileComposerExpanded] = useState(false);
  const [mobileArtifactInquiryExpanded, setMobileArtifactInquiryExpanded] =
    useState(false);
  const [mobileViewport, setMobileViewport] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 800px)").matches,
  );
  const [researchStateOpen, setResearchStateOpen] = useState(false);
  const createDialogReturnFocusRef = useRef<HTMLElement | null>(null);
  const createDialogRef = useRef<HTMLElement>(null);
  const importDialogRef = useRef<HTMLElement>(null);
  const mobileProjectMenuRef = useRef<HTMLElement>(null);
  const mobileProjectTriggerRef = useRef<HTMLButtonElement>(null);
  const researchStateReturnFocusRef = useRef<HTMLElement | null>(null);
  const researchStateDrawerRef = useRef<HTMLElement>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string>();
  const [scientificSelections, setScientificSelections] = useState<
    Record<string, ScientificSelection>
  >({});
  const [documentSelections, setDocumentSelections] = useState<
    Record<string, UploadDocumentSelection>
  >({});
  const [documentViewerAttachmentId, setDocumentViewerAttachmentId] =
    useState<string>();
  const [artifactComposerArtifactId, setArtifactComposerArtifactId] =
    useState<string>();
  const handledArtifactOpenRequestRef = useRef<number>();
  const [liveRightPanel, setLiveRightPanel] = useState<"artifacts" | "agents">(
    () =>
      artifacts.length > 0 || (mode === "live" && liveResearchActive)
        ? "artifacts"
        : "agents",
  );
  const liveRightPanelUserSelectedRef = useRef(false);
  const hadLiveArtifactsRef = useRef(
    artifacts.length > 0 || (mode === "live" && liveResearchActive),
  );
  const layoutRef = useRef<HTMLDivElement>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const followTimelineRef = useRef(false);
  const pendingTimelineAnchorRef = useRef<{
    height: number;
    top: number;
    element?: HTMLElement;
    elementTop?: number;
  }>();
  const previousTimelineProgressRef = useRef(new Map<string, string>());
  const [timelineUnreadUpdates, setTimelineUnreadUpdates] = useState(0);
  const projectTurnArtifactArrivalRef = useRef<{
    projectId?: string;
    initialized: boolean;
    artifactIdsByTurn: Map<string, string[]>;
    latestTurnId?: string;
    pendingArtifactId?: string;
  }>({ initialized: false, artifactIdsByTurn: new Map() });
  const artifactOpenerRef = useRef<HTMLElement | null>(null);
  const documentInspectionReturnFocusRef = useRef<HTMLElement | null>(null);
  const pendingArtifactInquiryFocusRef = useRef<string>();
  const [artifactInquiryFocusSequence, setArtifactInquiryFocusSequence] =
    useState(0);

  const selectTheme = useCallback(
    (candidate: CadenceTheme) => {
      if (candidate === theme) return;
      setTheme(candidate);
    },
    [setTheme, theme],
  );

  useEffect(() => {
    // Theme is local presentation preference only. It must not be attached to
    // a ProjectTurn, Artifact, or Runtime request, where it could be mistaken
    // for scientific state shared with collaborators.
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme === "dark" ? "dark" : "light";
  }, [theme]);

  const openCreateDialog = () => {
    setCreateValidationError(undefined);
    setCreateDialogOpen(true);
  };
  const closeMobileProjectMenu = useCallback((restoreFocus = true) => {
    setMobileProjectMenuOpen(false);
    if (!restoreFocus) return;
    window.setTimeout(() => {
      const trigger = mobileProjectTriggerRef.current;
      if (trigger?.isConnected && !trigger.hasAttribute("disabled")) {
        trigger.focus();
      }
    }, 0);
  }, []);
  const closeCreateDialog = useCallback(() => {
    setCreateDialogOpen(false);
    window.setTimeout(() => {
      const originalTrigger = createDialogReturnFocusRef.current;
      const trigger = originalTrigger?.isConnected
        ? originalTrigger
        : document.querySelector<HTMLElement>(
            '[data-testid="mobile-new-project"], [data-testid="desktop-new-project"]',
          );
      if (trigger && !trigger.hasAttribute("disabled")) trigger.focus();
    }, 0);
  }, []);
  const closeImportDialog = useCallback(() => {
    setFolderSelection(undefined);
    setFolderImportError(undefined);
    window.setTimeout(() => {
      const trigger = createDialogReturnFocusRef.current;
      if (trigger?.isConnected && !trigger.hasAttribute("disabled")) {
        trigger.focus();
      }
    }, 0);
  }, []);
  const closeResearchState = useCallback(() => {
    setResearchStateOpen(false);
    window.setTimeout(() => {
      const trigger = researchStateReturnFocusRef.current;
      if (trigger?.isConnected && !trigger.hasAttribute("disabled")) {
        trigger.focus();
      }
    }, 0);
  }, []);
  const openResearchState = (
    trigger: HTMLButtonElement,
  ) => {
    researchStateReturnFocusRef.current = trigger;
    setResearchStateOpen(true);
    onResearchStateOpen?.();
  };
  const shortcutsAvailable =
    !createDialogOpen &&
    !folderSelection &&
    !mobileProjectMenuOpen &&
    !researchStateOpen &&
    !onboardingActive;

  useEffect(() => {
    if (mobileViewport || !shortcutsAvailable || commandPaletteOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.isComposing ||
        event.altKey ||
        !(event.metaKey || event.ctrlKey) ||
        event.key.toLocaleLowerCase() !== "b"
      ) {
        return;
      }
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest(
          'input, textarea, select, [contenteditable="true"], [data-testid="artifact-workspace"], .scientific-artifact-studio, .document-inspection-viewer, .molstar-structure-viewer',
        )
      ) {
        return;
      }
      event.preventDefault();
      setSidebarCollapsed(!sidebarCollapsed);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    commandPaletteOpen,
    mobileViewport,
    setSidebarCollapsed,
    shortcutsAvailable,
    sidebarCollapsed,
  ]);

  useEffect(() => {
    if (!createDialogOpen) return;
    const handleDialogKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeCreateDialog();
        return;
      }
      if (event.key !== "Tab") return;
      const dialog = createDialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (!focusable.length) return;
      const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);
      const nextIndex = event.shiftKey
        ? currentIndex <= 0
          ? focusable.length - 1
          : currentIndex - 1
        : currentIndex === focusable.length - 1
          ? 0
          : currentIndex + 1;
      event.preventDefault();
      focusable[nextIndex].focus();
    };
    document.addEventListener("keydown", handleDialogKeyDown);
    window.requestAnimationFrame(() => {
      createDialogRef.current
        ?.querySelector<HTMLInputElement>("input")
        ?.focus();
    });
    return () => document.removeEventListener("keydown", handleDialogKeyDown);
  }, [closeCreateDialog, createDialogOpen]);

  useEffect(() => {
    if (!createDialogOpen) return;
    const scrollY = window.scrollY;
    const { body } = document;
    const original = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    return () => {
      body.style.overflow = original.overflow;
      body.style.position = original.position;
      body.style.top = original.top;
      body.style.width = original.width;
      window.scrollTo(0, scrollY);
    };
  }, [createDialogOpen]);

  useEffect(() => {
    if (!folderSelection) return;
    const handleImportDialogKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !importingFolder) {
        event.preventDefault();
        closeImportDialog();
        return;
      }
      if (event.key !== "Tab") return;
      const dialog = importDialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (!focusable.length) return;
      const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);
      const nextIndex = event.shiftKey
        ? currentIndex <= 0
          ? focusable.length - 1
          : currentIndex - 1
        : currentIndex === focusable.length - 1
          ? 0
          : currentIndex + 1;
      event.preventDefault();
      focusable[nextIndex].focus();
    };
    document.addEventListener("keydown", handleImportDialogKeyDown);
    window.requestAnimationFrame(() => {
      importDialogRef.current?.querySelector<HTMLElement>("button")?.focus();
    });
    return () => document.removeEventListener("keydown", handleImportDialogKeyDown);
  }, [closeImportDialog, folderSelection, importingFolder]);

  useEffect(() => {
    if (!folderSelection) return;
    const scrollY = window.scrollY;
    const { body } = document;
    const original = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    return () => {
      body.style.overflow = original.overflow;
      body.style.position = original.position;
      body.style.top = original.top;
      body.style.width = original.width;
      window.scrollTo(0, scrollY);
    };
  }, [folderSelection]);

  useEffect(() => {
    if (!researchStateOpen) return;
    const handleDrawerKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeResearchState();
        return;
      }
      if (event.key !== "Tab") return;
      const drawer = researchStateDrawerRef.current;
      if (!drawer) return;
      const focusable = Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'button:not([disabled]), summary, [href], [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (!focusable.length) return;
      const currentIndex = focusable.indexOf(
        document.activeElement as HTMLElement,
      );
      const nextIndex = event.shiftKey
        ? currentIndex <= 0
          ? focusable.length - 1
          : currentIndex - 1
        : currentIndex === focusable.length - 1
          ? 0
          : currentIndex + 1;
      event.preventDefault();
      focusable[nextIndex].focus();
    };
    document.addEventListener("keydown", handleDrawerKeyDown);
    window.requestAnimationFrame(() => {
      researchStateDrawerRef.current
        ?.querySelector<HTMLElement>("[data-research-state-initial-focus]")
        ?.focus();
    });
    return () =>
      document.removeEventListener("keydown", handleDrawerKeyDown);
  }, [closeResearchState, researchStateOpen]);

  useEffect(() => {
    if (!researchStateOpen) return;
    const scrollY = window.scrollY;
    const { body } = document;
    const original = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    return () => {
      body.style.overflow = original.overflow;
      body.style.position = original.position;
      body.style.top = original.top;
      body.style.width = original.width;
      window.scrollTo(0, scrollY);
    };
  }, [researchStateOpen]);

  useEffect(() => {
    if (!mobileProjectMenuOpen) return;
    const handleMenuKeyDown = (event: KeyboardEvent) => {
      // A dialog, if externally opened during this transition, owns Escape.
      if (createDialogOpen) return;
      if (event.key === "Escape") {
        event.preventDefault();
        closeMobileProjectMenu();
        return;
      }
      if (event.key !== "Tab") return;
      const menu = mobileProjectMenuRef.current;
      if (!menu) return;
      const focusable = Array.from(
        menu.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (!focusable.length) return;
      const currentIndex = focusable.indexOf(
        document.activeElement as HTMLElement,
      );
      const nextIndex = event.shiftKey
        ? currentIndex <= 0
          ? focusable.length - 1
          : currentIndex - 1
        : currentIndex === focusable.length - 1
          ? 0
          : currentIndex + 1;
      event.preventDefault();
      focusable[nextIndex].focus();
    };
    const handleOutsidePointerDown = (event: PointerEvent) => {
      if (createDialogOpen) return;
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest(
          '[data-testid="mobile-project-menu"], [data-testid="mobile-project-menu-panel"]',
        )
      ) {
        return;
      }
      closeMobileProjectMenu();
    };
    document.addEventListener("keydown", handleMenuKeyDown);
    document.addEventListener("pointerdown", handleOutsidePointerDown);
    const scrollY = window.scrollY;
    const { body } = document;
    const original = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    const focusFrame = window.requestAnimationFrame(() => {
      if (createDialogOpen) return;
      mobileProjectMenuRef.current
        ?.querySelector<HTMLElement>("[data-mobile-project-menu-initial-focus]")
        ?.focus();
    });
    return () => {
      document.removeEventListener("keydown", handleMenuKeyDown);
      document.removeEventListener("pointerdown", handleOutsidePointerDown);
      window.cancelAnimationFrame(focusFrame);
      body.style.overflow = original.overflow;
      body.style.position = original.position;
      body.style.top = original.top;
      body.style.width = original.width;
      window.scrollTo(0, scrollY);
    };
  }, [closeMobileProjectMenu, createDialogOpen, mobileProjectMenuOpen]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 800px)");
    const updateViewport = () => setMobileViewport(media.matches);
    updateViewport();
    media.addEventListener("change", updateViewport);
    return () => media.removeEventListener("change", updateViewport);
  }, []);

  const agents = useMemo(() => {
    const source =
      inputAgents ??
      demoAgentSeeds.slice(
        0,
        mode === "demo" ? rosterSize : demoAgentSeeds.length,
      );
    return mode === "live"
      ? [...source]
      : [...source].sort(
          (left, right) =>
            agentStatusPriority[left.status] -
            agentStatusPriority[right.status],
        );
  }, [inputAgents, mode, rosterSize]);
  const playback = useCollaborationPlayback({
    interactions: mode === "live" ? (collaboration?.interactions ?? []) : [],
    agents,
    sessionKey: collaboration?.projectId ?? projectScopeId,
    enabled: mode === "live" && !teamCollapsed,
  });
  const displayAgents = playback.agents ?? agents;
  const interactionAgentIds = new Set(
    playback.interaction
      ? [
          playback.interaction.sourceAgentId,
          ...playback.interaction.targetAgentIds,
        ]
      : [],
  );
  const collaborationAgents = playback.interaction
    ? displayAgents.filter((agent) => interactionAgentIds.has(agent.id))
    : [];
  const standaloneAgents = displayAgents.filter(
    (agent) => !interactionAgentIds.has(agent.id),
  );
  const visibleStandaloneAgents = activeOnly
    ? standaloneAgents.filter(
        (agent) =>
          agent.status === "working" ||
          agent.status === "waiting" ||
          agent.status === "error",
      )
    : standaloneAgents;
  const visibleAgentCount =
    collaborationAgents.length + visibleStandaloneAgents.length;
  const project =
    projectList.find((item) => item.id === projectId) ?? projectList[0];
  const commandActions = useMemo<CadenceCommandPaletteAction[]>(
    () => [
      {
        id: "new-project",
        label: "新建研究项目",
        detail: "使用现有项目创建流程",
        icon: Plus,
        disabled:
          !onCreateProject || creatingProject || projectListState === "loading",
        run: () => {
          createDialogReturnFocusRef.current = commandPaletteTriggerRef.current;
          openCreateDialog();
        },
      },
      {
        id: "toggle-sidebar",
        label: sidebarCollapsed ? "展开项目侧栏" : "折叠项目侧栏",
        detail: "Cmd / Ctrl + B",
        icon: LayoutPanelLeft,
        disabled: mobileViewport,
        run: () => setSidebarCollapsed(!sidebarCollapsed),
      },
      {
        id: "focus-composer",
        label: "聚焦研究请求",
        detail: "直接继续当前项目的研究问题",
        icon: MessageSquareMore,
        disabled: composerDisabled || !project,
        run: () => composerInputRef.current?.focus(),
      },
      ...projectList.map((candidate) => ({
        id: `switch-project-${candidate.id}`,
        label: `切换至「${candidate.name}」`,
        detail:
          candidate.id === project?.id ? "当前研究项目" : "切换当前研究工作区",
        icon: FolderKanban,
        disabled: candidate.id === project?.id,
        run: () => setProjectId(candidate.id),
      })),
      {
        id: "replay-onboarding",
        label: "打开使用指引",
        detail: "重新查看研究工作区的核心流程",
        icon: CircleHelp,
        disabled: !onOnboardingReplay,
        run: () => onOnboardingReplay?.(),
      },
    ],
    [
      composerDisabled,
      creatingProject,
      mobileViewport,
      onCreateProject,
      onOnboardingReplay,
      project,
      projectListState,
      projectList,
      setSidebarCollapsed,
      setProjectId,
      sidebarCollapsed,
    ],
  );
  const activeAgent = agents.find((item) => item.id === activeAgentId);
  const delegatedAgents =
    mode === "live" ? collaboration?.delegatedAgents ?? [] : [];
  const delegatedChildThreadIds = useMemo(
    () => new Set(delegatedAgents.map((agent) => agent.childThreadId)),
    [delegatedAgents],
  );
  const activeDelegatedAgent = delegatedAgents.find(
    (agent) => agent.id === activeDelegatedAgentId,
  );
  const activeArtifact = artifacts.find((item) => item.id === activeArtifactId);
  const besideArtifact = artifacts.find((item) => item.id === besideId);
  const hasHistoricalOrganizationActivity =
    mode === "live" &&
    (agentEvents.length > 0 || Boolean(collaboration?.interactions.length));
  const artifactCatalogUnavailable =
    mode === "live" && projectContentState === "error";
  const teamCatalogUnavailable = mode === "live" && Boolean(organizationError);
  const snapshotRecoveryUnavailable =
    mode === "live" && Boolean(projectSnapshotError);
  const artifactCatalogKnown =
    mode === "demo" || projectContentState === "ready";

  // A successful empty Artifact list means no result has been produced yet;
  // a failed read only means the current Project state is unknown. Keeping
  // those meanings separate prevents a transient Runtime failure from being
  // presented as an empty team or an empty scientific record.
  const teamRosterUnavailable = teamCatalogUnavailable && agents.length === 0;
  const runtimeCatalogUnknown =
    artifactCatalogUnavailable && teamRosterUnavailable && artifacts.length === 0;
  const runtimeCatalogLoading =
    mode === "live" &&
    (projectContentState === "idle" || projectContentState === "loading") &&
    !teamCatalogUnavailable &&
    artifacts.length === 0 &&
    agents.length === 0;
  const showLiveArtifacts =
    artifacts.length > 0 ||
    artifactCatalogKnown ||
    artifactCatalogUnavailable ||
    (mode === "live" && liveResearchActive);
  // A directory refresh can fail after the Runtime has already supplied real
  // Artifact references. Those references remain useful research objects, so the
  // warning explains freshness without taking the researcher away from them.
  const artifactCatalogCanBeBrowsed =
    artifacts.length > 0 || artifactCatalogKnown;
  const showLiveRightTabs =
    !runtimeCatalogUnknown &&
    !runtimeCatalogLoading &&
    (showLiveArtifacts || hasHistoricalOrganizationActivity || agents.length > 0);
  const activeLiveRightPanel =
    !showLiveArtifacts && hasHistoricalOrganizationActivity
      ? "agents"
      : mode === "live" &&
          artifactCatalogCanBeBrowsed &&
          !liveRightPanelUserSelectedRef.current
        ? "artifacts"
        : liveRightPanel;
  useEffect(() => {
    if (mode !== "live") return;
    const artifactsAreBrowsable = artifactCatalogCanBeBrowsed;
    if (
      !hadLiveArtifactsRef.current &&
      artifactsAreBrowsable &&
      !liveRightPanelUserSelectedRef.current
    ) {
      setLiveRightPanel("artifacts");
    }
    hadLiveArtifactsRef.current = artifactsAreBrowsable;
  }, [artifactCatalogCanBeBrowsed, mode]);
  const artifactComposerArtifact = artifacts.find(
    (item) => item.id === artifactComposerArtifactId,
  );
  const documentViewerAttachment = attachments.find(
    (attachment) => attachment.id === documentViewerAttachmentId,
  );
  const attachmentsWithDocumentSelections = useMemo(
    () =>
      attachments.map((attachment) => {
        const documentSelection = attachment.id
          ? documentSelections[attachment.id]
          : undefined;
        return documentSelection
          ? { ...attachment, documentSelection }
          : attachment;
      }),
    [attachments, documentSelections],
  );
  useEffect(() => {
    const artifactIds = new Set(artifacts.map((artifact) => artifact.id));
    const requestIds = new Set(
      turns.flatMap((turn) => (turn.requestId ? [turn.requestId] : [])),
    );
    setComposerReferences((current) => {
      const next = current.filter(
        (reference) =>
          reference.kind === "artifact"
            ? artifactIds.has(reference.artifactId)
            : requestIds.has(reference.requestId),
      );
      // Refreshes create new ProjectTurn/Artifact arrays. Keep the draft state
      // referentially stable when every reference is still valid; otherwise a
      // harmless history refresh would recursively re-render the Composer.
      return next.length === current.length ? current : next;
    });
  }, [artifacts, setComposerReferences, turns]);
  const artifactComposerScientificUiSelection = artifactComposerArtifact
    ? scientificSelections[artifactComposerArtifact.id]
    : undefined;
  useEffect(() => {
    setMobileArtifactInquiryExpanded(false);
  }, [artifactComposerArtifactId]);
  const artifactWorkspaceOpen = Boolean(
    activeArtifact && openIds.includes(activeArtifact.id),
  );
  const artifactCatalogReady =
    mode === "demo" || projectContentState === "ready";

  useEffect(() => {
    setArtifactWorkspaceDismissed(false);
  }, [projectScopeId]);
  const activeScientificSelection = activeArtifact && !isImageArtifact(activeArtifact)
    ? scientificSelections[activeArtifact.id]
    : undefined;
  const canonicalArtifactRevision = activeArtifact
    ? artifactCanonicalRevision(activeArtifact)
    : undefined;
  const rendererBindings = activeArtifact?.rendererDocument?.bindings;
  const rawRendererBindingRefs =
    rendererBindings &&
    typeof rendererBindings === "object" &&
    !Array.isArray(rendererBindings)
      ? (rendererBindings as Record<string, unknown>).artifact_refs
      : undefined;
  const rendererBindingRefs =
    Array.isArray(rawRendererBindingRefs)
      ? rawRendererBindingRefs.filter(
          (reference): reference is string => typeof reference === "string",
        )
      : [];
  const presentationArtifactRefs =
    activeArtifact?.rendererDocument &&
    (activeArtifact.rendererDocument.renderer === "scientific-ui" ||
      activeArtifact.rendererDocument.renderer === "custom-app")
      ? [
          ...new Set([
            activeArtifact.path ?? `artifact://${activeArtifact.id}`,
            ...rendererBindingRefs,
          ]),
        ]
      : [];
  const artifactScopeRef = useRef(new Map<string, string>());
  useEffect(() => {
    setArtifactComposerArtifactId(undefined);
    setDocumentSelections({});
    setDocumentViewerAttachmentId(undefined);
  }, [projectScopeId]);
  useEffect(() => {
    const attachmentIds = new Set(
      attachments
        .map((attachment) => attachment.id)
        .filter((id): id is string => Boolean(id)),
    );
    setDocumentSelections((current) => {
      const next = Object.fromEntries(
        Object.entries(current).filter(([uploadId]) => attachmentIds.has(uploadId)),
      ) as Record<string, UploadDocumentSelection>;
      return Object.keys(next).every((uploadId) => next[uploadId] === current[uploadId]) &&
        Object.keys(next).length === Object.keys(current).length
        ? current
        : next;
    });
    setDocumentViewerAttachmentId((current) =>
      current && !attachmentIds.has(current) ? undefined : current,
    );
  }, [attachments]);
  useEffect(() => {
    if (
      artifactCatalogReady &&
      artifactComposerArtifactId &&
      !artifactComposerArtifact
    ) {
      setArtifactComposerArtifactId(undefined);
    }
  }, [
    artifactCatalogReady,
    artifactComposerArtifact,
    artifactComposerArtifactId,
  ]);
  useEffect(() => {
    if (!activeArtifact) return;
    const scope = [
      canonicalArtifactRevision ?? "unversioned",
      activeArtifact.contentSha256 ?? "unhashed",
      activeArtifact.coordinateSha256 ?? "unhashed",
    ].join(":");
    const previousScope = artifactScopeRef.current.get(activeArtifact.id);
    artifactScopeRef.current.set(activeArtifact.id, scope);
    if (
      previousScope === undefined ||
      previousScope === scope
    ) {
      return;
    }
    setScientificSelections((current) => {
      if (!current[activeArtifact.id]) return current;
      const next = { ...current };
      delete next[activeArtifact.id];
      return next;
    });
  }, [
    activeArtifact?.contentSha256,
    activeArtifact?.coordinateSha256,
    activeArtifact?.id,
    canonicalArtifactRevision,
  ]);
  const activeTurn =
    turns.find((turn) => turn.id === activeTurnId) ??
    (projectRuntimeReady
      ? turns.find(
          (turn) =>
            !turn.submissionUncertain &&
            (turn.status === "running" || turn.status === "pending"),
        )
      : undefined);
  // The Composer may expose Stop only for a durable, server-confirmed turn
  // that the existing session cancellation endpoint owns. An optimistic row or
  // Organization delivery must not acquire a fictitious cancellation control.
  const activeTurnRunning = Boolean(
    activeTurn &&
      !activeTurn.optimistic &&
      !activeTurn.submissionUncertain &&
      (activeTurn.status === "running" || activeTurn.status === "pending"),
  );
  const activeTurnCanCancel = Boolean(
    activeTurnRunning && activeTurn && onCancelTurn && timelineTurnCanCancel(activeTurn),
  );
  // A running Organization root is steerable only after the server exposed the
  // Planner-committed Lead. This protects the PASEO action from becoming a
  // second ProjectTurn or a browser-selected Agent route when state is stale.
  const activeTurnCanSteer = Boolean(
    mode === "live" &&
      activeTurnRunning &&
      activeTurn?.kind === "organization_root" &&
      activeTurn.requestId &&
      activeTurn.primaryAgentId &&
      onSteerPrompt,
  );
  const pinComposerOnMobile =
    mobileViewport &&
    mode === "live" &&
    !composerDisabled &&
    !mobileProjectMenuOpen;
  const mobileScientificUiArtifactInquiry =
    pinComposerOnMobile &&
    artifactComposerScientificUiSelection?.renderer === "scientific-ui";
  const mobileScientificUiInquiryCollapsed =
    mobileScientificUiArtifactInquiry && !mobileArtifactInquiryExpanded;
  const compactComposerOnMobile =
    mobileScientificUiInquiryCollapsed ||
    (pinComposerOnMobile &&
      !artifactComposerArtifact &&
      !activeAgent &&
      !mobileComposerExpanded &&
      !composerValue.trim() &&
      !attachments.length &&
      !sending &&
      !sendError &&
      !uploadError &&
      !permissions.length &&
      !permissionError &&
      !permissionActionError);
  // The project banner reports project-wide scientific facts only. A team-panel
  // refresh is a degradable observation: promoting it here would hide readable
  // Artifacts and make a single unavailable runtime detail look like a failed
  // Project. The team panel keeps its own recovery affordance below.
  const projectReadIssue =
    artifactCatalogUnavailable || snapshotRecoveryUnavailable;
  const projectReadIssueMessage =
    artifactCatalogUnavailable && snapshotRecoveryUnavailable
      ? "项目运行快照与产物目录暂时无法读取；已打开的产物不会关闭。"
      : artifactCatalogUnavailable
        ? "产物目录暂时无法读取；已打开的产物不会关闭。"
        : projectSnapshotError;
  const projectReadIssueIsBlocking = artifactCatalogUnavailable;
  const context: CadenceContext = {
    projectId: project?.id ?? "",
    sessionId: project?.sessionId ?? project?.id ?? "",
    workspacePath: project?.workspacePath,
    tab: visibleTab,
    agentId: activeAgent?.id,
    artifactId: activeArtifact?.id,
    artifactPath: activeArtifact?.path,
    artifactRevision:
      canonicalArtifactRevision,
    artifactExecutionId: activeArtifact?.executionId,
    presentationArtifactRefs,
    scientificSelection: activeScientificSelection,
    stableResidues:
      activeScientificSelection?.renderer === "structure"
        ? activeScientificSelection.residues
        : (selectedStableResidues ?? []),
    residuePositions,
  };
  const projectComposerContext: CadenceContext = artifactWorkspaceOpen
    ? {
        projectId: context.projectId,
        sessionId: context.sessionId,
        workspacePath: context.workspacePath,
        tab: context.tab,
        agentId: context.agentId,
        presentationArtifactRefs: [],
        scientificSelection:
          activeScientificSelection?.renderer === "custom-app"
            ? activeScientificSelection
            : undefined,
        stableResidues: [],
        residuePositions: [],
      }
    : context;
  useEffect(() => {
    setScientificSelections((current) => {
      let next: Record<string, ScientificSelection> | undefined;
      for (const [artifactId, selection] of Object.entries(current)) {
        const artifact = artifacts.find((item) => item.id === artifactId);
        if (
          artifact &&
          !isCoordinateStructureArtifact(artifact) &&
          selection.renderer === "structure"
        ) {
          next ??= { ...current };
          delete next[artifactId];
        }
      }
      return next ?? current;
    });
  }, [artifacts]);

  useEffect(() => {
    if (!artifactCatalogReady) return;
    const validOpenIds = openIds.filter((openId) =>
      artifacts.some((artifact) => artifact.id === openId),
    );
    const nextActiveId =
      activeArtifactId && validOpenIds.includes(activeArtifactId)
        ? activeArtifactId
        : artifactWorkspaceDismissed
          ? undefined
          : validOpenIds[0];
    const nextBesideId =
      besideId && validOpenIds.includes(besideId) ? besideId : undefined;
    if (
      validOpenIds.length !== openIds.length ||
      validOpenIds.some((openId, index) => openId !== openIds[index])
    ) {
      setOpenIds(validOpenIds);
    }
    if (nextActiveId !== activeArtifactId) {
      setActiveArtifactId(nextActiveId);
    }
    if (nextBesideId !== besideId) {
      setBesideId(nextBesideId);
    }
  }, [
    activeArtifactId,
    artifactCatalogReady,
    artifactWorkspaceDismissed,
    artifacts,
    besideId,
    openIds,
    setActiveArtifactId,
    setBesideId,
    setOpenIds,
  ]);

  useLayoutEffect(() => {
    if (!artifactWorkspaceOpen || !activeArtifact) return;
    setTeamCollapsed(false);
    setLiveRightPanel("artifacts");
  }, [activeArtifact?.id, artifactWorkspaceOpen, projectScopeId]);

  useEffect(() => {
    if (!resizing) return undefined;
    const onMove = (event: PointerEvent) => {
      const maxWidth =
        measuredTeamWidthMax(layoutRef.current) ?? FALLBACK_MAX_TEAM_WIDTH;
      const next = Math.max(
        MIN_TEAM_WIDTH,
        Math.min(maxWidth, window.innerWidth - event.clientX),
      );
      setTeamWidth(next);
    };
    const onUp = () => setResizing(false);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [resizing, setTeamWidth]);

  const setSelectedAgent = (id: string) => {
    setActiveAgentId(id);
  };

  const beginContinuation = (turn: CadenceTurn, agentId: string) => {
    if (!turn.requestId) return;
    setSelectedAgent(agentId);
    setComposerIntent({
      kind: "continue_research",
      parentRequestId: turn.requestId,
      targetAgentId: agentId,
    });
    requestAnimationFrame(() => composerInputRef.current?.focus());
  };

  const activateArtifact = useCallback(
    (id: string | undefined) => {
      setArtifactWorkspaceDismissed(false);
      setActiveArtifactId(id);
    },
    [setActiveArtifactId],
  );

  const openArtifact = useCallback(
    (id: string, openMode: ArtifactOpenMode) => {
      if (!activeArtifactId) {
        const activeElement = document.activeElement;
        if (
          activeElement instanceof HTMLElement &&
          activeElement !== document.body
        ) {
          artifactOpenerRef.current = activeElement;
        }
      }
      const nextIds = openIds.includes(id) ? openIds : [...openIds, id];
      let nextBesideId: string | undefined;
      const canCompareWithActive =
        openMode === "beside" &&
        Boolean(activeArtifactId) &&
        activeArtifactId !== id &&
        artifacts.some((artifact) => artifact.id === activeArtifactId);
      if (openMode === "beside") {
        nextBesideId = canCompareWithActive ? id : undefined;
      }
      setOpenIds(nextIds);
      if (openMode === "tab") {
        const artifact = artifacts.find((item) => item.id === id);
        if (artifact) openWorkspaceArtifact(id, artifactDisplayName(artifact));
      }
      // A comparison selection keeps the researcher's current Artifact as the
      // primary reading context. Choosing another Artifact must not silently
      // replace it just because the reference UI happens to open a second pane.
      if (!canCompareWithActive) activateArtifact(id);
      setBesideId(nextBesideId);
      setTeamCollapsed(false);
      setLiveRightPanel("artifacts");
      onArtifactOpen?.(id, openMode);
    },
    [
      artifacts,
      activeArtifactId,
      activateArtifact,
      onArtifactOpen,
      openIds,
      openWorkspaceArtifact,
      setActiveArtifactId,
      setBesideId,
      setOpenIds,
      setTeamCollapsed,
    ],
  );

  useEffect(() => {
    if (mode !== "live" || !artifactCatalogReady) {
      projectTurnArtifactArrivalRef.current = {
        initialized: false,
        artifactIdsByTurn: new Map(),
      };
      return;
    }
    const arrival = projectTurnArtifactArrivalRef.current;
    if (arrival.projectId !== projectScopeId) {
      arrival.projectId = projectScopeId;
      arrival.initialized = false;
      arrival.artifactIdsByTurn = new Map();
      arrival.latestTurnId = undefined;
      arrival.pendingArtifactId = undefined;
    }
    const currentByTurn = new Map(
      turns.map((turn) => [turn.id, turn.artifactIds ?? []]),
    );
    if (!arrival.initialized) {
      // The initial history is a reading surface, not a new Runtime delivery.
      // Only a later, server-associated Artifact should move the Inspector.
      arrival.initialized = true;
      arrival.artifactIdsByTurn = currentByTurn;
      arrival.latestTurnId = turns.at(-1)?.id;
      return;
    }
    const previousLatestIndex = arrival.latestTurnId
      ? turns.findIndex((turn) => turn.id === arrival.latestTurnId)
      : -1;
    const newlyAppendedArtifactIds =
      previousLatestIndex >= 0
        ? turns
            .slice(previousLatestIndex + 1)
            .flatMap((turn) => turn.artifactIds ?? [])
            .reverse()
        : [];
    const newlyAssociatedArtifactIds = turns
      .filter((turn) => arrival.artifactIdsByTurn.has(turn.id))
      .slice()
      .reverse()
      .flatMap((turn) => {
        const previousIds = new Set(arrival.artifactIdsByTurn.get(turn.id) ?? []);
        return (turn.artifactIds ?? []).filter((id) => !previousIds.has(id));
      });
    const nextArtifactId =
      newlyAppendedArtifactIds[0] ??
      newlyAssociatedArtifactIds[0] ??
      arrival.pendingArtifactId;
    arrival.artifactIdsByTurn = currentByTurn;
    arrival.latestTurnId = turns.at(-1)?.id;
    if (!nextArtifactId) return;
    if (
      liveRightPanelUserSelectedRef.current &&
      activeLiveRightPanel === "agents"
    ) {
      arrival.pendingArtifactId = undefined;
      return;
    }
    if (!artifacts.some((artifact) => artifact.id === nextArtifactId)) {
      arrival.pendingArtifactId = nextArtifactId;
      return;
    }
    arrival.pendingArtifactId = undefined;
    // A new Artifact is the durable result of this ProjectTurn. Present its
    // existing renderer in the Inspector; do not synthesize a chart or a
    // progress panel from the surrounding message text.
    openArtifact(nextArtifactId, "tab");
  }, [
    activeLiveRightPanel,
    artifactCatalogReady,
    artifacts,
    mode,
    openArtifact,
    projectScopeId,
    turns,
  ]);

  useEffect(() => {
    if (!artifactOpenRequest) return;
    if (handledArtifactOpenRequestRef.current === artifactOpenRequest.requestId)
      return;
    if (
      !artifacts.some(
        (artifact) => artifact.id === artifactOpenRequest.artifactId,
      )
    )
      return;
    handledArtifactOpenRequestRef.current = artifactOpenRequest.requestId;
    openArtifact(artifactOpenRequest.artifactId, "tab");
  }, [artifactOpenRequest, artifacts, openArtifact]);

  useEffect(() => {
    // Esc on the mobile Inspector only dismisses its transient reading surface;
    // it must retain the original opener until the researcher actually closes
    // the final persisted Artifact tab.
    if (activeArtifactId !== undefined || openIds.length > 0) return;
    const target = artifactOpenerRef.current;
    artifactOpenerRef.current = null;
    if (target?.isConnected && !target.hasAttribute("disabled")) {
      target.focus();
      return;
    }
    const artifactId = target?.getAttribute("data-artifact-id");
    if (!artifactId || typeof window === "undefined") return;

    // Closing the last inspector returns the scientist to the same Artifact in the
    // project browser. The opener's DOM node may be gone, but its research context
    // is still the same stable Artifact rather than a new or inferred destination.
    const frame = window.requestAnimationFrame(() => {
      const replacement = [
        ...document.querySelectorAll<HTMLElement>(
          '[data-testid="artifact-browser"] [data-artifact-id]',
        ),
      ].find((element) => element.dataset.artifactId === artifactId);
      if (replacement && !replacement.hasAttribute("disabled")) {
        replacement.focus();
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeArtifactId, openIds]);

  useEffect(() => {
    const artifactId = pendingArtifactInquiryFocusRef.current;
    if (
      !artifactId ||
      artifactId !== activeArtifactId ||
      !openIds.includes(artifactId)
    ) {
      return;
    }
    pendingArtifactInquiryFocusRef.current = undefined;
    window.requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>('[data-testid="composer"]')
        ?.querySelector<HTMLTextAreaElement>(
          '[data-testid="artifact-inquiry-input"]',
        )
        ?.focus();
    });
  }, [activeArtifactId, artifactInquiryFocusSequence, openIds]);

  const closeArtifact = (id: string) => {
    const nextIds = openIds.filter((item) => item !== id);
    setOpenIds(nextIds);
    if (besideId === id) setBesideId(undefined);
    if (activeArtifactId === id) activateArtifact(nextIds[0]);
    if (pendingArtifactInquiryFocusRef.current === id) {
      pendingArtifactInquiryFocusRef.current = undefined;
    }
    setArtifactComposerArtifactId((current) =>
      current === id ? undefined : current,
    );
    onArtifactClose?.(id);
  };

  const dismissArtifactWorkspace = useCallback(() => {
    // A mobile Inspector is a temporary reading surface. Dismissing it must
    // preserve the project's open Artifact tabs instead of treating a UI close
    // as a deletion or a new Artifact state transition.
    setArtifactWorkspaceDismissed(true);
    setBesideId(undefined);
    setActiveArtifactId(undefined);
  }, [setActiveArtifactId, setBesideId]);

  const toggleResidue = (position: number) => {
    const next = residuePositions.includes(position)
      ? residuePositions.filter((item) => item !== position)
      : [...residuePositions, position];
    setResiduePositions(next);
    onResidueSelect?.(position);
  };

  const closeDocumentInspection = useCallback(() => {
    setDocumentViewerAttachmentId(undefined);
    window.setTimeout(() => {
      const trigger = documentInspectionReturnFocusRef.current;
      if (trigger?.isConnected && !trigger.hasAttribute("disabled")) {
        trigger.focus();
      }
    }, 0);
  }, []);

  const openDocumentInspection = useCallback((attachment: CadenceAttachment) => {
    if (
      attachment.kind !== "upload" ||
      !attachment.id ||
      attachment.inspection?.status !== "derived_available" ||
      attachment.inspection.parser?.name !== "docling-serve"
    ) {
      return;
    }
    const trigger = document.activeElement;
    if (trigger instanceof HTMLElement && trigger !== document.body) {
      documentInspectionReturnFocusRef.current = trigger;
    }
    setDocumentViewerAttachmentId(attachment.id);
  }, []);

  const captureDocumentSelection = useCallback(
    (selection?: UploadDocumentSelection) => {
      const uploadId = documentViewerAttachment?.id;
      if (!uploadId) return;
      setDocumentSelections((current) => {
        if (!selection) {
          if (!current[uploadId]) return current;
          const next = { ...current };
          delete next[uploadId];
          return next;
        }
        if (selection.uploadId !== uploadId) return current;
        return { ...current, [uploadId]: selection };
      });
    },
    [documentViewerAttachment?.id],
  );

  const continueFromDocumentSelection = useCallback(
    (intent: "query" | "change_proposal") => {
      setArtifactComposerArtifactId(undefined);
      setDocumentViewerAttachmentId(undefined);
      if (intent === "change_proposal") {
        setComposerValue((current) =>
          current.trim()
            ? current
            : "请基于所选文档内容生成一份修改草案，保留页码和元素定位；不要修改原始文件。",
        );
      }
      setMobileComposerExpanded(true);
      window.setTimeout(() => composerInputRef.current?.focus(), 0);
    },
    [],
  );

  const sendPrompt = async () => {
    const input = composerValue.trim();
    const controlledCommand = controlledCommandInput(composerValue);
    const typedControlledCommand = Boolean(controlledCommand);
    const controlledCommandCapability = composerCapabilities.find(
      (capability) => capability.kind === "controlled_command",
    );
    const effectiveIntent: CadenceComposerIntent = typedControlledCommand
      ? { kind: "controlled_command", command: controlledCommand?.command.trim() ?? "" }
      : composerIntent;
    if (activeTurnRunning) {
      if (!input || sending || uploading) return;
      if (!activeTurnCanSteer || !activeTurn?.requestId || !onSteerPrompt) {
        setSendError(
          "当前请求不支持追加指令；请等待完成后再发起新的研究请求。",
        );
        return;
      }
      if (
        typedControlledCommand ||
        effectiveIntent.kind !== "project_investigation" ||
        attachments.length > 0 ||
        composerReferences.length > 0 ||
        activeAgent
      ) {
        setSendError(
          "运行中追加指令只支持纯文本，并且只发送给当前 Planner 选定的 Lead。请移除附件、引用、命令和指定专家后重试。",
        );
        return;
      }
      setMobileComposerExpanded(false);
      if (mobileViewport) composerInputRef.current?.blur();
      setSending(true);
      setSendError(undefined);
      setComposerValue("");
      try {
        await onSteerPrompt(activeTurn.requestId, input);
        setLastSent(input);
      } catch (cause) {
        setComposerValue((current) => current || input);
        if (mobileViewport) {
          setMobileComposerExpanded(true);
          window.requestAnimationFrame(() => composerInputRef.current?.focus());
        }
        setSendError(
          cause instanceof Error && cause.message.trim()
            ? cause.message
            : "追加指令发送失败，请重试。",
        );
      } finally {
        setSending(false);
      }
      return;
    }
    const requiresText = effectiveIntent.kind !== "project_investigation";
    if (
      (!input && (requiresText || !attachments.length)) ||
      sending ||
      uploading ||
      !onSendPrompt
    ) {
      return;
    }
    if (
      typedControlledCommand &&
      (!controlledCommandCapability || !controlledCommandCapability.available)
    ) {
      setSendError(
        "当前项目没有可用的受控 Runtime/Sandbox；不会将 ! 命令改为浏览器或本机 shell。",
      );
      return;
    }
    if (effectiveIntent.kind === "continue_research" && attachments.length) {
      setSendError(
        "继续研究目前不支持添加附件；请移除附件后重新提交为新的项目研究请求。",
      );
      return;
    }
    setMobileComposerExpanded(false);
    if (mobileViewport) composerInputRef.current?.blur();
    setSending(true);
    setSendError(undefined);
    setComposerValue("");
    try {
      await onSendPrompt!(
        input,
        projectComposerContext,
        attachmentsWithDocumentSelections,
        effectiveIntent,
        composerReferences,
      );
      setLastSent(input || "附件");
      setDocumentSelections({});
      if (effectiveIntent.kind !== "project_investigation") {
        setComposerIntent({ kind: "project_investigation" });
      }
    } catch (cause) {
      setComposerValue((current) => current || input);
      if (mobileViewport) {
        setMobileComposerExpanded(true);
        window.requestAnimationFrame(() => composerInputRef.current?.focus());
      }
      setSendError(
        cause instanceof Error && cause.message.trim()
          ? cause.message
          : "研究请求发送失败，请重试。",
      );
    } finally {
      setSending(false);
    }
  };

  const usesPageTimelineScroll = () =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 800px)").matches;

  const timelineScrollElement = () =>
    usesPageTimelineScroll()
      ? document.scrollingElement as HTMLElement | null
      : contentScrollRef.current;

  const updateTimelineFollow = useCallback(() => {
    const element = timelineScrollElement();
    if (!element || visibleTab !== "results") return;
    const nearBottom =
      element.scrollHeight - element.clientHeight - element.scrollTop <= 48;
    followTimelineRef.current = nearBottom;
    if (nearBottom) setTimelineUnreadUpdates(0);
  }, [visibleTab]);

  useEffect(() => {
    if (visibleTab !== "results" || !usesPageTimelineScroll()) return;
    const frame = window.requestAnimationFrame(updateTimelineFollow);
    window.addEventListener("scroll", updateTimelineFollow, { passive: true });
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", updateTimelineFollow);
    };
  }, [visibleTab, updateTimelineFollow]);

  useEffect(() => {
    if (visibleTab !== "results" || typeof ResizeObserver === "undefined") return;
    const element = timelineScrollElement();
    if (!element) return;
    const observer = new ResizeObserver(() => {
      if (followTimelineRef.current) element.scrollTop = element.scrollHeight;
    });
    observer.observe(element);
    const timeline = element.querySelector<HTMLElement>(".request-timeline");
    if (timeline) observer.observe(timeline);
    return () => observer.disconnect();
  }, [visibleTab, projectScopeId, mobileViewport]);

  const loadOlderTurns = async () => {
    const element = timelineScrollElement();
    if (!element || !onLoadOlderTurns || turnsLoadingOlder) return;
    const previousHeight = element.scrollHeight;
    const previousTop = element.scrollTop;
    const anchor = element.querySelector<HTMLElement>(".timeline-turn");
    pendingTimelineAnchorRef.current = {
      height: previousHeight,
      top: previousTop,
      element: anchor ?? undefined,
      elementTop: anchor?.getBoundingClientRect().top,
    };
    await onLoadOlderTurns();
  };

  const scrollToLatestTurn = () => {
    const element = timelineScrollElement();
    if (!element) return;
    element.scrollTop = element.scrollHeight;
    followTimelineRef.current = true;
    setTimelineUnreadUpdates(0);
  };

  const scrollToProjectTurn = useCallback(
    (turnId: string) => {
      window.requestAnimationFrame(() => {
        const turn = document.getElementById(`project-turn-${turnId}`);
        if (!turn) return;
        // Respect motion preferences for navigation too: CSS cannot override
        // the explicit smooth behavior passed to this DOM API.
        turn.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ? "auto"
            : "smooth",
          block: "start",
        });
        turn.focus({ preventScroll: true });
      });
    },
    [],
  );

  const toggleProjectHistory = useCallback(
    (projectId: string, openByDefault: boolean) => {
      setExpandedProjectHistories((current) => ({
        ...current,
        [projectId]: !(current[projectId] ?? openByDefault),
      }));
    },
    [setExpandedProjectHistories],
  );

  const timelineProgress = useMemo(
    () =>
      new Map(
        turns.map((turn) => [
          turn.id,
          timelineTurnProgressSignature(turn, agentEvents),
        ]),
      ),
    [agentEvents, turns],
  );
  const timelineSignature = [...timelineProgress.entries()]
    .map(([id, signature]) => `${id}:${signature}`)
    .join("|");
  useLayoutEffect(() => {
    followTimelineRef.current = false;
    pendingTimelineAnchorRef.current = undefined;
    previousTimelineProgressRef.current = new Map();
    setTimelineUnreadUpdates(0);
    const element = timelineScrollElement();
    if (element) element.scrollTop = 0;
  }, [projectScopeId]);
  useLayoutEffect(() => {
    // This receipt is transient UI feedback, not a durable research fact. The
    // Project turn timeline is authoritative, so it must not cross Projects.
    setLastSent("");
    setSendError(undefined);
    setComposerIntent({ kind: "project_investigation" });
  }, [projectScopeId]);
  useLayoutEffect(() => {
    if (visibleTab !== "results" || !timelineSignature) {
      previousTimelineProgressRef.current = timelineProgress;
      return;
    }
    const element = timelineScrollElement();
    if (!element) return;
    const pendingAnchor = pendingTimelineAnchorRef.current;
    if (pendingAnchor && !turnsLoadingOlder) {
      const anchorShift =
        pendingAnchor.element?.isConnected &&
        pendingAnchor.elementTop !== undefined
          ? pendingAnchor.element.getBoundingClientRect().top -
            pendingAnchor.elementTop
          : element.scrollHeight - pendingAnchor.height;
      element.scrollTop = Math.max(0, pendingAnchor.top + anchorShift);
      pendingTimelineAnchorRef.current = undefined;
    }
    const previous = previousTimelineProgressRef.current;
    let seenExistingTurn = false;
    const changedCount = [...timelineProgress].reduce(
      (count, [id, signature]) => {
        if (previous.has(id)) {
          seenExistingTurn = true;
          return previous.get(id) === signature ? count : count + 1;
        }
        return seenExistingTurn ? count + 1 : count;
      },
      0,
    );
    previousTimelineProgressRef.current = timelineProgress;
    if (!previous.size) {
      followTimelineRef.current =
        element.scrollHeight - element.clientHeight - element.scrollTop <= 48;
      setTimelineUnreadUpdates(0);
      return;
    }
    if (followTimelineRef.current) {
      element.scrollTop = element.scrollHeight;
      setTimelineUnreadUpdates(0);
      return;
    }
    if (changedCount > 0) {
      setTimelineUnreadUpdates((current) => current + changedCount);
    }
  }, [visibleTab, timelineProgress, timelineSignature, turnsLoadingOlder]);

  const captureScientificSelection = useCallback(
    (selection: ScientificSelection) => {
      const artifact = artifacts.find((item) => item.id === selection.artifactId);
      if (
        selection.renderer === "structure" &&
        artifact &&
        !isCoordinateStructureArtifact(artifact)
      ) {
        return;
      }
      const descriptorRevision = artifact
        ? artifactCanonicalRevision(artifact)
        : undefined;
      const eligibleForArtifactInquiry =
        hasArtifactInquirySelection(selection, descriptorRevision) &&
        (selection.renderer !== "scientific-ui" || Boolean(ownerLabel(artifact?.owner)));
      if (eligibleForArtifactInquiry) {
        setActiveArtifactId(selection.artifactId);
        setArtifactComposerArtifactId(selection.artifactId);
        const keepScientificUiInteractionInView =
          selection.renderer === "scientific-ui" &&
          typeof window !== "undefined" &&
          window.matchMedia("(max-width: 800px)").matches;
        if (!keepScientificUiInteractionInView) {
          setMobileComposerExpanded(true);
          pendingArtifactInquiryFocusRef.current = selection.artifactId;
          setArtifactInquiryFocusSequence((current) => current + 1);
        }
      } else if (pendingArtifactInquiryFocusRef.current === selection.artifactId) {
        pendingArtifactInquiryFocusRef.current = undefined;
      }
      setScientificSelections((current) => {
        const emptyStructuredSelection =
          (selection.renderer === "curve" ||
            selection.renderer === "structure") &&
          selection.ids.length === 0;
        const emptyFlowSelection =
          selection.renderer === "flow" &&
          selection.ids.length === 0 &&
          !selection.gateId;
        const emptyScientificUiSelection =
          selection.renderer === "scientific-ui" &&
          (!selection.fidelity?.rowIds?.length || selection.ids.length === 0);
        if (
          emptyStructuredSelection ||
          emptyFlowSelection ||
          emptyScientificUiSelection
        ) {
          if (!current[selection.artifactId]) return current;
          const next = { ...current };
          delete next[selection.artifactId];
          return next;
        }
        if (selection.renderer === "report" && !selection.quote) {
          if (!current[selection.artifactId]) return current;
          const next = { ...current };
          delete next[selection.artifactId];
          return next;
        }
        return sameScientificSelection(current[selection.artifactId], selection)
          ? current
          : { ...current, [selection.artifactId]: selection };
      });
    },
    [artifacts],
  );

  const captureStructureSelection = useCallback(
    (selection: StructureScientificSelection) => {
      const previous = scientificSelections[selection.artifactId];
      const previousResidues =
        previous?.renderer === "structure" ? previous.residues : [];
      const previousPositions = stableResiduePositions(previousResidues);
      const legacyOnlyPositions = residuePositions.filter(
        (position) => !previousPositions.includes(position),
      );
      const nextPositions = [
        ...new Set([
          ...legacyOnlyPositions,
          ...stableResiduePositions(selection.residues),
        ]),
      ].sort((left, right) => left - right);
      const changedResidue = changedStableResidue(
        previousResidues,
        selection.residues,
      );

      captureScientificSelection(selection);
      setResiduePositions(nextPositions);
      onStableResidueSelectionChange?.(selection.residues);
      const legacyPosition = stableResiduePosition(changedResidue);
      if (legacyPosition !== undefined) onResidueSelect?.(legacyPosition);
    },
    [
      captureScientificSelection,
      onResidueSelect,
      onStableResidueSelectionChange,
      residuePositions,
      scientificSelections,
      setResiduePositions,
    ],
  );

  const sendBTW = async () => {
    const input = btwValue.trim();
    if (readOnly || !input || !activeAgent || btwSending) return;
    if (activeAgent.btwAvailable !== true) {
      setBtwSendError({
        agentId: activeAgent.id,
        message:
          "当前 Agent 没有可用的独立 BTW 通道，请等待其活动任务恢复后重试。",
      });
      return;
    }
    setBtwSendError(undefined);
    setBtwSending(true);
    try {
      if (!onSendBTW) {
        throw new Error("当前项目未连接 BTW 通道。");
      }
      await onSendBTW(activeAgent.id, input, context);
      setLastSent(`BTW → ${activeAgent.name}: ${input}`);
      setBtwValue("");
    } catch (cause) {
      setBtwSendError({
        agentId: activeAgent.id,
        message:
          cause instanceof Error && cause.message
            ? cause.message
            : "BTW 发送失败，请稍后重试。",
      });
    } finally {
      setBtwSending(false);
    }
  };

  const decidePermission = async (
    request: CadencePermissionRequest,
    decision: "approve" | "deny",
  ) => {
    if (!onPermissionDecision || permissionAction) return;
    setPermissionAction(request.requestId);
    setPermissionActionError(undefined);
    try {
      await onPermissionDecision(request.requestId, decision);
    } catch (cause) {
      setPermissionActionError(
        cause instanceof Error && cause.message.trim()
          ? cause.message
          : "权限决定未保存，请重试。",
      );
    } finally {
      setPermissionAction(undefined);
    }
  };

  const cancelActiveTurn = async () => {
    if (!activeTurn || !onCancelTurn || cancellingTurn) return;
    setCancellingTurn(true);
    setCancelError(undefined);
    try {
      await onCancelTurn(activeTurn.id);
    } catch (cause) {
      setCancelError(
        cause instanceof Error && cause.message.trim()
          ? cause.message
          : "无法取消当前运行，请重试。",
      );
    } finally {
      setCancellingTurn(false);
    }
  };

  const beginFolderImport = async () => {
    if (!onSelectLocalFolder || selectingFolder || importingFolder) return;
    setSelectingFolder(true);
    setFolderImportError(undefined);
    try {
      const selection = await onSelectLocalFolder();
      if (!selection) return;
      // The native picker has already granted the folder. Keep only its opaque
      // server token in browser state; host paths must never enter UI context.
      setFolderSelection({
        selectionId: selection.selectionId,
        name: selection.name,
      });
      setCreateDialogOpen(false);
    } catch (cause) {
      setFolderImportError(
        cause instanceof Error && cause.message.trim()
          ? cause.message
          : "无法打开本地文件夹选择器，请重试。",
      );
    } finally {
      setSelectingFolder(false);
    }
  };

  const submitFolderImport = async (mode: "copy" | "in_place") => {
    if (!folderSelection || !onImportProject || importingFolder) return;
    setImportingFolder(true);
    setFolderImportError(undefined);
    try {
      await onImportProject({
        selectionId: folderSelection.selectionId,
        mode,
        name: folderSelection.name,
      });
      setFolderSelection(undefined);
    } catch (cause) {
      setFolderImportError(
        cause instanceof Error && cause.message.trim()
          ? cause.message
          : "导入项目失败，请重新选择文件夹。",
      );
    } finally {
      setImportingFolder(false);
    }
  };

  const submitCreateProject = async () => {
    const name = createName.trim();
    const question = createQuestion.trim();
    if (!name) {
      setCreateValidationError("请填写项目名称。");
      return;
    }
    if (!onCreateProject) return;
    setCreateValidationError(undefined);
    try {
      await onCreateProject({ name, question: question || undefined });
      setCreateDialogOpen(false);
      setCreateName("");
      setCreateQuestion("");
    } catch {
      // The parent exposes the production error while the dialog remains open.
    }
  };

  const rootStyle = {
    "--cadence-team-width": `${teamWidth}px`,
  } as CSSProperties;
  const displayedBTWResponse =
    btwResponse?.agentId === activeAgent?.id ? btwResponse : undefined;
  const displayedBTWError =
    btwSendError && btwSendError.agentId === activeAgent?.id
      ? btwSendError.message
      : undefined;
  const logout = async () => {
    setLoggingOut(true);
    setLogoutError(undefined);
    try {
      await api.logout();
    } catch (cause) {
      setLogoutError(
        cause instanceof Error && cause.message.trim()
          ? cause.message
          : "退出失败，请重试。",
      );
      setLoggingOut(false);
    }
  };
  return (
    <div
      className={`cadence-bio cadence-${mode} ${compactComposerOnMobile ? "cadence-mobile-composer-compact" : ""} ${className}`}
      style={rootStyle}
      data-testid="cadence-root"
      data-theme={theme}
    >
      <header className="cadence-header">
        <div className="cadence-brand">
          <span className="cadence-brand-mark">
            <Dna size={21} />
          </span>
          <span>
            <strong>Cadence</strong> <em>Bio</em>
          </span>
        </div>
        <div className="cadence-project-bar">
          <span className="project-bar-icon">
            <FolderKanban size={15} />
          </span>
          <strong>{project?.name ?? "未选择研究项目"}</strong>
          {mode === "live" && (
            <ProjectBranchSwitcher
              projectId={project?.id}
              onSwitched={() => onRetryProject?.()}
            />
          )}
          {mode === "live" && (
            <button
              className="research-state-trigger research-state-trigger-desktop"
              data-testid="research-state-trigger"
              type="button"
              disabled={!project || !onResearchStateOpen}
              onClick={(event) => openResearchState(event.currentTarget)}
            >
              <BookOpenText size={14} aria-hidden="true" />
              研究状态
            </button>
          )}
        </div>
        <button
          type="button"
          className="mobile-project-trigger"
          data-testid="mobile-project-menu"
          ref={mobileProjectTriggerRef}
          aria-expanded={mobileProjectMenuOpen}
          aria-controls="mobile-project-menu-panel"
          onClick={() => {
            if (mobileProjectMenuOpen) closeMobileProjectMenu();
            else setMobileProjectMenuOpen(true);
          }}
        >
          <FolderKanban size={15} aria-hidden="true" />
          <span>项目切换 · {project?.name ?? "当前未选择研究项目"}</span>
          <ChevronRight
            size={15}
            className={
              mobileProjectMenuOpen ? "mobile-project-trigger-open" : ""
            }
            aria-hidden="true"
          />
        </button>
        {mode === "live" && (
          <button
            className="research-state-trigger research-state-trigger-mobile"
            data-testid="research-state-trigger-mobile"
            type="button"
            disabled={!project || !onResearchStateOpen}
            onClick={(event) => openResearchState(event.currentTarget)}
          >
            <BookOpenText size={14} aria-hidden="true" />
            研究状态
          </button>
        )}
        <div className="cadence-header-actions">
          <button
            aria-expanded={commandPaletteOpen}
            aria-haspopup="dialog"
            aria-label="快捷操作"
            className="header-action command-palette-trigger"
            data-testid="command-palette-trigger"
            disabled={!shortcutsAvailable}
            ref={commandPaletteTriggerRef}
            type="button"
            onClick={() => setCommandPaletteOpen((current) => !current)}
          >
            <Search size={14} aria-hidden="true" />
            <span>快捷操作</span>
            <kbd aria-hidden="true">⌘K</kbd>
          </button>
          <div className="cadence-theme-picker" role="group" aria-label="界面主题">
            {CADENCE_THEMES.map((candidate) => {
              const label = CADENCE_THEME_LABELS[candidate];
              const Icon =
                candidate === "light"
                  ? Sun
                  : candidate === "warm"
                    ? Palette
                    : Moon;
              return (
                <button
                  key={candidate}
                  type="button"
                  className="cadence-theme-option"
                  aria-label={`切换至${label}主题`}
                  aria-pressed={theme === candidate}
                  title={`${label}主题`}
                  onClick={() => selectTheme(candidate)}
                >
                  <Icon size={14} aria-hidden="true" />
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
          {mode === "demo" && (
            <span className="projection-badge projection-demo">
              <CircleDot size={12} />
              DEMO PROJECTION
            </span>
          )}
          {mode === "live" && onOnboardingReplay && (
            <button
              className="header-action onboarding-replay"
              data-testid="onboarding-replay"
              type="button"
              aria-label="使用指引"
              onClick={onOnboardingReplay}
            >
              <CircleHelp size={14} aria-hidden="true" />
              <span>使用指引</span>
            </button>
          )}
          {mode === "live" && (
            <button
              className="header-action logout-action"
              type="button"
              disabled={loggingOut}
              onClick={() => void logout()}
            >
              {loggingOut ? (
                <LoaderCircle
                  className="logout-spinner"
                  size={14}
                  aria-hidden="true"
                />
              ) : (
                <LogOut size={14} aria-hidden="true" />
              )}
              {loggingOut ? "退出中…" : "退出登录"}
            </button>
          )}
          {logoutError && (
            <span className="header-logout-error" role="alert">
              {logoutError}
            </span>
          )}
          {userError && (
            <span className="header-logout-error" role="alert">
              {userError}
            </span>
          )}
        </div>
      </header>
      {mobileProjectMenuOpen && (
        <>
          <div
            className="mobile-project-menu-backdrop"
            aria-hidden="true"
            onClick={() => closeMobileProjectMenu()}
          />
          <section
            className="mobile-project-menu"
            data-testid="mobile-project-menu-panel"
            id="mobile-project-menu-panel"
            ref={mobileProjectMenuRef}
            role="dialog"
            aria-modal="true"
            aria-label="研究项目"
            tabIndex={-1}
          >
          <div className="mobile-project-menu-head">
            <div>
              <strong>研究项目</strong>
              <span>选择项目或查看已记录的研究请求</span>
            </div>
            <div className="mobile-project-menu-actions">
              <button
                data-testid="mobile-new-project"
                type="button"
                className="outline-button"
                onClick={(event) => {
                  createDialogReturnFocusRef.current =
                    mobileProjectTriggerRef.current;
                  closeMobileProjectMenu(false);
                  // The dialog must own focus and scroll lock without an
                  // underlying full-screen Sheet competing for either.
                  window.setTimeout(openCreateDialog, 0);
                }}
                disabled={
                  !onCreateProject ||
                  creatingProject ||
                  projectListState === "loading"
                }
              >
                <Plus size={14} />
                新建项目
              </button>
              <button
                type="button"
                className="icon-button mobile-project-menu-close"
                data-mobile-project-menu-initial-focus
                aria-label="关闭项目导航"
                onClick={() => closeMobileProjectMenu()}
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
          </div>
          {projectListState === "loading" ? (
            <p className="mobile-project-menu-state">正在加载项目…</p>
          ) : projectListState === "error" ? (
            <div className="mobile-project-menu-state">
              <strong>项目列表加载失败</strong>
              <button type="button" onClick={onRetryProjects}>
                重试
              </button>
            </div>
          ) : (
            <div className="mobile-project-menu-list">
              {projectList.map((item) => {
                const itemStatus =
                  item.id === project?.id && projectRuntimeReady
                    ? "running"
                    : item.status === "complete" || item.status === "archived"
                      ? item.status
                      : "paused";
                const historyOpen =
                  expandedProjectHistories[item.id] ?? item.id === project?.id;
                const projectTurns =
                  item.id === project?.id ? turns.slice(-6).reverse() : [];
                return (
                  <div className="mobile-project-entry" key={item.id}>
                    <div className="mobile-project-row">
                      <button
                        type="button"
                        className={`mobile-project-item ${item.id === project?.id ? "mobile-project-item-selected" : ""}`}
                        onClick={() => {
                          setProjectId(item.id);
                          closeMobileProjectMenu();
                        }}
                      >
                        <span>{item.name}</span>
                        <small>
                          {itemStatus === "running"
                            ? "进行中"
                            : itemStatus === "complete"
                              ? "已完成"
                              : itemStatus === "paused"
                                ? "暂停中"
                                : "已归档"}
                        </small>
                      </button>
                      <button
                        type="button"
                        className="mobile-project-history-toggle"
                        aria-label={`${historyOpen ? "收起" : "展开"}研究请求历史`}
                        aria-controls={`mobile-project-history-${item.id}`}
                        aria-expanded={historyOpen}
                        onClick={() => {
                          if (item.id !== project?.id) setProjectId(item.id);
                          toggleProjectHistory(item.id, item.id === project?.id);
                        }}
                      >
                        <ChevronDown
                          size={16}
                          className={historyOpen ? "project-history-chevron-open" : undefined}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                    {historyOpen && item.id === project?.id && (
                      <ProjectHistoryRows
                        id={`mobile-project-history-${item.id}`}
                        turns={projectTurns}
                        loading={turnsLoading}
                        error={turnsError}
                        onRetry={onRetryTurns}
                        onTurnOpen={(turnId) => {
                          closeMobileProjectMenu(false);
                          scrollToProjectTurn(turnId);
                        }}
                        onViewAll={() => {
                          closeMobileProjectMenu();
                          scrollToLatestTurn();
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
          </section>
        </>
      )}

      <div
        ref={layoutRef}
        className={`cadence-layout ${sidebarCollapsed ? "sidebar-is-collapsed" : ""} ${teamCollapsed ? "team-is-collapsed" : ""} ${compactComposerOnMobile ? "cadence-layout-with-compact-composer" : pinComposerOnMobile ? "cadence-layout-with-pinned-composer" : ""}`}
      >
        <nav className="cadence-rail" aria-label="主导航">
          {sidebarCollapsed && (
            <button
              data-testid="project-sidebar-toggle"
              className="rail-button"
              type="button"
              aria-expanded={false}
              aria-label="展开项目侧栏"
              onClick={() => setSidebarCollapsed(false)}
            >
              <ChevronRight size={18} />
            </button>
          )}
          <span className="rail-spacer" />
        </nav>

        <aside
          className="project-sidebar"
          data-testid="project-sidebar"
          aria-label="研究项目"
        >
          <div className="sidebar-head">
            <span>我的项目</span>
            {!sidebarCollapsed && (
              <button
                data-testid="project-sidebar-toggle"
                className="icon-button"
                type="button"
                aria-expanded={true}
                aria-label="折叠项目侧栏"
                onClick={() => setSidebarCollapsed(true)}
              >
                <ChevronLeft size={16} />
              </button>
            )}
          </div>
          <button
            data-testid="desktop-new-project"
            className="new-project-button"
            type="button"
            onClick={(event) => {
              createDialogReturnFocusRef.current = event.currentTarget;
              openCreateDialog();
            }}
            disabled={
              !onCreateProject ||
              creatingProject ||
              projectListState === "loading"
            }
          >
            <Plus size={16} />
            {creatingProject
              ? "创建中…"
              : projectListState === "loading"
                ? "正在读取项目…"
                : "新建项目"}
          </button>
          <div className="project-list">
            {projectListState === "loading" && (
              <div
                className="project-state-card"
                data-testid="project-list-loading"
              >
                正在加载项目…
              </div>
            )}
            {projectListState === "error" && (
              <div
                className="project-state-card project-state-error"
                role="alert"
              >
                <strong>项目列表加载失败</strong>
                <span>{projectListError ?? "无法读取项目。"}</span>
                <button type="button" onClick={onRetryProjects}>
                  重试
                </button>
              </div>
            )}
            {projectListState !== "loading" &&
              projectListState !== "error" &&
              projectList.map((item) => {
                const historyOpen =
                  expandedProjectHistories[item.id] ?? item.id === project?.id;
                const projectTurns =
                  item.id === project?.id ? turns.slice(-6).reverse() : [];
                return (
                  <div className="project-entry" key={item.id}>
                    <div className="project-row">
                      <button
                        className={`project-item ${item.id === project?.id ? "project-selected" : ""}`}
                        type="button"
                        onClick={() => setProjectId(item.id)}
                      >
                        <span className="project-item-name">{item.name}</span>
                      </button>
                      <button
                        type="button"
                        className="project-history-toggle"
                        data-testid={`project-history-toggle-${item.id}`}
                        aria-label={`${historyOpen ? "收起" : "展开"}研究请求历史`}
                        aria-controls={`project-history-${item.id}`}
                        aria-expanded={historyOpen}
                        onClick={() => {
                          if (item.id !== project?.id) setProjectId(item.id);
                          toggleProjectHistory(item.id, item.id === project?.id);
                        }}
                      >
                        <ChevronDown
                          size={15}
                          className={historyOpen ? "project-history-chevron-open" : undefined}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                    {historyOpen && item.id === project?.id && (
                      <ProjectHistoryRows
                        id={`project-history-${item.id}`}
                        turns={projectTurns}
                        loading={turnsLoading}
                        error={turnsError}
                        onRetry={onRetryTurns}
                        onTurnOpen={scrollToProjectTurn}
                        onViewAll={scrollToLatestTurn}
                      />
                    )}
                  </div>
                );
              })}
            {projectListState === "ready" && !projectList.length && (
              <div className="project-state-card">
                暂无项目。创建一个项目后再开始研究。
              </div>
            )}
          </div>
        </aside>

        <main
          className={`cadence-main ${pinComposerOnMobile ? "cadence-main-with-pinned-composer" : ""} ${compactComposerOnMobile ? "cadence-main-with-compact-composer" : ""}`}
        >
          <ProjectWorkspaceTabs
            tabs={workspaceTabs}
            activeTabId={activeWorkspaceTab.id}
            onActivate={(tab) => {
              activateWorkspaceTab(tab.id);
              if (tab.kind === "artifact" && tab.artifactId) {
                openArtifact(tab.artifactId, "tab");
              }
            }}
            onClose={(tab) => {
              closeWorkspaceTab(tab.id);
              if (tab.kind === "artifact" && tab.artifactId) {
                closeArtifact(tab.artifactId);
              }
            }}
            onNew={() => {
              createWorkspaceDraft();
              window.requestAnimationFrame(() => composerInputRef.current?.focus());
            }}
          />
          <div
            className="cadence-main-body"
            id="project-workspace-panel"
            role="tabpanel"
            aria-labelledby={`workspace-tab-${activeWorkspaceTab.id}`}
          >
            <div className="cadence-main-top">
              {projectNotice && (
                <div
                  className="project-content-banner project-content-notice"
                  role="status"
                  data-testid="project-notice"
                >
                  <span>{projectNotice}</span>
                  <button type="button" onClick={onProjectNoticeDismiss}>
                    知道了
                  </button>
                </div>
              )}
              {projectContentState === "loading" && (
                <div
                  className="project-content-banner"
                  data-testid="project-loading"
                >
                  正在加载项目快照与产物…
                </div>
              )}
              {requestError && (
                <div
                  className="project-content-banner project-content-error"
                  role="alert"
                >
                  {requestError}
                </div>
              )}
              {projectReadIssue && !runtimeCatalogUnknown && (
                <div
                  className={`project-content-banner ${projectReadIssueIsBlocking ? "project-content-error" : "project-content-warning"}`}
                  data-testid={
                    artifactCatalogUnavailable
                      ? "project-content-error"
                      : "project-snapshot-warning"
                  }
                  role={projectReadIssueIsBlocking ? "alert" : "status"}
                >
                  <span>{projectReadIssueMessage}</span>
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        artifactCatalogUnavailable ||
                        snapshotRecoveryUnavailable
                      ) {
                        onRetryProject?.();
                      }
                    }}
                  >
                    重试
                  </button>
                </div>
              )}
              <div className="main-heading">
                <div>
                  <span className="eyebrow">
                    Research project / {project?.subtitle ?? "Evidence workspace"}
                  </span>
                  <h1
                    title={
                      project?.question ??
                      project?.subtitle ??
                      "等待运行时研究问题"
                    }
                  >
                    {project?.question ??
                      project?.subtitle ??
                      "等待运行时研究问题"}
                  </h1>
                  {mode === "demo" && (
                    <div className="heading-meta">
                      <span>数据截止：2024-05-24 10:30</span>
                    </div>
                  )}
                </div>
              </div>

            </div>

            <div
              className={`cadence-content ${delegatedAgents.length ? "cadence-content-with-delegated-track" : ""}`}
              ref={contentScrollRef}
              onScroll={updateTimelineFollow}
            >
              <>
                  {visibleTab === "results" && (
                    <RequestTimeline
                      projectId={project?.id ?? projectScopeId}
                      turns={turns}
                      agents={agents}
                      events={agentEvents}
                      artifacts={artifacts}
                      childThreadIds={delegatedChildThreadIds}
                      selectedAgentId={activeAgent?.id}
                      loading={turnsLoading}
                      loadingOlder={turnsLoadingOlder}
                      hasMore={turnsHasMore}
                      error={turnsError}
                      onRetry={onRetryTurns}
                      retryLabel={turnsRetryLabel}
                      onLoadOlder={onLoadOlderTurns ? loadOlderTurns : undefined}
                      activeTurnId={activeTurn?.id}
                      onCancelActive={onCancelTurn ? cancelActiveTurn : undefined}
                      cancellingTurn={cancellingTurn}
                      cancelError={cancelError}
                      onAgentSelect={setSelectedAgent}
                      onOpenArtifact={(id) => openArtifact(id, "tab")}
                      onOpenAgentDetail={(id) => {
                        setSelectedAgent(id);
                        setLiveRightPanel("agents");
                      }}
                      onContinueResearch={mode === "live" ? beginContinuation : undefined}
                    />
                  )}
                  <span
                    className="visually-hidden"
                    data-testid="timeline-update-announcer"
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    {timelineUnreadUpdates > 0
                      ? `有 ${timelineUnreadUpdates} 条新进展`
                      : ""}
                  </span>
                  {visibleTab === "results" && timelineUnreadUpdates > 0 && (
                    <button
                      className="timeline-new-updates"
                      data-testid="timeline-new-updates"
                      type="button"
                      aria-label={`有 ${timelineUnreadUpdates} 条新进展，跳到最新请求`}
                      onClick={scrollToLatestTurn}
                    >
                      有 {timelineUnreadUpdates} 条新进展 ↓
                    </button>
                  )}
              </>
            </div>
            <DelegatedAgentsTrack
              agents={delegatedAgents}
              onOpenAgent={(agent) => {
                // Opening PASEO's track is navigation to a read-only child
                // timeline. It must never select a roster Agent or invoke BTW.
                setActiveDelegatedAgentId(agent.id);
                setActiveAgentId(null);
                // This is an explicit researcher choice, unlike automatic
                // Artifact arrival. Preserve it instead of defaulting back to
                // the empty Artifact directory on the next render.
                liveRightPanelUserSelectedRef.current = true;
                setLiveRightPanel("agents");
                setTeamCollapsed(false);
              }}
            />
          </div>
          <CadenceComposer
            value={composerValue}
            lastSent={lastSent}
            sendError={sendError}
            sending={sending}
            running={activeTurnRunning}
            steerAvailable={activeTurnCanSteer}
            stopping={cancellingTurn}
            onStop={activeTurnCanCancel ? cancelActiveTurn : undefined}
            context={projectComposerContext}
            projectName={project?.name}
            agentName={activeAgent?.name}
            artifactName={artifactWorkspaceOpen ? undefined : activeArtifact?.name}
            scopeNotice={
              artifactWorkspaceOpen
                ? "项目范围 · 不附带当前 Artifact"
                : undefined
            }
            intent={composerIntent}
            capabilities={composerCapabilities}
            availableAttachments={availableAttachments}
            artifacts={artifacts}
            agents={agents}
            turns={turns}
            references={composerReferences}
            attachments={attachmentsWithDocumentSelections}
            uploading={uploading}
            uploadError={uploadError}
            onRetryUpload={onRetryUpload}
            onChange={(nextValue) => {
              setComposerValue(nextValue);
              if (controlledCommandInput(nextValue)) {
                const controlledCapability = composerCapabilities.find(
                  (capability) => capability.kind === "controlled_command",
                );
                if (controlledCapability?.available) {
                  setComposerIntent({ kind: "controlled_command", command: "" });
                }
              } else if (composerIntent.kind === "controlled_command") {
                setComposerIntent({ kind: "project_investigation" });
              }
            }}
            textAreaRef={composerInputRef}
            onSubmit={sendPrompt}
            onUploadFiles={onUploadFiles}
            onAttachProjectInput={onAttachProjectInput}
            onIntentChange={setComposerIntent}
            onAddReference={(reference) => {
              setComposerReferences((current) => {
                if (current.some((item) => item.id === reference.id)) {
                  return current;
                }
                if (current.length >= 12) {
                  setSendError("一次研究请求最多引用 12 个项目对象。");
                  return current;
                }
                return [...current, reference];
              });
            }}
            onRemoveReference={(referenceId) => {
              setComposerReferences((current) =>
                current.filter((reference) => reference.id !== referenceId),
              );
            }}
            onOpenAttachment={openDocumentInspection}
            onRemoveAttachment={(attachmentId) => {
              setDocumentSelections((current) => {
                if (!current[attachmentId]) return current;
                const next = { ...current };
                delete next[attachmentId];
                return next;
              });
              if (documentViewerAttachmentId === attachmentId) {
                setDocumentViewerAttachmentId(undefined);
              }
              onRemoveAttachment?.(attachmentId);
            }}
            readOnly={readOnly}
            disabled={composerDisabled}
            disabledReason={composerDisabledReason}
            permissions={permissions}
            permissionError={permissionError ?? permissionActionError}
            pendingPermissionRequestId={permissionAction}
            onPermissionDecision={
              onPermissionDecision ? decidePermission : undefined
            }
            pinned={pinComposerOnMobile}
            compact={compactComposerOnMobile}
            onClearAgent={() => setActiveAgentId(null)}
            onMobileFocusChange={(focused) => {
              if (
                typeof window !== "undefined" &&
                !window.matchMedia("(max-width: 800px)").matches
              ) {
                return;
              }
              setMobileComposerExpanded(focused);
            }}
            artifactInquiry={
              artifactComposerArtifact
                ? {
                    artifact: artifactComposerArtifact,
                    revision: artifactCanonicalRevision(
                      artifactComposerArtifact,
                    ),
                    selection:
                      scientificSelections[artifactComposerArtifact.id],
                    detailState:
                      artifactDetailStates?.[artifactComposerArtifact.id],
                    detailError:
                      artifactDetailErrors?.[artifactComposerArtifact.id],
                    turns,
                    collapsed: mobileScientificUiInquiryCollapsed,
                    readOnly,
                    composerDisabled,
                    composerDisabledReason,
                    onSubmit: onArtifactComposerSubmit,
                    onReportTextReplacementApply,
                    onDetailRetry: onArtifactDetailRetry,
                    onSelectionChange: captureScientificSelection,
                    onClearScope: () => {
                      const selection =
                        scientificSelections[artifactComposerArtifact.id];
                      if (selection) {
                        captureScientificSelection(
                          clearedScientificSelection(selection),
                        );
                      }
                      setArtifactComposerArtifactId(undefined);
                    },
                    onExpand: mobileScientificUiArtifactInquiry
                      ? () => setMobileArtifactInquiryExpanded(true)
                      : undefined,
                    onCollapse: mobileScientificUiArtifactInquiry
                      ? () => setMobileArtifactInquiryExpanded(false)
                      : undefined,
                    onReturnToDraft: (artifactId) => {
                      setActiveArtifactId(artifactId);
                      setArtifactComposerArtifactId(artifactId);
                      setLiveRightPanel("artifacts");
                    },
                  }
                : undefined
            }
          />
        </main>

        {teamCollapsed ? (
          <div className="team-collapsed-dock">
            <button
              data-testid="agent-team-toggle"
              className="team-expand-tab"
              type="button"
              aria-expanded={false}
              aria-label="展开智能体团队"
              onClick={() => setTeamCollapsed(false)}
            >
              <ChevronLeft size={16} />
              <span>Agents</span>
            </button>
            {agents[0] && (
              <button
                data-testid={`btw-${agents[0].id}`}
                className="team-btw-quick"
                type="button"
                aria-label={`向 ${agents[0].name} 旁路提问`}
                onClick={() => {
                  setActiveAgentId(agents[0].id);
                  setTeamCollapsed(false);
                  setLiveRightPanel("agents");
                }}
              >
                <MessageSquareMore size={15} />
                <span>BTW</span>
              </button>
            )}
          </div>
        ) : (
          <aside
            className={`agent-team ${resizing ? "team-is-resizing" : ""}`}
            aria-label="智能体团队"
          >
            <button
              data-testid="agent-team-resize"
              className="team-resize-handle"
              type="button"
              aria-label="调整团队宽度"
              aria-keyshortcuts="ArrowLeft ArrowRight Home End"
              onPointerDown={(event: ReactPointerEvent<HTMLButtonElement>) => {
                event.preventDefault();
                setResizing(true);
              }}
              onMouseDown={() => setResizing(true)}
              onKeyDown={(event) => {
                const current = Number(teamWidth);
                const maxWidth =
                  measuredTeamWidthMax(layoutRef.current) ??
                  FALLBACK_MAX_TEAM_WIDTH;
                const clamp = (value: number) =>
                  Math.max(MIN_TEAM_WIDTH, Math.min(maxWidth, value));
                if (event.key === "ArrowLeft") {
                  event.preventDefault();
                  setTeamWidth(clamp(current + 24));
                } else if (event.key === "ArrowRight") {
                  event.preventDefault();
                  setTeamWidth(clamp(current - 24));
                } else if (event.key === "Home") {
                  event.preventDefault();
                  setTeamWidth(MIN_TEAM_WIDTH);
                } else if (event.key === "End") {
                  event.preventDefault();
                  setTeamWidth(maxWidth);
                }
              }}
            >
              <GripVertical size={15} />
            </button>
            {showLiveRightTabs && (
              <div
                className="live-right-tabs"
                data-testid="live-right-tabs"
                role="tablist"
                aria-label="运行侧栏"
              >
                {showLiveArtifacts && (
                  <button
                    type="button"
                    role="tab"
                    data-testid="live-artifacts-tab"
                    className="live-artifacts-tab"
                    aria-selected={activeLiveRightPanel === "artifacts"}
                    aria-label="查看项目产物"
                    title="查看项目产物"
                    onClick={() => {
                      liveRightPanelUserSelectedRef.current = true;
                      setLiveRightPanel("artifacts");
                    }}
                  >
                    <FileText size={16} aria-hidden="true" />
                    <span>产物</span>
                  </button>
                )}
                <button
                  type="button"
                  role="tab"
                  data-testid="live-agents-tab"
                  aria-selected={activeLiveRightPanel === "agents"}
                  aria-label="查看研究团队"
                  title="查看研究团队"
                  onClick={() => {
                    liveRightPanelUserSelectedRef.current = true;
                    setLiveRightPanel("agents");
                  }}
                >
                  <Network size={16} aria-hidden="true" />
                  <span>团队</span>
                  <small>{teamRosterUnavailable ? "—" : agents.length}</small>
                </button>
              </div>
            )}
            {runtimeCatalogUnknown ? (
              <RuntimeCatalogState
                kind="all"
                onRetryArtifacts={onRetryProject}
                onRetryTeam={onRetryOrganization}
              />
            ) : runtimeCatalogLoading ? (
              <RuntimeCatalogState kind="loading" />
            ) : activeLiveRightPanel === "artifacts" && showLiveArtifacts ? (
              artifactCatalogUnavailable && artifacts.length === 0 ? (
                <RuntimeCatalogState
                  kind="artifacts"
                  onRetry={onRetryProject}
                />
              ) : (
              <LiveArtifactsPanel
                artifacts={artifacts}
                agents={agents}
                onOpenArtifact={(id) => openArtifact(id, "tab")}
                activeArtifact={activeArtifact}
                besideArtifact={besideArtifact}
                openIds={openIds}
                sessionRunning={sessionRunning}
                streamState={streamState}
                onReturnToResearch={() => {
                  dismissArtifactWorkspace();
                }}
                onSetActiveArtifact={activateArtifact}
                onCloseArtifact={closeArtifact}
                onOpenArtifactBeside={(id) => openArtifact(id, "beside")}
                onSetBesideArtifact={(id) => setBesideId(id)}
                onExitArtifactComparison={() => setBesideId(undefined)}
                mobileSheet={mobileViewport}
                onDismissMobileSheet={dismissArtifactWorkspace}
                onArtifactDetailRequest={onArtifactDetailRequest}
                onArtifactDetailRetry={onArtifactDetailRetry}
                scientificSelections={scientificSelections}
                onStructureSelectionChange={captureStructureSelection}
                onScientificSelectionChange={captureScientificSelection}
                scientificOperation={scientificOperation}
                onScientificApply={onScientificArtifactApply}
                artifactDetailStates={artifactDetailStates}
                artifactDetailErrors={artifactDetailErrors}
              />
              )
            ) : teamRosterUnavailable ? (
              <RuntimeCatalogState
                kind="team"
                onRetry={onRetryOrganization}
              />
            ) : activeDelegatedAgent ? (
              <DelegatedAgentDetail
                agent={activeDelegatedAgent}
                events={agentEvents}
                onClose={() => setActiveDelegatedAgentId(undefined)}
              />
            ) : (
              <>
                <div className="team-heading">
                  <div>
                    <span className="eyebrow">协作上下文</span>
                    <h2 data-testid="agent-roster-count">
                      智能体团队 <span>Agents ({agents.length})</span>
                    </h2>
                  </div>
                  <button
                    data-testid="agent-team-toggle"
                    className="icon-button"
                    type="button"
                    aria-expanded={!teamCollapsed}
                    aria-label="折叠智能体团队"
                    onClick={() => setTeamCollapsed(true)}
                  >
                    <PanelRightClose size={16} />
                  </button>
                </div>
                {teamCatalogUnavailable && (
                  <div
                    className="team-read-error"
                    data-testid="organization-error"
                    role="status"
                  >
                    <span>
                      研究团队暂时无法刷新；{agents.length ? "已保留上次读取的团队信息。" : "请稍后重试。"}
                    </span>
                    <button type="button" onClick={onRetryOrganization}>
                      <RefreshCw size={13} aria-hidden="true" />
                      重试
                    </button>
                  </div>
                )}
                <div className="team-view-controls" aria-label="团队视图控制">
                  {mode === "demo" && !inputAgents && (
                    <div
                      className="roster-size-control"
                      aria-label="Demo roster size"
                    >
                      {[2, 4, 8, 12].map((size) => (
                        <button
                          className={
                            rosterSize === size ? "roster-size-active" : ""
                          }
                          type="button"
                          key={size}
                          onClick={() => setRosterSize(size)}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  )}
                  <button
                    data-testid="agent-compact-toggle"
                    className="team-filter-toggle"
                    type="button"
                    aria-label="紧凑模式"
                    aria-pressed={compactMode}
                    onClick={() => setCompactMode(!compactMode)}
                  >
                    <span className="toggle-mark" />
                    紧凑模式
                  </button>
                  <button
                    data-testid="agent-active-only-toggle"
                    className="team-filter-toggle"
                    type="button"
                    aria-label="仅显示活跃"
                    aria-pressed={activeOnly}
                    onClick={() => setActiveOnly(!activeOnly)}
                  >
                    <span className="toggle-mark" />
                    仅显示活跃
                  </button>
                </div>
                <div
                  className={`agent-list ${compactMode ? "agent-list-compact" : ""}`}
                >
                  {playback.interaction &&
                    playback.interactionState &&
                    collaborationAgents.length >= 2 && (
                      <CollaborationGroup
                        interaction={playback.interaction}
                        interactionState={playback.interactionState}
                        agents={collaborationAgents}
                        activeAgentId={activeAgent?.id}
                        compact={compactMode}
                        queuedCount={playback.queuedCount}
                        onSelect={setSelectedAgent}
                      />
                    )}
                  {visibleStandaloneAgents.map((agent) => (
                    <AgentRosterCard
                      key={agent.id}
                      agent={agent}
                      compact={compactMode}
                      active={agent.id === activeAgent?.id}
                      onSelect={setSelectedAgent}
                    />
                  ))}
                  {!visibleAgentCount && (
                    <div className="agent-empty-state">
                      <CircleDot size={18} />
                      <strong>
                        {agents.length
                          ? "没有符合当前筛选的 Agent"
                          : "当前没有已激活的 Agent"}
                      </strong>
                      <span>
                        {agents.length
                          ? "关闭“仅显示活跃”以查看完整团队。"
                          : "等待 Cadence 激活研究成员。"}
                      </span>
                    </div>
                  )}
                </div>
                {activeAgent && (
                  <AgentDetail
                    agent={activeAgent}
                    readOnly={readOnly}
                    residues={residues}
                    btwValue={btwValue}
                    selectedResidues={residuePositions}
                    onResidueSelect={toggleResidue}
                    onBTWChange={setBtwValue}
                    onBTWSubmit={sendBTW}
                    btwSending={btwSending}
                    btwResponse={displayedBTWResponse}
                    btwSendError={displayedBTWError}
                    onBTWResponseDismiss={onBTWResponseDismiss}
                    onClose={() => setActiveAgentId(null)}
                  />
                )}
              </>
            )}
          </aside>
        )}
      </div>
      {documentViewerAttachment?.id && projectComposerContext.sessionId && (
        <Suspense
          fallback={
            <div className="document-inspection-backdrop" role="presentation">
              <div className="document-inspection-loading" role="status">
                正在打开结构化文档…
              </div>
            </div>
          }
        >
          <DocumentInspectionViewer
            attachment={documentViewerAttachment}
            sessionId={projectComposerContext.sessionId}
            selection={documentSelections[documentViewerAttachment.id]}
            onSelectionChange={captureDocumentSelection}
            onSelectionIntent={continueFromDocumentSelection}
            onClose={closeDocumentInspection}
          />
        </Suspense>
      )}
      {researchStateOpen && (
        <div
          className="research-state-backdrop"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) closeResearchState();
          }}
        >
          <ResearchStateDrawer
            drawerRef={researchStateDrawerRef}
            projectName={project?.name}
            views={researchStateViews}
            loadingSections={researchStateLoadingSections}
            errors={researchStateErrors}
            onClose={closeResearchState}
            onRefresh={onResearchStateRefresh}
            onLoadSection={onResearchStateSectionLoad}
            onOpenArtifact={(reference) => {
              closeResearchState();
              onResearchStateArtifactOpen?.(reference);
            }}
            onReviewRecord={(recordId) => {
              closeResearchState();
              setComposerValue(
                `[Research State review]\nrecord_id=${recordId}\n请核对该记录的证据、冲突与当前结论，不要直接修改研究状态。`,
              );
              window.setTimeout(() => composerInputRef.current?.focus(), 0);
            }}
          />
        </div>
      )}
      {createDialogOpen && (
        <div
          className="project-dialog-backdrop"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) closeCreateDialog();
          }}
        >
          <section
            className="project-dialog"
            ref={createDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-project-title"
          >
            <div className="project-dialog-head">
              <div>
                <span className="eyebrow">New research project</span>
                <h2 id="create-project-title">创建研究项目</h2>
              </div>
              <button
                className="icon-button"
                type="button"
                aria-label="关闭新建项目"
                onClick={closeCreateDialog}
              >
                <X size={16} />
              </button>
            </div>
            <label>
              项目名称 <span aria-hidden="true">*</span>
              <input
                autoFocus
                required
                value={createName}
                onChange={(event) => setCreateName(event.target.value)}
                placeholder="例如：ADC-03 机制研究"
              />
            </label>
            <label>
              研究问题（可选）
              <textarea
                value={createQuestion}
                onChange={(event) => setCreateQuestion(event.target.value)}
                placeholder="例如：为什么模型 B 的疗效显著下降？"
                rows={3}
              />
              <span className="project-dialog-hint">
                {createQuestion.trim()
                  ? "提交后会创建项目并立即开始这项研究。"
                  : "不填写问题时只创建项目，不会启动研究团队。"}
              </span>
            </label>
            {(createValidationError || createProjectError || folderImportError) && (
              <p className="project-dialog-error" role="alert">
                {createValidationError ?? createProjectError ?? folderImportError}
              </p>
            )}
            <div className="project-dialog-actions">
              {localFolderImportAvailable && onSelectLocalFolder && onImportProject && (
                <button
                  className="outline-button project-dialog-import"
                  type="button"
                  onClick={() => void beginFolderImport()}
                  disabled={selectingFolder || creatingProject}
                >
                  <FolderKanban size={15} aria-hidden="true" />
                  {selectingFolder ? "正在选择…" : "导入本地文件夹"}
                </button>
              )}
              <button
                className="outline-button"
                type="button"
                onClick={closeCreateDialog}
              >
                取消
              </button>
              <button
                className="new-project-button"
                type="button"
                onClick={() => void submitCreateProject()}
                disabled={creatingProject}
              >
                {creatingProject
                  ? "创建中…"
                  : createQuestion.trim()
                    ? "创建并开始研究"
                    : "创建项目"}
              </button>
            </div>
          </section>
        </div>
      )}
      {folderSelection && (
        <div
          className="project-dialog-backdrop"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget && !importingFolder) {
              closeImportDialog();
            }
          }}
        >
          <section
            className="project-dialog project-import-dialog"
            ref={importDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="import-project-title"
          >
            <div className="project-dialog-head">
              <div>
                <h2 id="import-project-title">导入“{folderSelection.name}”</h2>
                <p className="project-dialog-hint">
                  选择 Cadence 如何使用这个本地文件夹。此选择不会启动研究或创建 Sandbox。
                </p>
              </div>
              <button
                className="icon-button"
                type="button"
                aria-label="关闭导入项目"
                onClick={closeImportDialog}
                disabled={importingFolder}
              >
                <X size={16} />
              </button>
            </div>
            <div className="project-import-options">
              <button
                className="project-import-option project-import-option-primary"
                type="button"
                onClick={() => void submitFolderImport("in_place")}
                disabled={importingFolder || creatingProject}
              >
                <strong>在原位置打开</strong>
                <span>
                  Agent 会在这个文件夹中工作，可能修改其中的文件。删除 Cadence 项目不会删除该文件夹。
                </span>
              </button>
              <button
                className="project-import-option"
                type="button"
                onClick={() => void submitFolderImport("copy")}
                disabled={importingFolder || creatingProject}
              >
                <strong>复制到 Cadence</strong>
                <span>
                  创建由 Cadence 管理的可写副本。原文件夹保持不变；删除项目只会删除这个副本。
                </span>
              </button>
            </div>
            {folderImportError && (
              <p className="project-dialog-error" role="alert">
                {folderImportError}
              </p>
            )}
            <div className="project-dialog-actions">
              <button
                className="outline-button"
                type="button"
                onClick={closeImportDialog}
                disabled={importingFolder}
              >
                取消
              </button>
            </div>
          </section>
        </div>
      )}
      {onOnboardingStepChange &&
        onOnboardingDismiss &&
        onOnboardingUnavailable &&
        onOnboardingComplete && (
          <CadenceOnboardingTour
            active={mode === "live" && onboardingActive}
            stepIndex={onboardingStep}
            hasProjects={projectList.length > 0}
            onStepChange={onOnboardingStepChange}
            onDismiss={onOnboardingDismiss}
            onUnavailable={onOnboardingUnavailable}
            onComplete={onOnboardingComplete}
          />
        )}
      <CadenceCommandPalette
        actions={commandActions}
        enabled={shortcutsAvailable}
        open={commandPaletteOpen}
        triggerRef={commandPaletteTriggerRef}
        onOpenChange={setCommandPaletteOpen}
      />
    </div>
  );
}

function ResearchStateDrawer({
  drawerRef,
  projectName,
  views,
  loadingSections,
  errors,
  onClose,
  onRefresh,
  onLoadSection,
  onOpenArtifact,
  onReviewRecord,
}: {
  drawerRef: RefObject<HTMLElement>;
  projectName?: string;
  views: Partial<Record<ResearchStateSection, ResearchStateView>>;
  loadingSections: ResearchStateSection[];
  errors: Partial<Record<ResearchStateSection, string>>;
  onClose(): void;
  onRefresh?: () => void;
  onLoadSection?: (section: ResearchStateSection) => void;
  onOpenArtifact(reference: string): void;
  onReviewRecord(recordId: string): void;
}) {
  const entry = views.entry?.entry;
  const current = views.current?.current ?? [];
  const conflicts = views.conflicts?.conflicts ?? [];
  const unresolvedConflicts = conflicts.filter(
    (conflict) => conflict.status === "unresolved",
  );
  const resolvedConflicts = conflicts.filter(
    (conflict) => conflict.status === "resolved",
  );
  const negatives = views.negatives?.negatives ?? [];
  const staging = views.staging?.staging ?? [];
  const trace = views.trace?.trace ?? [];
  const revisions = Object.values(views).flatMap((view) =>
    view ? [view.revision] : [],
  );
  const revision = revisions.length ? Math.max(...revisions) : undefined;
  const revisionMismatch = new Set(revisions).size > 1;
  const initialLoading = ["entry", "current", "conflicts", "negatives"].some(
    (section) => loadingSections.includes(section as ResearchStateSection),
  );

  return (
    <aside
      className="research-state-drawer"
      ref={drawerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="research-state-title"
      aria-describedby="research-state-description"
      data-testid="research-state-drawer"
    >
      <header className="research-state-head">
        <div>
          <h2 id="research-state-title">研究状态</h2>
          <p id="research-state-description">
            {projectName ?? "当前研究项目"} · 只读 Project 研究状态快照
          </p>
        </div>
        <div className="research-state-head-actions">
          {revision !== undefined && (
            <span data-testid="research-state-revision">
              Project revision {revision}
            </span>
          )}
          <button
            className="icon-button"
            type="button"
            aria-label="刷新研究状态"
            disabled={!onRefresh || loadingSections.length > 0}
            onClick={onRefresh}
          >
            <RefreshCw
              className={loadingSections.length ? "logout-spinner" : ""}
              size={15}
              aria-hidden="true"
            />
          </button>
          <button
            className="icon-button"
            type="button"
            aria-label="关闭研究状态"
            data-research-state-initial-focus
            onClick={onClose}
          >
            <X size={17} aria-hidden="true" />
          </button>
        </div>
      </header>
      <div className="research-state-body">
        {revisionMismatch && (
          <p className="research-state-sync-note" role="status">
            分节来自不同 Project revision，请刷新以同步。
          </p>
        )}
        {initialLoading && !revision && (
          <div
            className="research-state-loading"
            data-testid="research-state-loading"
            role="status"
          >
            <LoaderCircle className="logout-spinner" size={18} />
            正在读取研究目标、Current、冲突与负面知识…
          </div>
        )}
        <ResearchStateSectionBlock
          title="研究目标"
          count={entry?.objective ? 1 : 0}
          loaded={Boolean(views.entry)}
          loading={loadingSections.includes("entry")}
          error={errors.entry}
          onRetry={onRefresh}
          empty="该项目尚未记录研究目标。"
        >
          {entry?.objective && (
            <p
              className="research-state-objective"
              data-testid="research-state-objective"
            >
              {entry.objective}
            </p>
          )}
        </ResearchStateSectionBlock>
        {unresolvedConflicts.length > 0 && (
          <ResearchStateSectionBlock
            title="未解决冲突"
            count={unresolvedConflicts.length}
            loaded={Boolean(views.conflicts)}
            loading={loadingSections.includes("conflicts")}
            error={errors.conflicts}
            onRetry={onRefresh}
            tone="conflict"
            empty="当前没有未解决的冲突。"
          >
            {unresolvedConflicts.map((conflict) => (
              <ResearchConflictRecord
                conflict={conflict}
                key={conflict.id}
                onOpenArtifact={onOpenArtifact}
                onReview={onReviewRecord}
              />
            ))}
          </ResearchStateSectionBlock>
        )}

        <ResearchStateSectionBlock
          title="Current"
          count={current.length}
          loaded={Boolean(views.current)}
          loading={loadingSections.includes("current")}
          error={errors.current}
          onRetry={onRefresh}
          empty="暂无已进入 Current 的研究理解。"
        >
          {current.map((entry) => (
            <ResearchCurrentRecord
              entry={entry}
              key={entry.id}
              onOpenArtifact={onOpenArtifact}
              onReview={onReviewRecord}
            />
          ))}
        </ResearchStateSectionBlock>

        <ResearchStateSectionBlock
          title="需关注"
          count={negatives.length}
          loaded={Boolean(views.negatives)}
          loading={loadingSections.includes("negatives")}
          error={errors.negatives}
          onRetry={onRefresh}
          tone="attention"
          empty="当前没有负面发现、弱化结论或未解决方向。"
        >
          {negatives.map((finding) => (
            <ResearchNegativeRecord
              finding={finding}
              key={`${finding.kind}-${finding.reference}`}
              onReview={onReviewRecord}
            />
          ))}
        </ResearchStateSectionBlock>

        {(resolvedConflicts.length > 0 || unresolvedConflicts.length === 0) && (
          <ResearchStateSectionBlock
            title={resolvedConflicts.length ? "已解决冲突" : "冲突"}
            count={resolvedConflicts.length}
            loaded={Boolean(views.conflicts)}
            loading={loadingSections.includes("conflicts")}
            error={errors.conflicts}
            onRetry={onRefresh}
            tone={resolvedConflicts.length ? undefined : "conflict"}
            empty="当前没有显式冲突记录。"
          >
            {resolvedConflicts.map((conflict) => (
              <ResearchConflictRecord
                conflict={conflict}
                key={conflict.id}
                onOpenArtifact={onOpenArtifact}
                onReview={onReviewRecord}
              />
            ))}
          </ResearchStateSectionBlock>
        )}

        <ResearchLazySection
          section="staging"
          title="Staging"
          loaded={Boolean(views.staging)}
          loading={loadingSections.includes("staging")}
          error={errors.staging}
          count={staging.length}
          truncated={Boolean(
            views.staging?.truncated_sections.includes("staging"),
          )}
          onLoad={onLoadSection}
        >
          {staging.length ? (
            staging.map((item) => (
              <ResearchStagingRecord
                item={item}
                key={item.observation.id}
                onOpenArtifact={onOpenArtifact}
                onReview={onReviewRecord}
              />
            ))
          ) : (
            <p className="research-state-empty">暂无待审阅的 Staging 记录。</p>
          )}
        </ResearchLazySection>

        <ResearchLazySection
          section="trace"
          title="Trace"
          loaded={Boolean(views.trace)}
          loading={loadingSections.includes("trace")}
          error={errors.trace}
          count={trace.length}
          truncated={Boolean(
            views.trace?.truncated_sections.includes("trace"),
          )}
          onLoad={onLoadSection}
        >
          {trace.length ? (
            trace.map((event) => (
              <ResearchTraceRecord
                event={event}
                key={event.id}
                onOpenArtifact={onOpenArtifact}
                onReview={onReviewRecord}
              />
            ))
          ) : (
            <p className="research-state-empty">暂无研究 Trace 记录。</p>
          )}
        </ResearchLazySection>
      </div>
    </aside>
  );
}

function ResearchStateSectionBlock({
  title,
  count,
  loaded,
  loading,
  error,
  onRetry,
  tone,
  empty,
  children,
}: {
  title: string;
  count: number;
  loaded: boolean;
  loading?: boolean;
  error?: string;
  onRetry?: () => void;
  tone?: "attention" | "conflict";
  empty: string;
  children: ReactNode;
}) {
  return (
    <section
      className={`research-state-section ${tone ? `research-state-section-${tone}` : ""}`}
    >
      <div className="research-state-section-head">
        <h3>{title}</h3>
        <span>{count}</span>
      </div>
      {error && (
        <div className="research-state-error" role="alert">
          <AlertTriangle size={15} aria-hidden="true" />
          <span>{error}</span>
          {onRetry && (
            <button type="button" onClick={onRetry}>
              重试
            </button>
          )}
        </div>
      )}
      {count ? (
        children
      ) : loaded ? (
        <p className="research-state-empty">{empty}</p>
      ) : loading ? (
        <p className="research-state-loading" role="status">
          <LoaderCircle className="logout-spinner" size={15} />
          正在读取 {title}…
        </p>
      ) : error ? null : (
        <p className="research-state-empty">等待读取该分节。</p>
      )}
    </section>
  );
}

function ResearchLazySection({
  section,
  title,
  loaded,
  loading,
  error,
  count,
  truncated,
  onLoad,
  children,
}: {
  section: "staging" | "trace";
  title: string;
  loaded: boolean;
  loading: boolean;
  error?: string;
  count: number;
  truncated: boolean;
  onLoad?: (section: ResearchStateSection) => void;
  children: ReactNode;
}) {
  return (
    <details
      className="research-state-lazy"
      data-testid={`research-state-${section}`}
      onToggle={(event) => {
        if (event.currentTarget.open && !loaded && !loading) onLoad?.(section);
      }}
    >
      <summary>
        <span>
          <ChevronDown size={15} aria-hidden="true" />
          <strong>{title}</strong>
        </span>
        <span>{loaded ? `${count}${truncated ? "+" : ""}` : "按需读取 · 最多 50 条"}</span>
      </summary>
      <div className="research-state-lazy-body">
        {loading ? (
          <p className="research-state-loading" role="status">
            <LoaderCircle className="logout-spinner" size={15} />
            正在读取 {title}…
          </p>
        ) : error ? (
          <div className="research-state-error" role="alert">
            <span>{error}</span>
            <button type="button" onClick={() => onLoad?.(section)}>
              重试
            </button>
          </div>
        ) : loaded ? (
          <>
            {truncated && (
              <p className="research-state-sync-note" role="status">
                结果已按 50 条读取上限截断。
              </p>
            )}
            {children}
          </>
        ) : null}
      </div>
    </details>
  );
}

function ResearchCurrentRecord({
  entry,
  onOpenArtifact,
  onReview,
}: {
  entry: ResearchStateCurrentEntry;
  onOpenArtifact(reference: string): void;
  onReview(recordId: string): void;
}) {
  const recordedAt =
    entry.last_revised?.recorded_at ?? entry.provenance.recorded_at;
  return (
    <article
      className="research-state-record"
      data-testid={`research-state-record-${entry.id}`}
    >
      <div className="research-state-record-head">
        <code>{entry.id}</code>
        <div>
          <span>{researchKindLabel(entry.kind)}</span>
          {entry.claim_status && (
            <span className={`research-claim-status status-${entry.claim_status}`}>
              {claimStatusLabel(entry.claim_status)}
            </span>
          )}
        </div>
      </div>
      <p>{entry.summary}</p>
      <ResearchProvenance provenance={entry.last_revised ?? entry.provenance} />
      <ResearchEvidenceRefs
        refs={entry.evidence_refs}
        onOpenArtifact={onOpenArtifact}
      />
      {entry.conflict_refs?.length ? (
        <div className="research-record-refs">
          <strong>冲突</strong>
          {entry.conflict_refs.map((reference) => (
            <code key={reference}>{reference}</code>
          ))}
        </div>
      ) : null}
      <div className="research-state-record-meta">
        <span>来源 {entry.promoted_from}</span>
        <span>{closureSignalLabel(entry.closure_signal)}</span>
        <time dateTime={recordedAt}>{formatResearchStateTime(recordedAt)}</time>
      </div>
      <ReviewResearchRecordButton recordId={entry.id} onReview={onReview} />
    </article>
  );
}

function ResearchNegativeRecord({
  finding,
  onReview,
}: {
  finding: ResearchStateNegativeFinding;
  onReview(recordId: string): void;
}) {
  return (
    <article className="research-state-record research-state-negative">
      <div className="research-state-record-head">
        <code>{finding.reference}</code>
        <span>{negativeKindLabel(finding.kind)}</span>
      </div>
      <p>{finding.summary}</p>
      {finding.related_refs.length > 0 && (
        <div className="research-record-refs">
          <strong>关联</strong>
          {finding.related_refs.map((reference) => (
            <code key={reference}>{reference}</code>
          ))}
        </div>
      )}
      <ReviewResearchRecordButton
        recordId={finding.reference}
        onReview={onReview}
      />
    </article>
  );
}

function ResearchConflictRecord({
  conflict,
  onOpenArtifact,
  onReview,
}: {
  conflict: ResearchStateConflict;
  onOpenArtifact(reference: string): void;
  onReview(recordId: string): void;
}) {
  return (
    <article className="research-state-record research-state-conflict">
      <div className="research-state-record-head">
        <code>{conflict.id}</code>
        <span>
          {conflict.status === "unresolved" ? "未解决" : "已解决"}
        </span>
      </div>
      <p>{conflict.summary}</p>
      <div className="research-conflict-pair">
        <code>{conflict.left_ref}</code>
        <span>vs</span>
        <code>{conflict.right_ref}</code>
      </div>
      <ResearchProvenance provenance={conflict.provenance} />
      <ResearchEvidenceRefs
        refs={conflict.evidence_refs}
        onOpenArtifact={onOpenArtifact}
      />
      {conflict.resolved_by && (
        <div className="research-record-refs">
          <strong>解决记录</strong>
          <code>{conflict.resolved_by}</code>
        </div>
      )}
      <ReviewResearchRecordButton recordId={conflict.id} onReview={onReview} />
    </article>
  );
}

function ResearchStagingRecord({
  item,
  onOpenArtifact,
  onReview,
}: {
  item: ResearchStateStagingView;
  onOpenArtifact(reference: string): void;
  onReview(recordId: string): void;
}) {
  const observation = item.observation;
  return (
    <article className="research-state-record">
      <div className="research-state-record-head">
        <code>{observation.id}</code>
        <span>{researchKindLabel(observation.kind)}</span>
      </div>
      <p>{observation.summary}</p>
      <ResearchProvenance provenance={observation.provenance} />
      <ResearchEvidenceRefs
        refs={observation.evidence_refs}
        onOpenArtifact={onOpenArtifact}
      />
      {item.promoted_to && (
        <div className="research-record-refs">
          <strong>已进入 Current</strong>
          <code>{item.promoted_to}</code>
        </div>
      )}
      <ReviewResearchRecordButton recordId={observation.id} onReview={onReview} />
    </article>
  );
}

function ResearchTraceRecord({
  event,
  onOpenArtifact,
  onReview,
}: {
  event: ResearchStateTraceEvent;
  onOpenArtifact(reference: string): void;
  onReview(recordId: string): void;
}) {
  return (
    <article className="research-state-record">
      <div className="research-state-record-head">
        <code>{event.id}</code>
        <span>{traceKindLabel(event.kind)}</span>
      </div>
      <p>{event.summary}</p>
      <ResearchProvenance provenance={event.provenance} />
      <ResearchEvidenceRefs
        refs={event.evidence_refs}
        onOpenArtifact={onOpenArtifact}
      />
      {event.related_refs.length > 0 && (
        <div className="research-record-refs">
          <strong>关联</strong>
          {event.related_refs.map((reference) => (
            <code key={reference}>{reference}</code>
          ))}
        </div>
      )}
      <ReviewResearchRecordButton recordId={event.id} onReview={onReview} />
    </article>
  );
}

function ResearchProvenance({
  provenance,
}: {
  provenance: ResearchStateProvenance;
}) {
  return (
    <div className="research-provenance">
      <span>{provenanceSourceLabel(provenance.source)}</span>
      <code>{provenance.actor_id}</code>
      <time dateTime={provenance.recorded_at}>
        {formatResearchStateTime(provenance.recorded_at)}
      </time>
    </div>
  );
}

function ResearchEvidenceRefs({
  refs,
  onOpenArtifact,
}: {
  refs: ResearchStateEvidenceRef[] | null | undefined;
  onOpenArtifact(reference: string): void;
}) {
  const [copied, setCopied] = useState<string>();
  const references = refs ?? [];
  if (!references.length) return null;
  return (
    <div className="research-evidence-refs">
      <strong>证据</strong>
      {references.map((reference) =>
        reference.kind === "artifact" ? (
          <button
            type="button"
            key={`${reference.kind}-${reference.reference}`}
            onClick={() => onOpenArtifact(reference.reference)}
          >
            <FileText size={13} aria-hidden="true" />
            {reference.reference}
          </button>
        ) : reference.kind === "execution" ? (
          <span
            className="research-execution-ref"
            key={`${reference.kind}-${reference.reference}`}
          >
            <code>{reference.reference}</code>
            <button
              type="button"
              aria-label={`复制执行引用 ${reference.reference}`}
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(reference.reference)
                  .then(() => setCopied(reference.reference));
              }}
            >
              <Copy size={12} aria-hidden="true" />
              {copied === reference.reference ? "已复制" : "复制"}
            </button>
          </span>
        ) : (
          <span
            className="research-legacy-ref"
            key={`${reference.kind}-${reference.reference}`}
          >
            旧版 Project File · <code>{reference.reference}</code>
          </span>
        ),
      )}
    </div>
  );
}

function ReviewResearchRecordButton({
  recordId,
  onReview,
}: {
  recordId: string;
  onReview(recordId: string): void;
}) {
  return (
    <button
      className="research-review-button"
      data-testid={`research-state-review-${recordId}`}
      type="button"
      onClick={() => onReview(recordId)}
    >
      <MessageSquareMore size={13} aria-hidden="true" />
      请 Agent 复核
    </button>
  );
}

function formatResearchStateTime(value: string) {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return value;
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(timestamp);
}

function researchKindLabel(kind: ResearchStateCurrentEntry["kind"]) {
  return (
    {
      claim: "主张",
      heuristic: "启发式",
      concept: "概念",
      constraint: "约束",
      architecture: "架构",
      unknown: "未分类",
    }[kind] ?? kind
  );
}

function claimStatusLabel(
  status: NonNullable<ResearchStateCurrentEntry["claim_status"]>,
) {
  return (
    {
      hypothesis: "假设",
      untested: "未测试",
      testing: "测试中",
      supported: "支持",
      weakened: "已弱化",
      refuted: "已证伪",
      withdrawn: "已撤回",
    }[status] ?? status
  );
}

function closureSignalLabel(
  signal: ResearchStateCurrentEntry["closure_signal"],
) {
  return (
    {
      topic_abandonment: "主题终止",
      verbal_affirmation: "用户确认",
      empirical_resolution: "实证解决",
      artifact_commitment: "Artifact 提交",
    }[signal] ?? signal
  );
}

function provenanceSourceLabel(source: ResearchStateProvenance["source"]) {
  return (
    {
      user: "用户",
      "ai-suggested": "AI 建议",
      "ai-executed": "AI 执行",
      "user-revised": "用户修订",
      system: "系统",
    }[source] ?? source
  );
}

function negativeKindLabel(kind: ResearchStateNegativeFinding["kind"]) {
  return (
    {
      dead_end: "无效方向",
      pivot: "研究转向",
      conflict: "冲突",
      weakened_claim: "弱化主张",
      refuted_claim: "证伪主张",
      withdrawn_claim: "撤回主张",
    }[kind] ?? kind
  );
}

function traceKindLabel(kind: ResearchStateTraceEvent["kind"]) {
  return (
    {
      question: "问题",
      decision: "决策",
      experiment: "实验",
      dead_end: "无效方向",
      pivot: "转向",
      closure: "闭合",
      contradiction: "矛盾",
      reconciliation: "修订",
    }[kind] ?? kind
  );
}

function CollaborationGroup({
  interaction,
  interactionState,
  agents,
  activeAgentId,
  compact,
  queuedCount,
  onSelect,
}: {
  interaction: CadenceCollaborationInteraction;
  interactionState: "active" | "recent";
  agents: CadenceAgent[];
  activeAgentId?: string;
  compact: boolean;
  queuedCount: number;
  onSelect: (agentId: string) => void;
}) {
  const source = agents.find((agent) => agent.id === interaction.sourceAgentId);
  const targets = interaction.targetAgentIds
    .map((agentId) => agents.find((agent) => agent.id === agentId))
    .filter((agent): agent is CadenceAgent => Boolean(agent));
  if (!source || targets.length === 0) return null;
  const accent = identityAccent(interaction.id);
  const direction = `${source.name} → ${targets.map((agent) => agent.name).join("、")}`;
  const label = interaction.completed
    ? "历史协作"
    : interactionState === "recent"
      ? "最近协作"
      : interaction.kind === "waiting"
        ? "等待协作者"
        : interaction.needReply
          ? "协作请求 · 需回复"
          : "协作请求";
  const style = {
    "--collaboration-accent": accent,
  } as CSSProperties;

  return (
    <section
      data-testid={`collaboration-playback-${interaction.id}`}
      data-interaction-state={interactionState}
      data-interaction-completed={String(interaction.completed)}
      data-protocol-source={interaction.protocolSource}
      data-message-id={interaction.messageId}
      data-playback-queued={queuedCount}
      className="collaboration-playback"
      style={style}
      aria-label={`${label}：${direction}`}
    >
      <div
        data-testid={`collaboration-bubble-${interaction.id}`}
        className="collaboration-bubble"
      >
        <span className="collaboration-bubble-label">
          <Network size={12} aria-hidden="true" />
          {label}
        </span>
        <p>
          <strong>{direction}</strong>
          {interaction.preview && <span> · {interaction.preview}</span>}
        </p>
      </div>
      <div
        data-testid={`collaboration-connector-${interaction.id}`}
        className="collaboration-connector"
        aria-hidden="true"
      >
        <span />
        <i />
        <span />
      </div>
      <div className="collaboration-agent-grid">
        {[source, ...targets].map((agent, index) => (
          <div
            key={agent.id}
            data-testid={`collaboration-agent-${interaction.id}-${agent.id}`}
            data-approach={index % 2 === 0 ? "left" : "right"}
            className="collaboration-agent-slot"
          >
            <AgentRosterCard
              agent={agent}
              active={agent.id === activeAgentId}
              compact={compact}
              collaborationState={interactionState}
              collaborationColor={accent}
              onSelect={onSelect}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

type LiveDisclosure = "detail" | "plan" | "tool";

interface LivePlanStep {
  step: string;
  status: "pending" | "in_progress" | "completed";
}

interface LiveToolActivity {
  key: string;
  label: string;
  seq: number;
}

function eventRecordValue(event: CadenceAgentEvent, key: string): unknown {
  return event.event[key];
}

function eventRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function eventText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function eventDelta(value: unknown): string {
  return typeof value === "string" ? value : "";
}

const EXTERNAL_EVIDENCE_EXECUTION_DISCLAIMER =
  "外部检索候选证据：本结果仅记录本次 Execution，不代表已核验、已采纳或已更新项目科研事实。";

const EXTERNAL_EVIDENCE_LANGUAGE_NOTE =
  "正常研究简报用简体中文解释“为什么重要、支持什么、不能支持什么”；论文标题、作者/期刊名、基因/蛋白/药物名、单位、DOI、citation_key 和 evidence_id 保留原样。下方候选来源回执保留来源原文，仅用于核查，不替代上方简报。";

const externalEvidenceStatusLabel: Record<CadenceExternalEvidenceRetrievalStatus, string> = {
  "candidate-sources": "候选来源 · candidate-sources",
  "insufficient-or-conflicting":
    "证据不足或存在冲突 · insufficient-or-conflicting",
  "provider-unavailable": "受管外部检索不可用 · provider-unavailable",
  "fetched-candidate": "已读取候选片段 · fetched-candidate",
  "source-fetch-unavailable": "候选原文读取不可用 · source-fetch-unavailable",
  "evidence-not-found": "候选证据已失效 · evidence-not-found",
};

const EXTERNAL_EVIDENCE_TOOL_NAMES = new Set([
  "search_external_evidence",
  "pingmedical_search_external_evidence",
  "mcp__pingmedical__search_external_evidence",
]);

// Search is an implementation detail of a research answer. Keep the raw
// retrieval lifecycle in the server-owned Execution receipt, but do not let a
// normal Project timeline turn it into a second, technical answer surface.
const USER_HIDDEN_SEARCH_TOOL_NAMES = new Set([
  ...EXTERNAL_EVIDENCE_TOOL_NAMES,
  "query_science_source",
  "pingmedical_query_science_source",
  "mcp__pingmedical__query_science_source",
]);

function externalEvidenceInvocation(event: CadenceAgentEvent) {
  if (
    event.event.type !== "mcp_tool_call_begin" &&
    event.event.type !== "mcp_tool_call_end"
  ) {
    return undefined;
  }
  const invocation = eventRecord(eventRecordValue(event, "invocation"));
  const argumentsValue = eventRecord(invocation?.arguments);
  return eventText(invocation?.server) === "pingmedical" &&
    EXTERNAL_EVIDENCE_TOOL_NAMES.has(eventText(invocation?.tool))
    ? invocation
    : undefined;
}

function isUserHiddenSearchEvent(event: CadenceAgentEvent): boolean {
  const type = event.event.type;
  if (
    type === "web_search_begin" ||
    type === "web_search_end" ||
    type === "web_search_result" ||
    type === "search_begin" ||
    type === "search_end"
  ) {
    return true;
  }
  if (externalEvidenceInvocation(event)) return true;
  if (type !== "mcp_tool_call_begin" && type !== "mcp_tool_call_end") {
    return false;
  }
  const invocation = eventRecord(eventRecordValue(event, "invocation"));
  const tool = eventText(
    invocation?.tool ?? eventRecordValue(event, "tool"),
  ).toLowerCase();
  // A known search name is implementation detail regardless of which wire
  // alias supplied the server field; hiding it prevents a provider rename
  // from reopening the technical activity row in the normal conversation.
  return USER_HIDDEN_SEARCH_TOOL_NAMES.has(tool);
}

function safeExternalEvidenceSourceUrl(value: unknown): string | undefined {
  if (
    typeof value !== "string" ||
    !value ||
    value !== value.trim() ||
    value.length > 2_048
  ) {
    return undefined;
  }
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    if (
      url.protocol !== "https:" ||
      !url.hostname ||
      host === "api.exa.ai" ||
      host === "localhost" ||
      host.endsWith(".localhost") ||
      host.endsWith(".local") ||
      /^[0-9.]+$/u.test(host) ||
      host.includes(":") ||
      url.username ||
      url.password ||
      url.port ||
      url.search ||
      url.hash
    ) {
      return undefined;
    }
    return url.href;
  } catch {
    return undefined;
  }
}

function externalEvidenceCreditsUsed(value: unknown): number | undefined {
  return typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= 1_000_000
    ? value
    : undefined;
}

function localizedExternalEvidenceDetail(value: string): string {
  const detail = value.trim();
  if (!detail) return "";
  if (/^candidate source requires independent review\.?$/iu.test(detail)) {
    return "候选来源需要独立核查。";
  }
  if (/^candidate sources require independent review\.?$/iu.test(detail)) {
    return "候选来源需要独立核查。";
  }
  if (/^candidate source only\.?$/iu.test(detail)) {
    return "当前仅得到候选来源，尚未完成核查。";
  }
  if (/^managed external search is unavailable\.?$/iu.test(detail)) {
    return "受管外部检索当前不可用；这不等于没有相关证据。";
  }
  if (/^bounded source excerpt recovered(?:; claim alignment remains unverified)?\.?$/iu.test(detail)) {
    return "已读取有限原文片段，但它与具体 claim 的对应关系仍未核验。";
  }
  if (/^reason=empty_or_unusable;/iu.test(detail)) {
    return "检索已完成，但没有可用的来源摘要片段。";
  }
  if (/^reason=no_candidate_sources;/iu.test(detail)) {
    return "检索已完成，但没有返回候选来源。";
  }
  const missingReceipt = /^no live candidate receipt exists for (.+)\.?$/iu.exec(detail);
  if (missingReceipt) {
    return `服务器未找到候选回执：${missingReceipt[1]}`;
  }
  // Unknown provider text stays visible for audit, but the Chinese label keeps
  // it from looking like a user-facing conclusion or silently translated fact.
  return `服务端原始说明（English）：${detail}`;
}

function localizedExternalEvidenceNextAction(value?: string): string | undefined {
  switch (value) {
    case "fetch_or_review":
      return "建议：先核对候选来源；若摘要不足，再按 evidence_id 读取有限原文。";
    case "refine_query_or_use_typed_source":
      return "建议：明确剩余证据缺口，必要时改用稳定编号查询。";
    case "retry_provider_or_report_unavailable":
      return "建议：稍后重试受管检索，并将不可用状态与“无证据”分开记录。";
    case "review_claim_alignment":
      return "建议：核对原文片段是否真正支持目标 claim。";
    case "report_fetch_limitation":
      return "建议：记录原文读取限制，不要用模型记忆补齐结论。";
    case "search_again_if_decision_relevant":
      return "建议：只有在该证据缺口会改变决策时才重新检索。";
    default:
      return value ? `下一步（原始状态）：${value}` : undefined;
  }
}

function externalEvidenceSourceExcerptLabel(
  source: CadenceExternalEvidenceCandidateSource,
  resultStatus: CadenceExternalEvidenceRetrievalStatus,
): string {
  const locator = source.locator;
  const isBoundedFetch =
    resultStatus === "fetched-candidate" &&
    source.source_type === "bounded_fetch" &&
    eventText(locator?.kind) === "content-byte-range" &&
    locator?.exact === true;
  return isBoundedFetch
    ? "有限原文摘录（保留来源语言，便于核查）"
    : "检索高亮/来源摘要（保留来源原文；非逐字摘录）";
}

function externalEvidenceSourceStatusLabel(
  source: CadenceExternalEvidenceCandidateSource,
  resultStatus: CadenceExternalEvidenceRetrievalStatus,
): string {
  // Keep the status display server-derived and closed: a provider-supplied
  // label must never make an unassessed candidate look like verified evidence.
  const status =
    resultStatus === "fetched-candidate" ||
    source.evidence_status === "fetched_candidate"
      ? "fetched_candidate"
      : "candidate";
  return `证据状态：${status} · 未验证`;
}

function safeExternalEvidenceReceiptDownloadUrl(
  value: unknown,
  projectId: string | undefined,
): string | undefined {
  if (typeof value !== "string") return undefined;
  const match =
    /^\/api\/projects\/([A-Za-z0-9_-]+)\/exa-receipts\/[a-f0-9]{64}$/u.exec(
      value,
    );
  /*
   * A receipt is a one-time, server-issued download capability. Match its
   * Project segment to the already-scoped public event so a malformed or
   * cross-Project event cannot turn this Execution card into a link elsewhere.
   */
  return match?.[1] === projectId ? value : undefined;
}

function externalEvidenceText(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (!Array.isArray(value)) return "";
  return value
    .filter((item): item is string => typeof item === "string")
    .join("\n")
    .trim();
}

function externalEvidenceCandidateSource(
  value: unknown,
): CadenceExternalEvidenceCandidateSource | undefined {
  const source = eventRecord(value);
  const title = eventText(source?.title);
  const url = safeExternalEvidenceSourceUrl(source?.url);
  const snippet = externalEvidenceText(
    source?.highlight ?? source?.snippet ?? source?.text,
  );
  if (
    !title ||
    title.length > 512 ||
    !url ||
    !snippet ||
    snippet.length > 2_048
  ) {
    return undefined;
  }
  const stableIdsValue = eventRecord(source?.stable_ids ?? source?.stableIds);
  const stableIds = stableIdsValue
    ? Object.fromEntries(
        Object.entries(stableIdsValue)
          .map(([key, value]) => [key, eventText(value)])
          .filter(
            ([key, value]) =>
              /^[a-z][a-z0-9_-]{0,31}$/u.test(key) &&
              Boolean(value) &&
              value.length <= 128,
          ),
      )
    : undefined;
  const limitations = Array.isArray(source?.limitations)
    ? source.limitations
        .filter((item): item is string => typeof item === "string")
        .map((item) => item.trim())
        .filter((item) => item.length > 0 && item.length <= 128)
        .slice(0, 16)
    : undefined;
  const score =
    typeof source?.score === "number" &&
    Number.isFinite(source.score) &&
    source.score >= 0 &&
    source.score <= 1_000_000
      ? source.score
      : undefined;
  const evidenceId = eventText(source?.evidence_id ?? source?.evidenceId);
  const citationKey = eventText(source?.citation_key ?? source?.citationKey);
  const needsFetch = source?.needs_fetch ?? source?.needsFetch;
  const publishedDate = eventText(
    source?.published_date ?? source?.publishedDate,
  );
  return {
    title,
    url,
    snippet,
    ...( /^ev-[0-9a-f]{32}$/u.test(evidenceId)
      ? { evidence_id: evidenceId }
      : {}),
    ...( /^[A-Za-z][A-Za-z0-9._-]{0,63}$/u.test(citationKey)
      ? { citation_key: citationKey }
      : {}),
    ...(eventText(source?.provider)
      ? { provider: eventText(source?.provider).slice(0, 64) }
      : {}),
    ...(eventText(source?.source_type ?? source?.sourceType)
      ? { source_type: eventText(source?.source_type ?? source?.sourceType).slice(0, 64) }
      : {}),
    ...(stableIds && Object.keys(stableIds).length ? { stable_ids: stableIds } : {}),
    ...(publishedDate && publishedDate.length <= 128
      ? { published_date: publishedDate }
      : {}),
    ...(eventText(source?.evidence_status ?? source?.evidenceStatus)
      ? { evidence_status: eventText(source?.evidence_status ?? source?.evidenceStatus).slice(0, 64) }
      : {}),
    ...(typeof needsFetch === "boolean" ? { needs_fetch: needsFetch } : {}),
    ...(eventRecord(source?.locator)
      ? { locator: eventRecord(source?.locator) }
      : {}),
    ...(limitations?.length ? { limitations } : {}),
    ...(score !== undefined ? { score } : {}),
  };
}

function jsonObject(value: unknown): Record<string, unknown> | undefined {
  if (typeof value === "string") {
    try {
      return eventRecord(JSON.parse(value));
    } catch {
      return undefined;
    }
  }
  return eventRecord(value);
}

/**
 * WECODE serializes MCP results under result.Ok.structuredContent, while
 * older replay fixtures put the same server-owned object directly on the
 * event. Accept both wire versions so a real Exa result cannot disappear
 * merely because the transport casing changed.
 */
function externalEvidenceStructuredContent(
  event: CadenceAgentEvent,
): Record<string, unknown> | undefined {
  const direct =
    jsonObject(event.event.structured_content) ??
    jsonObject(event.event.structuredContent);
  if (direct) return direct;
  const result = eventRecord(event.event.result);
  const resultOk = eventRecord(result?.Ok ?? result?.ok) ?? result;
  const nested =
    jsonObject(resultOk?.structuredContent) ??
    jsonObject(resultOk?.structured_content);
  if (nested) return nested;
  const content = resultOk?.content;
  if (Array.isArray(content)) {
    for (const item of content) {
      const itemRecord = eventRecord(item);
      const parsed = jsonObject(
        itemRecord?.text ?? itemRecord?.content ?? itemRecord?.output,
      );
      if (parsed) return parsed;
    }
  }
  return undefined;
}

function externalEvidenceExecutionResultFromEvent(
  event: CadenceAgentEvent,
): CadenceExternalEvidenceExecutionOutcome | undefined {
  if (
    event.event.type !== "mcp_tool_call_end" ||
    !externalEvidenceInvocation(event)
  ) {
    return undefined;
  }
  const content = externalEvidenceStructuredContent(event);
  if (!content) return undefined;
  const disclaimer = eventText(content?.disclaimer);
  const status = eventText(
    content?.status,
  ) as CadenceExternalEvidenceRetrievalStatus;
  const schemaVersion = eventText(content?.schema_version ?? content?.schemaVersion);
  if (
    schemaVersion !== "scientific_search.v1" ||
    (disclaimer && disclaimer !== EXTERNAL_EVIDENCE_EXECUTION_DISCLAIMER) ||
    ((status === "candidate-sources" ||
      status === "insufficient-or-conflicting" ||
      status === "provider-unavailable") &&
      disclaimer !== EXTERNAL_EVIDENCE_EXECUTION_DISCLAIMER) ||
    !Object.prototype.hasOwnProperty.call(externalEvidenceStatusLabel, status)
  ) {
    return undefined;
  }
  const detail = eventText(content?.detail);
  const rawSources = content?.sources;
  const rawSource = content?.source;
  const receiptDownloadUrl = safeExternalEvidenceReceiptDownloadUrl(
    content?.receipt_download_url ?? content?.receiptDownloadUrl,
    event.projectId,
  );
  // Provider-unavailable is an operational outcome, not a billable provider
  // result; mirror the backend allowlist so an adversarial payload cannot show
  // usage for an unavailable call.
  const creditsUsed =
    status === "provider-unavailable"
      ? undefined
      : externalEvidenceCreditsUsed(content?.credits_used);

  const common = {
    disclaimer: disclaimer || EXTERNAL_EVIDENCE_EXECUTION_DISCLAIMER,
    schema_version: schemaVersion || undefined,
    execution_id: eventText(content?.execution_id ?? content?.executionId) || undefined,
    decision_context: eventText(content?.decision_context ?? content?.decisionContext) || undefined,
    detail: detail || undefined,
    credits_used: creditsUsed,
    provider_call_made:
      typeof content?.provider_call_made === "boolean"
        ? content.provider_call_made
        : typeof content?.providerCallMade === "boolean"
          ? content.providerCallMade
          : undefined,
    quality_gate: eventRecord(content?.quality_gate ?? content?.qualityGate),
    evidence_ledger: eventRecord(content?.evidence_ledger ?? content?.evidenceLedger),
    next_action: eventText(content?.next_action ?? content?.nextAction) || undefined,
    receipt_download_url: receiptDownloadUrl,
  };

  /*
   * The backend is authoritative for the governed outcome; this client accepts
   * only its narrow allowlist so provider output cannot become an Artifact,
   * Research State, or an unbounded generic tool-result disclosure.
   */
  if (status === "candidate-sources") {
    if (!Array.isArray(rawSources)) {
      return undefined;
    }
    const sources = rawSources
      .map(externalEvidenceCandidateSource)
      .filter(
        (source): source is CadenceExternalEvidenceCandidateSource =>
          Boolean(source),
      )
      .slice(0, 5);
    if (!sources.length) return undefined;
    return {
      ...common,
      status,
      sources,
    };
  }
  if (status === "fetched-candidate") {
    const source = externalEvidenceCandidateSource(rawSource);
    if (!source) return undefined;
    return { ...common, status, source, sources: [source] };
  }
  return {
    ...common,
    status,
  };
}

function ExternalEvidenceResultCard({
  result,
  eventKey,
}: {
  result: CadenceExternalEvidenceExecutionOutcome;
  eventKey: string;
}) {
  const nextAction = localizedExternalEvidenceNextAction(result.next_action);
  const sourceList = result.sources?.length ? (
    <ol className="external-evidence-sources">
      {result.sources.map((source, index) => (
        <li key={source.evidence_id ?? `${source.url}-${index}`}>
          <a href={source.url} target="_blank" rel="noreferrer noopener">
            {source.title}
          </a>
          <small>来源域名：{new URL(source.url).hostname}</small>
          <small className="external-evidence-source-language">
            {externalEvidenceSourceExcerptLabel(source, result.status)}
          </small>
          <small className="external-evidence-provenance">
            {externalEvidenceSourceStatusLabel(source, result.status)}
          </small>
          <p>{source.snippet}</p>
          {source.published_date ? (
            <small>发表日期：{source.published_date}</small>
          ) : null}
          {source.evidence_id ? (
            <small className="external-evidence-provenance">
              {source.citation_key || "候选证据"} · {source.evidence_id} · 未验证
            </small>
          ) : null}
          {source.stable_ids && Object.keys(source.stable_ids).length ? (
            <small className="external-evidence-provenance">
              稳定编号：{Object.entries(source.stable_ids)
                .map(([key, value]) => `${key}=${value}`)
                .join("，")}
            </small>
          ) : null}
        </li>
      ))}
    </ol>
  ) : null;

  return (
    <section
      className="external-evidence-result"
      data-testid={`external-evidence-result-${eventKey}`}
      data-status={result.status}
      aria-label="受管外部检索 Execution 结果"
    >
      <header>
        <span className="external-evidence-kicker">受管外部检索 · Execution</span>
        <strong>{externalEvidenceStatusLabel[result.status]}</strong>
      </header>
      <p className="external-evidence-disclaimer">
        {result.disclaimer || EXTERNAL_EVIDENCE_EXECUTION_DISCLAIMER}
      </p>
      <p className="external-evidence-language-note">
        {EXTERNAL_EVIDENCE_LANGUAGE_NOTE}
      </p>
      {result.decision_context ? (
        <p className="external-evidence-context">
          本次决策问题：{result.decision_context}
        </p>
      ) : null}
      {result.status === "candidate-sources" && sourceList ? (
        // Keep the translated research brief readable while preserving every
        // server-issued candidate for an explicit audit; collapsing this list
        // is presentation only and must never be interpreted as verification.
        <details className="external-evidence-source-disclosure">
          <summary>
            候选来源回执（{result.sources?.length ?? 0}）· 展开核查
          </summary>
          <p className="external-evidence-source-note">
            上方研究简报已把重点证据翻译并解释；这里保留本次 Execution 的来源回执和原文片段，便于逐条核查。
          </p>
          {sourceList}
        </details>
      ) : (
        sourceList
      )}
      {result.detail ? (
        <p className="external-evidence-detail">
          {localizedExternalEvidenceDetail(result.detail)}
        </p>
      ) : null}
      {nextAction ? (
        <p className="external-evidence-next-action">
          {nextAction}
        </p>
      ) : null}
      {result.credits_used !== undefined ? (
        <small className="external-evidence-credits">
          本次 Provider 用量：{result.credits_used} credits
        </small>
      ) : null}
      {result.receipt_download_url ? (
        <a
          className="external-evidence-receipt"
          href={result.receipt_download_url}
          download
        >
          <Download size={13} aria-hidden="true" />
          下载本次 Execution 回执
        </a>
      ) : null}
    </section>
  );
}

function ExternalEvidenceResults({ events }: { events: CadenceAgentEvent[] }) {
  const results = events.flatMap((event) => {
    const result = externalEvidenceExecutionResultFromEvent(event);
    return result ? [{ event, result }] : [];
  });
  if (!results.length) return null;
  return (
    <section
      className="external-evidence-results"
      data-testid="external-evidence-results"
      aria-label="受管外部检索 Execution 结果"
    >
      {results.map(({ event, result }) => (
        <ExternalEvidenceResultCard
          eventKey={event.key}
          key={event.key}
          result={result}
        />
      ))}
    </section>
  );
}

function externalEvidenceActivityEvents(events: CadenceAgentEvent[]) {
  // The report is the user-facing projection of search. Removing the complete
  // lifecycle here (rather than merely redacting its payload) also prevents a
  // generic "执行轨迹" row from revealing the provider or query route.
  return events.filter((event) => !isUserHiddenSearchEvent(event));
}

const hiddenDetailEventTypes = new Set([
  "agent_reasoning_raw_content",
  "agent_reasoning_raw_content_delta",
  "reasoning_raw_content_delta",
  "debug_trace_snapshot",
  "incremental_compression_state",
  "coordinator_state",
  "raw_response_item",
]);

const hiddenDetailPayloadKeys =
  /(?:raw[_-]?reasoning|reasoning[_-]?raw|encrypted[_-]?content|chain[_-]?of[_-]?thought|private[_-]?reasoning|system[_-]?prompt|developer[_-]?(?:instructions?|prompt))/i;

function publicDetailValue(value: unknown, key = ""): unknown {
  if (hiddenDetailPayloadKeys.test(key)) return undefined;
  if (typeof value === "string") {
    return sanitizeInternalArtifactReferences(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => publicDetailValue(item))
      .filter((item) => item !== undefined);
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .map(([entryKey, entryValue]) => [
          entryKey,
          publicDetailValue(entryValue, entryKey),
        ])
        .filter(([, entryValue]) => entryValue !== undefined),
    );
  }
  return value;
}

function rawResponseItemIsPublic(event: CadenceAgentEvent): boolean {
  if (event.event.type !== "raw_response_item") return true;
  const item = eventRecord(eventRecordValue(event, "item"));
  const itemType = eventText(item?.type).toLowerCase();
  return !/(?:reasoning|encrypted|redacted|private)/u.test(itemType);
}

export function livePublicDetailEvents(
  events: CadenceAgentEvent[],
): CadenceAgentEvent[] {
  return events
    .filter((event) => !isUserHiddenSearchEvent(event))
    .filter((event) => !hiddenDetailEventTypes.has(event.event.type))
    .filter(rawResponseItemIsPublic)
    .slice()
    .sort(compareOrganizationAgentEvents);
}

export function livePublicDetailPayload(event: CadenceAgentEvent): string {
  const provenance = event.source
    ? {
        source: event.source,
        source_index: event.sourceIndex,
        source_watermark: event.sourceWatermark,
        organization_session_id: event.organizationSessionId,
      }
    : {
        event_id: event.eventId,
        seq: event.seq,
        ts_ms: event.tsMs,
      };
  return JSON.stringify(
    publicDetailValue({
      ...provenance,
      project_id: event.projectId,
      agent_id: event.agentId,
      thread_id: event.threadId,
      submission_id: event.submissionId,
      event: event.event,
    }),
    null,
    2,
  );
}

function livePreview(value: unknown): string | undefined {
  const preview = sanitizeUserFacingResearchText(
    safeCollaborationPreview(value) ?? "",
  );
  if (!preview) return undefined;
  return preview.replace(/^(\*\*|__)(.+)\1$/u, "$2").trim();
}

function toolLabel(event: CadenceAgentEvent): string {
  if (externalEvidenceInvocation(event)) {
    return event.event.type === "mcp_tool_call_end"
      ? "已完成外部候选来源检索"
      : "正在检索外部候选来源";
  }
  const invocation =
    eventRecord(eventRecordValue(event, "invocation")) ??
    eventRecord(eventRecordValue(event, "tool"));
  const tool = eventText(invocation?.tool ?? eventRecordValue(event, "tool"));
  const server = eventText(invocation?.server);
  return deterministicToolLabel(tool, server);
}

function deterministicToolLabel(tool: string, server = ""): string {
  const normalized = tool.toLowerCase();
  const readable =
    normalized.includes("search") || normalized.includes("pubmed")
      ? "检索文献"
      : normalized.includes("python") || normalized.includes("analysis")
        ? "运行数据分析"
        : normalized.includes("read")
          ? "读取研究文件"
          : normalized.includes("run_skill")
            ? "执行科研技能"
            : tool
              ? `工具 · ${tool}`
              : "工具活动";
  return server && tool ? `${readable} · ${server}` : readable;
}

function execActivityLabel(event: CadenceAgentEvent): string {
  const commandValue = eventRecordValue(event, "command");
  const command = Array.isArray(commandValue)
    ? commandValue.map(eventText).filter(Boolean).join(" ")
    : eventText(commandValue);
  const analysisCommand =
    /(?:^|\s)(?:python(?:3)?|rscript|julia|matlab)(?:\s|$)/i.test(command) ||
    /\.(?:py|r|jl|m)(?:\s|$)/i.test(command);
  if (event.event.type === "exec_command_output_delta") {
    return analysisCommand ? "正在读取数据分析输出" : "正在读取命令输出";
  }
  if (event.event.type === "exec_command_end") {
    return analysisCommand ? "数据分析执行完成" : "命令执行完成";
  }
  return analysisCommand ? "正在运行数据分析" : "正在运行命令";
}

export function liveActivityFromEvent(
  event: CadenceAgentEvent,
): string | undefined {
  if (isUserHiddenSearchEvent(event)) return undefined;
  const type = event.event.type;
  if (
    type === "agent_reasoning" ||
    type === "agent_reasoning_delta" ||
    type === "reasoning_content_delta"
  ) {
    return livePreview(
      eventText(
        eventRecordValue(event, "text") ?? eventRecordValue(event, "delta"),
      ),
    );
  }
  if (
    type === "agent_message" ||
    type === "agent_message_delta" ||
    type === "agent_message_content_delta"
  ) {
    return livePreview(
      eventText(
        eventRecordValue(event, "message") ??
          eventRecordValue(event, "delta") ??
          eventRecordValue(event, "summary"),
      ),
    );
  }
  if (type === "mcp_tool_call_begin" || type === "mcp_tool_call_end") {
    return toolLabel(event);
  }
  if (type === "web_search_begin" || type === "web_search_end") {
    return "检索文献";
  }
  if (
    type === "exec_command_begin" ||
    type === "exec_command_output_delta" ||
    type === "exec_command_end"
  ) {
    return execActivityLabel(event);
  }
  if (type === "collab_agent_interaction_begin") {
    return "正在与协作者通信";
  }
  if (type === "collab_agent_interaction_end") return "已完成一次 Agent 协作";
  if (type === "collab_waiting_begin") return "正在等待协作者";
  if (type === "collab_waiting_end") {
    return eventRecordValue(event, "timed_out") === true
      ? "等待协作者超时"
      : "已收到协作者反馈";
  }
  if (type === "collab_agent_spawn_begin") return "正在启动协作者";
  if (type === "collab_agent_spawn_end") return "已启动协作者";
  if (type === "turn_aborted") return "任务已中止";
  if (type === "error" || type === "stream_error") {
    return livePreview(eventRecordValue(event, "message")) ?? "运行发生异常";
  }
  if (type === "task_started" || type === "turn_started") return "正在工作";
  return undefined;
}

export function liveCurrentActivityForAgent(
  events: CadenceAgentEvent[],
  agent: Pick<CadenceAgent, "status" | "summary">,
): string {
  const liveActivity = currentActivityFromSource(
    events.filter((event) => !event.source),
  );
  if (liveActivity) return liveActivity;

  const summary = livePreview(agent.summary);
  if (summary && summary !== "正在工作") return summary;

  const durableSources = [
    "organization.agent_rollout",
    "organization.message_service",
    ...[
      ...new Set(
        events
          .map((event) => event.source)
          .filter(
            (source): source is string =>
              Boolean(source) &&
              source !== "organization.agent_rollout" &&
              source !== "organization.message_service",
          ),
      ),
    ].sort(),
  ];
  for (const source of durableSources) {
    const activity = currentActivityFromSource(
      events.filter((event) => event.source === source),
    );
    if (activity) return activity;
  }

  return {
    working: "正在工作",
    waiting: "等待任务",
    paused: "任务已暂停",
    complete: "任务已完成",
    error: "运行状态异常",
    unknown: "运行状态未公开",
  }[agent.status];
}

function currentActivityFromSource(
  events: CadenceAgentEvent[],
): string | undefined {
  const sorted = events.slice().sort((left, right) => {
    const leftIndex = left.sourceIndex ?? left.seq;
    const rightIndex = right.sourceIndex ?? right.seq;
    return leftIndex - rightIndex || left.key.localeCompare(right.key);
  });
  for (let index = sorted.length - 1; index >= 0; index -= 1) {
    const event = sorted[index];
    const type = event.event.type;
    if (
      type === "agent_reasoning_delta" ||
      type === "agent_message_delta" ||
      type === "reasoning_content_delta" ||
      type === "agent_message_content_delta"
    ) {
      const deltas = sorted
        .filter(
          (candidate) =>
            candidate.submissionId === event.submissionId &&
            candidate.event.type === type &&
            candidate.seq <= event.seq,
        )
        .map((candidate) => eventDelta(eventRecordValue(candidate, "delta")))
        .join("");
      const activity = livePreview(deltas);
      if (activity) return activity;
    }
    const activity = liveActivityFromEvent(event);
    if (activity) return activity;
  }
  return undefined;
}

export function livePlanForAgent(events: CadenceAgentEvent[]): LivePlanStep[] {
  const planEvents = events
    .filter(
      (event) =>
        event.event.type === "plan_update" || event.event.type === "plan_delta",
    )
    .sort((left, right) => compareOrganizationAgentEvents(right, left));
  for (const event of planEvents) {
    const directPlan = eventRecordValue(event, "plan");
    if (Array.isArray(directPlan)) return parsePlanItems(directPlan);
    const structuredDelta = eventRecord(eventRecordValue(event, "delta"));
    if (Array.isArray(structuredDelta?.plan)) {
      return parsePlanItems(structuredDelta.plan);
    }
  }
  return [];
}

function parsePlanItems(items: unknown[]): LivePlanStep[] {
  return items.flatMap((item) => {
    const record = eventRecord(item);
    const step = sanitizeUserFacingResearchText(eventText(record?.step));
    const status = eventText(record?.status).toLowerCase();
    if (!step) return [];
    const normalized =
      status === "completed"
        ? "completed"
        : status === "in_progress" || status === "in-progress"
          ? "in_progress"
          : status === "pending"
            ? "pending"
            : undefined;
    if (!normalized) return [];
    return [{ step, status: normalized }];
  });
}

export function liveToolsForAgent(
  events: CadenceAgentEvent[],
): LiveToolActivity[] {
  const toolEvents = events
    .filter((event) => !isUserHiddenSearchEvent(event))
    .filter((event) => liveToolFamily(event.event.type))
    .sort(compareOrganizationAgentEvents);
  const latestByCall = new Map<string, CadenceAgentEvent>();
  toolEvents.forEach((event) => {
    const family = liveToolFamily(event.event.type);
    const callId = eventText(
      eventRecordValue(event, "call_id") ?? eventRecordValue(event, "callId"),
    );
    if (!family || !callId) return;
    latestByCall.set(`${family}:${callId}`, event);
  });
  const entries = [...latestByCall.values()]
    .sort(compareOrganizationAgentEvents)
    .map((event) => ({
      key: event.key,
      label: liveActivityFromEvent(event) ?? toolLabel(event),
      seq: event.seq,
    }));
  return entries.slice(-2).reverse();
}

function liveToolFamily(
  type: string,
): "mcp" | "web_search" | "exec" | undefined {
  if (type === "mcp_tool_call_begin" || type === "mcp_tool_call_end") {
    return "mcp";
  }
  if (type === "web_search_begin" || type === "web_search_end") {
    return "web_search";
  }
  if (type === "exec_command_begin" || type === "exec_command_end") {
    return "exec";
  }
  return undefined;
}

export function liveResearchCompleted(
  sessionRunning: boolean,
  agents: CadenceAgent[],
  startedAgentIds: string[],
  events: CadenceAgentEvent[] = [],
): boolean {
  if (sessionRunning || startedAgentIds.length === 0) return false;
  const statusByAgentId = new Map(
    agents.map((agent) => [agent.id, agent.status]),
  );
  const completedAgentIds = new Set(
    events
      .filter((event) =>
        ["task_complete", "turn_complete"].includes(event.event.type),
      )
      .map((event) => event.agentId),
  );
  return [...new Set(startedAgentIds)].every(
    (agentId) =>
      statusByAgentId.get(agentId) === "complete" ||
      completedAgentIds.has(agentId),
  );
}

function LiveResearchWorkspace({
  projectId,
  question,
  showQuestion = true,
  agents,
  startedAgentIds,
  events,
  artifacts,
  startedAt,
  sessionRunning,
  selectedAgentId,
  onAgentSelect,
  onOpenArtifact,
  onOpenAgentDetail,
}: {
  projectId: string;
  question?: string;
  showQuestion?: boolean;
  agents: CadenceAgent[];
  startedAgentIds: string[];
  events: CadenceAgentEvent[];
  artifacts: CadenceArtifact[];
  startedAt?: number;
  sessionRunning: boolean;
  selectedAgentId?: string;
  onAgentSelect(agentId: string): void;
  onOpenArtifact(artifactId: string): void;
  onOpenAgentDetail(agentId: string): void;
}) {
  const railRef = useRef<HTMLDivElement>(null);
  const [focusedSlot, setFocusedSlot] = useState(0);
  const slotStorageKey = `cadence.${projectId}.${startedAt ?? "current"}.live-agent-slots`;
  const [slotIds, setSlotIds] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const stored = JSON.parse(
        window.localStorage.getItem(slotStorageKey) ?? "[]",
      );
      return Array.isArray(stored)
        ? stored.filter((value): value is string => typeof value === "string")
        : [];
    } catch {
      return [];
    }
  });
  const [railAgentIds, setRailAgentIds] = useState<string[]>([]);
  const [disclosure, setDisclosure] = useState<{
    agentId: string;
    section: LiveDisclosure;
  }>();
  const [clock, setClock] = useState(() => Date.now());

  useEffect(() => {
    setRailAgentIds((current) => {
      const available = new Set(agents.map((agent) => agent.id));
      const next = current.filter((agentId) => available.has(agentId));
      agents.forEach((agent) => {
        if (!next.includes(agent.id)) next.push(agent.id);
      });
      return next;
    });
  }, [agents]);

  useEffect(() => {
    setSlotIds((current) => {
      if (!agents.length) return current;
      const available = new Set(agents.map((agent) => agent.id));
      const next = current.filter((agentId): agentId is string =>
        Boolean(agentId && available.has(agentId)),
      );
      startedAgentIds.forEach((agentId) => {
        if (
          next.length < 2 &&
          available.has(agentId) &&
          !next.includes(agentId)
        ) {
          next.push(agentId);
        }
      });
      return next.slice(0, 2);
    });
  }, [agents, startedAgentIds]);

  useEffect(() => {
    try {
      window.localStorage.setItem(slotStorageKey, JSON.stringify(slotIds));
    } catch {
      // Storage can be unavailable in embedded/private contexts.
    }
  }, [slotIds, slotStorageKey]);

  const visibleEvents = useMemo(
    () => events.filter((event) => !isUserHiddenSearchEvent(event)),
    [events],
  );
  const byAgent = useMemo(() => {
    const map = new Map<string, CadenceAgentEvent[]>();
    // Search/tool lifecycle belongs to the server receipt. Keep it out of the
    // live cards as well as the completed timeline so a transient render
    // cannot reveal the provider, query route, or machine evidence labels.
    visibleEvents.forEach((event) => {
      const current = map.get(event.agentId) ?? [];
      current.push(event);
      map.set(event.agentId, current);
    });
    return map;
  }, [visibleEvents]);

  const railAgents = railAgentIds
    .map((id) => agents.find((agent) => agent.id === id))
    .filter((agent): agent is CadenceAgent => Boolean(agent));
  const slotAgents = [0, 1].map((index) => {
    const id = slotIds[index];
    return id ? agents.find((agent) => agent.id === id) : undefined;
  });
  const researchCompleted = liveResearchCompleted(
    sessionRunning,
    agents,
    startedAgentIds,
    visibleEvents,
  );
  const activatedAgentIds = new Set(
    startedAgentIds.length
      ? startedAgentIds
      : agents
          .filter((agent) =>
            ["working", "complete", "error"].includes(agent.status),
          )
          .map((agent) => agent.id),
  );
  const focusedAgent =
    slotAgents[focusedSlot] ??
    slotAgents.find((agent): agent is CadenceAgent => Boolean(agent));
  const overallActivity = focusedAgent
    ? liveCurrentActivityForAgent(
        byAgent.get(focusedAgent.id) ?? [],
        focusedAgent,
      )
    : researchCompleted
      ? "结果整理完成"
      : "等待专家公开进展";

  useEffect(() => {
    if (!startedAt || researchCompleted) return;
    setClock(Date.now());
    const timer = window.setInterval(() => setClock(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [researchCompleted, startedAt]);

  const selectAgent = (agentId: string, slot = focusedSlot) => {
    const existingSlot = slotIds.findIndex((id) => id === agentId);
    if (existingSlot >= 0) {
      setFocusedSlot(existingSlot);
      onAgentSelect(agentId);
      return;
    }
    setFocusedSlot(slot);
    setSlotIds((current) => {
      const next = [...current];
      next[slot] = agentId;
      return next.slice(0, 2);
    });
    onAgentSelect(agentId);
  };
  const scrollRail = (amount: number) => {
    railRef.current?.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <section
      className="live-research"
      data-research-state={researchCompleted ? "completed" : "running"}
      data-testid="live-research"
    >
      <div className="live-research-heading">
        <div>
          <h2 data-testid="live-research-state">
            {researchCompleted ? "研究已完成" : "正在研究"}
          </h2>
          {showQuestion && (
            <p>{question ? `“${question}”` : "当前研究任务"}</p>
          )}
          <div
            className="live-research-summary"
            data-testid="live-research-summary"
          >
            <span>研究请求已提交</span>
            <span>
              已激活 {activatedAgentIds.size} / {agents.length} 位专家
            </span>
            {startedAt && (
              <span>
                {researchCompleted ? "总耗时" : "已运行"}{" "}
                {formatResearchElapsed(clock - startedAt)}
              </span>
            )}
            <span>当前阶段 · {overallActivity}</span>
          </div>
        </div>
        <span className="live-research-source">
          {researchCompleted
            ? "所有已启动专家均已完成"
            : visibleEvents.length
              ? "来自专家实时进展"
              : "来自当前会话运行状态"}
        </span>
      </div>
      <div className="live-research-slots">
        {slotAgents.map((agent, index) =>
          agent ? (
            <LiveResearchCard
              key={agent.id}
              agent={agent}
              events={byAgent.get(agent.id) ?? []}
              artifacts={artifacts.filter((item) => item.owner === agent.id)}
              focused={focusedSlot === index}
              disclosure={
                disclosure?.agentId === agent.id
                  ? disclosure.section
                  : undefined
              }
              onFocus={() => {
                setFocusedSlot(index);
                onAgentSelect(agent.id);
              }}
              onDisclosure={(section) =>
                setDisclosure((current) =>
                  current?.agentId === agent.id && current.section === section
                    ? undefined
                    : { agentId: agent.id, section },
                )
              }
              onOpenArtifact={onOpenArtifact}
              onOpenDetails={() => onOpenAgentDetail(agent.id)}
            />
          ) : (
            <div className="live-agent-slot-empty" key={`empty-${index}`}>
              等待专家启动
            </div>
          ),
        )}
      </div>
      <div className="live-agent-rail-wrap">
        <div className="live-agent-rail-heading">
          <span>研究专家</span>
          <small>
            {slotIds.length} / {railAgents.length} 个展示位
          </small>
        </div>
        <div
          className="live-agent-rail"
          data-testid="agent-rail"
          ref={railRef}
          tabIndex={0}
          aria-label="研究专家列表"
          onWheel={(event) => {
            if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
              event.currentTarget.scrollLeft += event.deltaY;
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") {
              event.preventDefault();
              scrollRail(-220);
            } else if (event.key === "ArrowRight") {
              event.preventDefault();
              scrollRail(220);
            }
          }}
        >
          {railAgents.map((agent) => (
            <button
              key={agent.id}
              type="button"
              data-testid={`agent-rail-item-${agent.id}`}
              className={`live-agent-rail-item ${slotIds.includes(agent.id) ? "is-slotted" : ""}`}
              aria-label={`切换 ${agent.name}`}
              onClick={() => selectAgent(agent.id)}
            >
              <span className={`status-dot status-dot-${agent.status}`} />
              <strong>{agent.name}</strong>
              <small>{statusLabel[agent.status]}</small>
            </button>
          ))}
          {railAgents.length > 2 && (
            <span className="live-agent-rail-overflow">
              +{railAgents.length - 2}
            </span>
          )}
        </div>
        <div className="live-agent-rail-actions">
          <button
            type="button"
            aria-label="研究专家列表向左滚动"
            onClick={() => scrollRail(-220)}
          >
            <ChevronLeft size={14} />
          </button>
          <button
            type="button"
            aria-label="研究专家列表向右滚动"
            onClick={() => scrollRail(220)}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </section>
  );
}

function LivePublicEventTranscript({
  events,
  compact = false,
}: {
  events: CadenceAgentEvent[];
  compact?: boolean;
}) {
  const detailEvents = livePublicDetailEvents(events);
  if (!detailEvents.length) {
    return <span>暂无可公开的执行事件。</span>;
  }
  return (
    <div
      className={`live-event-transcript ${compact ? "is-compact" : ""}`}
      data-testid="agent-event-transcript"
      data-retained-event-count={detailEvents.length}
    >
      {detailEvents.map((event) => {
        return (
          <article
            className="live-detail-event"
            data-testid={`agent-detail-event-${event.key}`}
            data-event-type={event.event.type}
            data-event-seq={event.seq}
            key={event.key}
          >
            <header>
              <small>
                {event.source
                  ? `${event.source} #${event.sourceIndex ?? event.seq}`
                  : `seq ${event.seq}`}
              </small>
              <strong>{event.event.type}</strong>
              <span>{liveActivityFromEvent(event) ?? "公开事件"}</span>
            </header>
            <pre>{livePublicDetailPayload(event)}</pre>
          </article>
        );
      })}
    </div>
  );
}

function LiveResearchCard({
  agent,
  events,
  artifacts,
  focused,
  disclosure,
  onFocus,
  onDisclosure,
  onOpenArtifact,
  onOpenDetails,
}: {
  agent: CadenceAgent;
  events: CadenceAgentEvent[];
  artifacts: CadenceArtifact[];
  focused: boolean;
  disclosure?: LiveDisclosure;
  onFocus(): void;
  onDisclosure(section: LiveDisclosure): void;
  onOpenArtifact(artifactId: string): void;
  onOpenDetails(): void;
}) {
  const sortedEvents = events.slice().sort(compareOrganizationAgentEvents);
  const activity = liveCurrentActivityForAgent(sortedEvents, agent);
  const plan = livePlanForAgent(sortedEvents);
  const tools = liveToolsForAgent(sortedEvents);
  const completedPlan = plan.filter(
    (step) => step.status === "completed",
  ).length;
  return (
    <article
      className={`live-agent-card ${focused ? "is-focused" : ""}`}
      data-testid={`agent-rail-slot-${agent.id}`}
      data-live-agent-card={agent.id}
      data-agent-status={agent.status}
      onClick={onFocus}
    >
      <header className="live-agent-card-header">
        <div>
          <span className="live-agent-role">{agent.role}</span>
          <h3>{agent.name}</h3>
        </div>
        <span className="live-agent-status">
          <span className={`status-dot status-dot-${agent.status}`} />
          {statusLabel[agent.status]}
        </span>
      </header>
      <section className="live-agent-activity">
        <span className="live-section-label">当前阶段</span>
        <p>{activity}</p>
      </section>
      <div className="live-agent-card-controls">
        <button
          type="button"
          aria-expanded={disclosure === "detail"}
          onClick={(event) => {
            event.stopPropagation();
            onDisclosure("detail");
          }}
        >
          查看执行详情
        </button>
        {plan.length > 0 && (
          <button
            type="button"
            aria-expanded={disclosure === "plan"}
            data-testid="agent-plan-disclosure"
            onClick={(event) => {
              event.stopPropagation();
              onDisclosure("plan");
            }}
          >
            计划 {completedPlan} / {plan.length}
          </button>
        )}
        {tools.length > 0 && (
          <button
            type="button"
            aria-expanded={disclosure === "tool"}
            data-testid="agent-tool-disclosure"
            onClick={(event) => {
              event.stopPropagation();
              onDisclosure("tool");
            }}
          >
            工具记录 {tools.length}
          </button>
        )}
      </div>
      {disclosure === "detail" && (
        <div
          className="live-agent-disclosure"
          data-testid="agent-detail-panel"
          data-retained-event-count={sortedEvents.length}
        >
          <LivePublicEventTranscript events={sortedEvents} compact />
          <button type="button" onClick={onOpenDetails}>
            打开 Agent 详情
          </button>
        </div>
      )}
      {disclosure === "plan" && (
        <div className="live-agent-disclosure">
          {plan.length ? (
            plan.map((step) => (
              <div className="live-plan-step" key={step.step}>
                <span>
                  {step.status === "completed"
                    ? "✓"
                    : step.status === "in_progress"
                      ? "●"
                      : "○"}
                </span>
                <span>{step.step}</span>
              </div>
            ))
          ) : (
            <span>当前运行尚未公开研究计划。</span>
          )}
        </div>
      )}
      {disclosure === "tool" && (
        <div className="live-agent-disclosure">
          {tools.length ? (
            tools.map((tool) => (
              <div className="live-tool-line" key={tool.key}>
                <span>{tool.label}</span>
                <small>seq {tool.seq}</small>
              </div>
            ))
          ) : (
            <span>当前运行尚未公开工具记录。</span>
          )}
        </div>
      )}
      {artifacts.length > 0 && (
        <div
          className="live-agent-artifacts"
          data-testid="agent-artifact-disclosure"
        >
          <span className="live-section-label">研究产物</span>
          {artifacts.map((artifact) => (
            <button
              type="button"
              key={artifact.id}
              onClick={(event) => {
                event.stopPropagation();
                onOpenArtifact(artifact.id);
              }}
            >
              <FileText size={13} />
              {artifact.name}
            </button>
          ))}
        </div>
      )}
    </article>
  );
}

function RuntimeCatalogState({
  kind,
  onRetry,
  onRetryArtifacts,
  onRetryTeam,
}: {
  kind: "all" | "artifacts" | "team" | "loading";
  onRetry?(): void;
  onRetryArtifacts?(): void;
  onRetryTeam?(): void;
}) {
  const content = {
    all: {
      title: "项目运行信息暂不可得",
      description: "这不代表项目中没有团队或产物；恢复后会显示真实项目状态。",
      retryLabel: "重试读取项目运行信息",
      testId: "runtime-catalog-unavailable",
    },
    artifacts: {
      title: "暂时无法读取项目产物",
      description: "这不代表项目中没有产物；恢复后会显示真实产物目录。",
      retryLabel: "重试读取项目产物",
      testId: "runtime-artifact-catalog-unavailable",
    },
    team: {
      title: "暂时无法读取研究团队",
      description: "这不代表项目中没有研究成员；恢复后会显示真实团队状态。",
      retryLabel: "重试读取研究团队",
      testId: "runtime-team-catalog-unavailable",
    },
    loading: {
      title: "正在读取项目运行信息",
      description: "团队与产物会在真实数据返回后显示。",
      retryLabel: "",
      testId: "runtime-catalog-loading",
    },
  }[kind];

  return (
    <section
      className={`runtime-catalog-state runtime-catalog-state-${kind}`}
      data-testid={content.testId}
      role={kind === "loading" ? "status" : "alert"}
      aria-live={kind === "loading" ? "polite" : "assertive"}
    >
      <div className="runtime-catalog-state-copy">
        <span className="runtime-catalog-state-icon" aria-hidden="true">
          {kind === "loading" ? (
            <LoaderCircle size={18} />
          ) : (
            <AlertTriangle size={18} />
          )}
        </span>
        <div>
          <strong>{content.title}</strong>
          <p>{content.description}</p>
        </div>
      </div>
      {kind === "all" ? (
        /* “未知”必须保留两项独立事实和独立恢复路径，不能被压成一个
           看似空白的项目状态。 */
        <div className="runtime-catalog-status-list" aria-label="运行信息读取状态">
          <div className="runtime-catalog-status-row">
            <span>产物目录暂不可读</span>
            {onRetryArtifacts && (
              <button type="button" onClick={onRetryArtifacts}>
                重试产物
              </button>
            )}
          </div>
          <div className="runtime-catalog-status-row">
            <span>研究团队暂不可读</span>
            {onRetryTeam && (
              <button type="button" onClick={onRetryTeam}>
                重试团队
              </button>
            )}
          </div>
        </div>
      ) : onRetry ? (
        <button
          type="button"
          className="runtime-catalog-state-retry"
          onClick={onRetry}
        >
          <RefreshCw size={14} aria-hidden="true" />
          {content.retryLabel}
        </button>
      ) : null}
    </section>
  );
}

function LiveArtifactsPanel({
  artifacts,
  agents,
  onOpenArtifact,
  activeArtifact,
  besideArtifact,
  openIds,
  sessionRunning,
  streamState,
  onReturnToResearch,
  onSetActiveArtifact,
  onCloseArtifact,
  onOpenArtifactBeside,
  onSetBesideArtifact,
  onExitArtifactComparison,
  mobileSheet,
  onDismissMobileSheet,
  onArtifactDetailRequest,
  onArtifactDetailRetry,
  scientificSelections,
  onStructureSelectionChange,
  onScientificSelectionChange,
  scientificOperation,
  onScientificApply,
  artifactDetailStates,
  artifactDetailErrors,
}: {
  artifacts: CadenceArtifact[];
  agents: CadenceAgent[];
  onOpenArtifact(artifactId: string): void;
  activeArtifact?: CadenceArtifact;
  besideArtifact?: CadenceArtifact;
  openIds: string[];
  sessionRunning: boolean;
  streamState: string;
  onReturnToResearch(): void;
  onSetActiveArtifact(id: string | undefined): void;
  onCloseArtifact(id: string): void;
  onOpenArtifactBeside(id: string): void;
  onSetBesideArtifact(id: string | undefined): void;
  onExitArtifactComparison(): void;
  mobileSheet: boolean;
  onDismissMobileSheet(): void;
  onArtifactDetailRequest?(artifactId: string): void;
  onArtifactDetailRetry?(artifactId: string): void;
  scientificSelections: Record<string, ScientificSelection>;
  onStructureSelectionChange(selection: StructureScientificSelection): void;
  onScientificSelectionChange(selection: ScientificSelection): void;
  scientificOperation?: ScientificOperationResult;
  onScientificApply?: (
    artifactId: string,
    baseRevision: number,
    document: ScientificArtifactDocument,
  ) => Promise<ScientificArtifactDocument>;
  artifactDetailStates?: CadenceBioAppProps["artifactDetailStates"];
  artifactDetailErrors?: CadenceBioAppProps["artifactDetailErrors"];
}) {
  const ordered = artifacts.slice().sort((left, right) => {
    const leftTime = Date.parse(left.updatedAt ?? "");
    const rightTime = Date.parse(right.updatedAt ?? "");
    return (
      (Number.isFinite(rightTime) ? rightTime : 0) -
      (Number.isFinite(leftTime) ? leftTime : 0)
    );
  });
  const activeOpenArtifact =
    activeArtifact && openIds.includes(activeArtifact.id)
      ? activeArtifact
      : undefined;
  const [browserOpen, setBrowserOpen] = useState(
    () => activeOpenArtifact === undefined,
  );
  const hadOpenArtifactRef = useRef(Boolean(activeOpenArtifact));

  useEffect(() => {
    if (!activeOpenArtifact) {
      hadOpenArtifactRef.current = false;
      setBrowserOpen(true);
      return;
    }
    if (!hadOpenArtifactRef.current) {
      hadOpenArtifactRef.current = true;
      setBrowserOpen(false);
    }
  }, [activeOpenArtifact?.id]);

  // Browsing is only a way back to project Artifacts. It must not discard the
  // persisted open/active/beside context that carries the user's real research view.
  const openFromBrowser = (id: string) => {
    setBrowserOpen(false);
    onOpenArtifact(id);
  };
  const selectOpenArtifact = (id: string) => {
    setBrowserOpen(false);
    onSetActiveArtifact(id);
  };
  const compareFromBrowser = (id: string) => {
    setBrowserOpen(false);
    onOpenArtifactBeside(id);
  };

  return (
    <section
      className="live-artifacts-panel"
      data-testid="live-artifacts-panel"
    >
      {ordered.length ? (
        activeOpenArtifact && !browserOpen ? (
          <ArtifactWorkbench
            artifacts={artifacts}
            agents={agents}
            activeArtifact={activeOpenArtifact}
            besideArtifact={besideArtifact}
            openIds={openIds}
            sessionRunning={sessionRunning}
            streamState={streamState}
            onReturnToResearch={onReturnToResearch}
            onBrowseArtifacts={() => setBrowserOpen(true)}
            onSetActiveArtifact={onSetActiveArtifact}
            onCloseArtifact={onCloseArtifact}
            onOpenArtifactBeside={onOpenArtifactBeside}
            onSetBesideArtifact={onSetBesideArtifact}
            onExitArtifactComparison={onExitArtifactComparison}
            mobileSheet={mobileSheet}
            onDismissMobileSheet={onDismissMobileSheet}
            onArtifactDetailRequest={onArtifactDetailRequest}
            onArtifactDetailRetry={onArtifactDetailRetry}
            scientificSelections={scientificSelections}
            onStructureSelectionChange={onStructureSelectionChange}
            onScientificSelectionChange={onScientificSelectionChange}
            scientificOperation={scientificOperation}
            onScientificApply={onScientificApply}
            artifactDetailStates={artifactDetailStates}
            artifactDetailErrors={artifactDetailErrors}
          />
        ) : (
          <ArtifactBrowser
            artifacts={ordered}
            activeArtifact={activeOpenArtifact}
            openIds={openIds}
            onOpenArtifact={openFromBrowser}
            onSetActiveArtifact={selectOpenArtifact}
            onCloseArtifact={onCloseArtifact}
            onCompareArtifact={compareFromBrowser}
            onReturnToArtifact={
              activeOpenArtifact
                ? () => setBrowserOpen(false)
                : undefined
            }
          />
        )
      ) : (
        <div className="live-artifacts-empty" role="status">
          <strong>尚未产生研究产物</strong>
          <span>研究团队完成可追溯结果后，会出现在这里。</span>
        </div>
      )}
    </section>
  );
}

function ArtifactBrowser({
  artifacts,
  activeArtifact,
  openIds,
  onOpenArtifact,
  onSetActiveArtifact,
  onCloseArtifact,
  onCompareArtifact,
  onReturnToArtifact,
}: {
  artifacts: CadenceArtifact[];
  activeArtifact?: CadenceArtifact;
  openIds: string[];
  onOpenArtifact(id: string): void;
  onSetActiveArtifact(id: string): void;
  onCloseArtifact(id: string): void;
  onCompareArtifact?(id: string): void;
  onReturnToArtifact?(): void;
}) {
  const openArtifacts = openIds.flatMap((id) => {
    const artifact = artifacts.find((item) => item.id === id);
    return artifact ? [artifact] : [];
  });
  const unopenedArtifacts = artifacts.filter(
    (artifact) => !openIds.includes(artifact.id),
  );
  const nonFinalArtifacts = unopenedArtifacts.filter((artifact) => {
    const state = artifact.publishedPresentation?.deliveryState;
    return state === "draft" || state === "blocked";
  });
  const projectArtifacts = unopenedArtifacts.filter(
    (artifact) => !nonFinalArtifacts.includes(artifact),
  );
  const browserArtifacts = [
    ...openArtifacts,
    ...projectArtifacts,
    ...nonFinalArtifacts,
  ];
  const completedDeliverableCount = artifacts.filter(
    (artifact) =>
      artifact.publishedPresentation &&
      (artifact.publishedPresentation.deliveryState ?? "final") === "final",
  ).length;
  const projectSectionNeedsHeading =
    openArtifacts.length > 0 || nonFinalArtifacts.length > 0;
  const renderArtifact = (artifact: CadenceArtifact) => {
    const isOpen = openIds.includes(artifact.id);
    const browserIndex = browserArtifacts.findIndex(
      (item) => item.id === artifact.id,
    );
    const displayName = artifactDisplayName(artifact);
    const deliveryLabel = artifactDeliveryStateLabel(
      artifact.publishedPresentation?.deliveryState,
    );
    return (
      <div
        className={`artifact-browser-row ${artifact.id === activeArtifact?.id ? "is-active" : ""}`}
        key={artifact.id}
      >
        <button
          className="artifact-browser-open"
          type="button"
          data-testid={
            browserIndex === 0
              ? "artifact-open"
              : `artifact-open-${artifact.id}`
          }
          data-artifact-id={artifact.id}
          aria-current={
            artifact.id === activeArtifact?.id ? "true" : undefined
          }
          onClick={() => {
            if (isOpen) {
              onSetActiveArtifact(artifact.id);
              return;
            }
            onOpenArtifact(artifact.id);
          }}
        >
          <FileText size={15} aria-hidden="true" />
          <span data-testid={`live-artifact-${artifact.id}`}>
            <strong>{displayName}</strong>
            <small>{artifactPresentationLabel(artifact)}</small>
          </span>
          <div className="artifact-browser-row-meta">
            {deliveryLabel && (
              <span
                className={`artifact-delivery-state is-${artifact.publishedPresentation?.deliveryState}`}
                data-testid={`artifact-delivery-state-${artifact.id}`}
              >
                {deliveryLabel}
              </span>
            )}
            {isOpen && <em>已打开</em>}
          </div>
        </button>
        {isOpen && (
          <button
            className="artifact-browser-close"
            type="button"
            data-testid={`artifact-browser-close-${artifact.id}`}
            aria-label={`关闭产物 ${displayName}`}
            title={`关闭产物 ${displayName}`}
            onClick={() => onCloseArtifact(artifact.id)}
          >
            <X size={14} aria-hidden="true" />
          </button>
        )}
        {activeArtifact && artifact.id !== activeArtifact.id && (
          <button
            className="artifact-browser-compare"
            data-testid={`artifact-browser-compare-${artifact.id}`}
            type="button"
            disabled={!onCompareArtifact}
            title={`与 ${artifactDisplayName(activeArtifact)} 比较`}
            onClick={() => onCompareArtifact?.(artifact.id)}
          >
            比较
          </button>
        )}
      </div>
    );
  };

  return (
    <section
      className="artifact-browser"
      data-testid="artifact-browser"
      aria-label="全部项目产物"
    >
      <header className="artifact-browser-head">
        <div>
          <h2>
            全部产物 <span>{artifacts.length}</span>
          </h2>
          <p>
            {completedDeliverableCount
              ? `${completedDeliverableCount} 项已发布交付`
              : "按最近更新排序"}
            {nonFinalArtifacts.length
              ? ` · ${nonFinalArtifacts.length} 项不计入正式交付`
              : ""}
          </p>
        </div>
        {onReturnToArtifact && activeArtifact && (
          <button
            className="artifact-browser-return"
            data-testid="artifact-browser-return"
            type="button"
            onClick={onReturnToArtifact}
          >
            返回查看
          </button>
        )}
      </header>
      <div className="artifact-browser-list">
        {openArtifacts.length > 0 && (
          <section className="artifact-browser-section" aria-label="已打开的 Artifact">
            <div className="artifact-browser-section-head">
              <span>已打开</span>
              <small>{openArtifacts.length}</small>
            </div>
            {openArtifacts.map(renderArtifact)}
          </section>
        )}
        {projectArtifacts.length > 0 && (
          <section className="artifact-browser-section" aria-label="项目 Artifact">
            {projectSectionNeedsHeading && (
              <div className="artifact-browser-section-head">
                <span>{openArtifacts.length ? "其他产物" : "项目产物"}</span>
                <small>{projectArtifacts.length}</small>
              </div>
            )}
            {projectArtifacts.map(renderArtifact)}
          </section>
        )}
        {nonFinalArtifacts.length > 0 && (
          <section
            className="artifact-browser-section artifact-browser-pending-section"
            aria-label="草稿与受阻说明"
          >
            <div className="artifact-browser-section-head">
              <span>草稿与受阻说明</span>
              <small>{nonFinalArtifacts.length} · 不计入正式交付</small>
            </div>
            {nonFinalArtifacts.map(renderArtifact)}
          </section>
        )}
      </div>
    </section>
  );
}

function ArtifactWorkbench({
  artifacts,
  agents,
  activeArtifact,
  besideArtifact,
  openIds,
  sessionRunning,
  streamState,
  onReturnToResearch,
  onBrowseArtifacts,
  onSetActiveArtifact,
  onCloseArtifact,
  onOpenArtifactBeside,
  onSetBesideArtifact,
  onExitArtifactComparison,
  mobileSheet,
  onDismissMobileSheet,
  onArtifactDetailRequest,
  onArtifactDetailRetry,
  scientificSelections,
  onStructureSelectionChange,
  onScientificSelectionChange,
  scientificOperation,
  onScientificApply,
  artifactDetailStates,
  artifactDetailErrors,
}: {
  artifacts: CadenceArtifact[];
  agents: CadenceAgent[];
  activeArtifact: CadenceArtifact;
  besideArtifact?: CadenceArtifact;
  openIds: string[];
  sessionRunning: boolean;
  streamState: string;
  onReturnToResearch(): void;
  onBrowseArtifacts(): void;
  onSetActiveArtifact(id: string | undefined): void;
  onCloseArtifact(id: string): void;
  onOpenArtifactBeside(id: string): void;
  onSetBesideArtifact(id: string | undefined): void;
  onExitArtifactComparison(): void;
  mobileSheet: boolean;
  onDismissMobileSheet(): void;
  onArtifactDetailRequest?(artifactId: string): void;
  onArtifactDetailRetry?(artifactId: string): void;
  scientificSelections: Record<string, ScientificSelection>;
  onStructureSelectionChange(selection: StructureScientificSelection): void;
  onScientificSelectionChange(selection: ScientificSelection): void;
  scientificOperation?: ScientificOperationResult;
  onScientificApply?: (
    artifactId: string,
    baseRevision: number,
    document: ScientificArtifactDocument,
  ) => Promise<ScientificArtifactDocument>;
  artifactDetailStates?: CadenceBioAppProps["artifactDetailStates"];
  artifactDetailErrors?: CadenceBioAppProps["artifactDetailErrors"];
}) {
  const openArtifacts = openIds.flatMap((id) => {
    const artifact = artifacts.find((item) => item.id === id);
    return artifact ? [artifact] : [];
  });
  const mobileSheetRef = useRef<HTMLElement>(null);
  const artifactTabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const splitViewActive =
    besideArtifact !== undefined && besideArtifact.id !== activeArtifact.id;
  const [maximizedArtifactId, setMaximizedArtifactId] = useState<string>();
  const comparedArtifacts =
    besideArtifact && besideArtifact.id !== activeArtifact.id
      ? [activeArtifact, besideArtifact]
      : [activeArtifact];
  const visibleArtifacts = maximizedArtifactId
    ? comparedArtifacts.filter((artifact) => artifact.id === maximizedArtifactId)
    : comparedArtifacts;
  const comparisonCandidates = openArtifacts.filter(
    (artifact) => artifact.id !== activeArtifact.id,
  );

  useEffect(() => {
    if (
      maximizedArtifactId &&
      (!splitViewActive ||
        !comparedArtifacts.some(
          (artifact) => artifact.id === maximizedArtifactId,
        ))
    ) {
      setMaximizedArtifactId(undefined);
    }
  }, [comparedArtifacts, maximizedArtifactId, splitViewActive]);

  const toggleComparisonFromActivePane = () => {
    if (splitViewActive) {
      onExitArtifactComparison();
      return;
    }
    const next = comparisonCandidates[0];
    if (next) onSetBesideArtifact(next.id);
  };

  const activateArtifactTab = (artifactId: string) => {
    onSetActiveArtifact(artifactId);
    // A selected workbench tab is the primary pane. If it was the temporary
    // comparison pane, end that reading-only comparison instead of retaining
    // an invalid "active beside itself" layout state.
    if (besideArtifact?.id === artifactId) onExitArtifactComparison();
  };

  const moveArtifactTabFocus = (
    event: ReactKeyboardEvent,
    index: number,
  ) => {
    const key = event.key;
    if (![
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End",
    ].includes(key)) {
      return;
    }
    event.preventDefault();
    const nextIndex =
      key === "Home"
        ? 0
        : key === "End"
          ? openArtifacts.length - 1
          : (index + (key === "ArrowRight" ? 1 : -1) + openArtifacts.length) %
            openArtifacts.length;
    const next = openArtifacts[nextIndex];
    if (!next) return;
    activateArtifactTab(next.id);
    requestAnimationFrame(() => artifactTabRefs.current[next.id]?.focus());
  };

  useEffect(() => {
    if (!mobileSheet) return;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => {
      mobileSheetRef.current
        ?.querySelector<HTMLButtonElement>('[data-testid="artifact-close"]')
        ?.focus();
    });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onDismissMobileSheet();
        return;
      }
      if (event.key !== "Tab") return;
      const sheet = mobileSheetRef.current;
      if (!sheet?.contains(document.activeElement)) return;
      const focusable = Array.from(
        sheet.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), select:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => !element.hasAttribute("hidden"));
      if (!focusable.length) return;
      const currentIndex = focusable.findIndex(
        (element) => element === document.activeElement,
      );
      const nextIndex = event.shiftKey
        ? currentIndex <= 0
          ? focusable.length - 1
          : currentIndex - 1
        : currentIndex >= focusable.length - 1
          ? 0
          : currentIndex + 1;
      event.preventDefault();
      focusable[nextIndex]?.focus();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (focusFrame !== undefined) window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousBodyOverflow;
    };
  }, [mobileSheet, onDismissMobileSheet]);

  return (
    <section
      ref={mobileSheetRef}
      className={`artifact-workbench ${openIds.length > 1 ? "" : "artifact-workbench-single"} ${splitViewActive ? "artifact-workbench-comparing" : ""} ${mobileSheet ? "artifact-workbench-mobile-sheet" : ""}`}
      data-testid="artifact-workspace"
      aria-label="已打开的产物"
      role={mobileSheet ? "dialog" : undefined}
      aria-modal={mobileSheet || undefined}
    >
      <header className="artifact-workbench-header">
        <nav
          className="artifact-workbench-tabs"
          data-testid="artifact-workbench-tabs"
          role="tablist"
          aria-label="已打开的 Artifact"
        >
          {openArtifacts.map((artifact, index) => {
            const active = artifact.id === activeArtifact.id;
            return (
              <button
                key={artifact.id}
                type="button"
                role="tab"
                aria-selected={active}
                tabIndex={active ? 0 : -1}
                className={active ? "is-active" : undefined}
                title={`查看 ${artifactDisplayName(artifact)}`}
                onClick={() => activateArtifactTab(artifact.id)}
                onKeyDown={(event) => moveArtifactTabFocus(event, index)}
                ref={(node) => {
                  artifactTabRefs.current[artifact.id] = node;
                }}
              >
                <FileText size={13} aria-hidden="true" />
                <span>{artifactDisplayName(artifact)}</span>
                {artifactCanonicalRevision(artifact) && (
                  <small>v{artifactCanonicalRevision(artifact)}</small>
                )}
              </button>
            );
          })}
        </nav>
        <div className="artifact-workbench-controls">
          {comparisonCandidates.length > 0 && (
            <label className="artifact-comparison-select">
              <LayoutPanelLeft size={14} aria-hidden="true" />
              <span>比较</span>
              <select
                data-testid="artifact-compare-select"
                aria-label="选择并排比较的 Artifact"
                value={splitViewActive ? (besideArtifact?.id ?? "") : ""}
                onChange={(event) => {
                  const nextId = event.target.value;
                  if (nextId) onSetBesideArtifact(nextId);
                  else onExitArtifactComparison();
                }}
              >
                <option value="">不比较</option>
                {comparisonCandidates.map((artifact) => (
                  <option key={artifact.id} value={artifact.id}>
                    {artifactDisplayName(artifact)}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </header>
      {sessionRunning && (
        <div
          className="artifact-running-indicator"
          data-testid="artifact-running-indicator"
          role="status"
        >
          <span className="status-dot status-dot-working" />
          {streamState === "reconnecting" || streamState === "connecting"
            ? "Research reconnecting"
            : "Research running"}
          <button type="button" onClick={onReturnToResearch}>
            返回 Live Research
          </button>
        </div>
      )}
      {/* Comparison is a human-only reading mode over the same persisted Artifacts.
          It must never alter an Execution, Artifact version, or Runtime Agent work. */}
      {splitViewActive && (
        <div
          className="artifact-comparison-bar"
          data-testid="artifact-comparison-bar"
          role="status"
        >
          <span>
            <LayoutPanelLeft size={14} aria-hidden="true" />
            比较中
          </span>
          <small>
            {artifactDisplayName(activeArtifact)} 与 {besideArtifact ? artifactDisplayName(besideArtifact) : ""}
          </small>
          <button
            data-testid="artifact-compare-exit"
            type="button"
            onClick={onExitArtifactComparison}
          >
            退出比较
          </button>
        </div>
      )}
      <div
        className={`artifact-view-grid ${splitViewActive ? "artifact-view-beside" : ""}`}
      >
        {visibleArtifacts.map((artifact) => {
          const active = artifact.id === activeArtifact.id;
          const beside = !active;
          return (
            <div
              className={`artifact-view-slot ${active ? "artifact-view-slot-active" : ""}`}
              key={artifact.id}
            >
              <ArtifactPreview
                artifact={artifact}
                artifacts={artifacts}
                beside={active ? splitViewActive : beside}
                artifactCount={active ? artifacts.length : undefined}
                openArtifactCount={active ? openIds.length : undefined}
                onBrowseArtifacts={active ? onBrowseArtifacts : undefined}
                ownerAgent={agents.find((agent) => agent.id === artifact.owner)}
                onClose={() => onCloseArtifact(artifact.id)}
                onOpenBeside={toggleComparisonFromActivePane}
                canOpenBeside={comparisonCandidates.length > 0 || splitViewActive}
                maximized={splitViewActive && maximizedArtifactId === artifact.id}
                onToggleMaximize={
                  splitViewActive
                    ? () =>
                        setMaximizedArtifactId((current) =>
                          current === artifact.id ? undefined : artifact.id,
                        )
                    : undefined
                }
                onArtifactDetailRequest={onArtifactDetailRequest}
                detailState={artifactDetailStates?.[artifact.id]}
                detailError={artifactDetailErrors?.[artifact.id]}
                onArtifactDetailRetry={onArtifactDetailRetry}
                artifactDetailStates={artifactDetailStates}
                artifactDetailErrors={artifactDetailErrors}
                scientificSelections={scientificSelections}
                onStructureSelectionChange={onStructureSelectionChange}
                onScientificSelectionChange={onScientificSelectionChange}
                scientificOperation={
                  scientificOperation &&
                  scientificOperation.selection.artifactId === artifact.id &&
                  scientificOperation.base_revision ===
                    artifactCanonicalRevision(artifact)
                    ? scientificOperation
                    : undefined
                }
                onScientificApply={onScientificApply}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ProjectHistoryRows({
  id,
  turns,
  loading,
  error,
  onRetry,
  onTurnOpen,
  onViewAll,
}: {
  id: string;
  turns: CadenceTurn[];
  loading: boolean;
  error?: string;
  onRetry?(): void;
  onTurnOpen(turnId: string): void;
  onViewAll(): void;
}) {
  return (
    <div className="project-history" id={id} aria-label="研究请求历史">
      {loading && turns.length === 0 ? (
        <span className="project-history-state">正在读取请求历史…</span>
      ) : error && turns.length === 0 ? (
        <div className="project-history-state project-history-error" role="alert">
          <span>请求历史暂时无法读取。</span>
          {onRetry && (
            <button type="button" onClick={onRetry}>
              重试
            </button>
          )}
        </div>
      ) : turns.length === 0 ? (
        <span className="project-history-state">尚无研究请求</span>
      ) : (
        <>
          {error && (
            <div className="project-history-state project-history-error" role="status">
              <span>部分历史可能未刷新。</span>
              {onRetry && (
                <button type="button" onClick={onRetry}>
                  重试
                </button>
              )}
            </div>
          )}
          <div className="project-history-list">
            {turns.map((turn) => {
              const status = turn.submissionUncertain
                ? "提交待确认"
                : turnStatusLabel(turn.status);
              return (
                <button
                  type="button"
                  className="project-history-turn"
                  data-testid={`project-history-turn-${turn.id}`}
                  key={turn.id}
                  title={turn.input || "基于附件的研究请求"}
                  aria-label={`${status}：${turn.input || "基于附件的研究请求"}`}
                  onClick={() => onTurnOpen(turn.id)}
                >
                  <span
                    className={`project-history-status project-history-status-${turn.submissionUncertain ? "pending" : turn.status}`}
                    aria-hidden="true"
                  />
                  <span className="project-history-turn-copy">
                    <span>{turn.input || "基于附件的研究请求"}</span>
                    <small>
                      {turn.createdAt ? formatTimelineTime(turn.createdAt) : "刚刚"}
                    </small>
                  </span>
                </button>
              );
            })}
          </div>
          <button
            className="project-history-all"
            data-testid="project-history-view-all"
            type="button"
            onClick={onViewAll}
          >
            在工作区查看全部请求
          </button>
        </>
      )}
    </div>
  );
}

function RequestTimeline({
  projectId,
  turns,
  agents,
  events,
  artifacts,
  childThreadIds,
  selectedAgentId,
  loading,
  loadingOlder,
  hasMore,
  error,
  onRetry,
  retryLabel = "重试",
  activeTurnId,
  cancellingTurn,
  cancelError,
  onLoadOlder,
  onCancelActive,
  onAgentSelect,
  onOpenArtifact,
  onOpenAgentDetail,
  onContinueResearch,
}: {
  projectId: string;
  turns: CadenceTurn[];
  agents: CadenceAgent[];
  events: CadenceAgentEvent[];
  artifacts: CadenceArtifact[];
  childThreadIds: ReadonlySet<string>;
  selectedAgentId?: string;
  loading: boolean;
  loadingOlder: boolean;
  hasMore: boolean;
  error?: string;
  onRetry?(): void;
  retryLabel?: string;
  activeTurnId?: string;
  cancellingTurn: boolean;
  cancelError?: string;
  onLoadOlder?: () => void;
  onCancelActive?: () => void;
  onAgentSelect(agentId: string): void;
  onOpenArtifact(artifactId: string): void;
  onOpenAgentDetail(agentId: string): void;
  onContinueResearch?(turn: CadenceTurn, agentId: string): void;
}) {
  return (
    <section
      className="request-timeline"
      data-testid="request-timeline"
      aria-label="项目对话工作区"
      aria-busy={loading || loadingOlder}
    >
      {hasMore && (
        <div className="timeline-older">
          <button
            data-testid="timeline-load-older"
            type="button"
            disabled={loadingOlder || !onLoadOlder}
            aria-busy={loadingOlder}
            onClick={() => onLoadOlder?.()}
          >
            {loadingOlder && (
              <LoaderCircle
                className="logout-spinner"
                size={14}
                aria-hidden="true"
              />
            )}
            <span>{loadingOlder ? "正在加载…" : "加载更早请求"}</span>
          </button>
        </div>
      )}
      {error && turns.length > 0 && (
        <div className="timeline-error timeline-error-retry" role="alert">
          <span>{error}</span>
          {onRetry && (
            <button
              className="outline-button timeline-retry"
              data-testid="timeline-retry"
              type="button"
              onClick={onRetry}
            >
              {retryLabel}
            </button>
          )}
        </div>
      )}
      {loading && turns.length === 0 ? (
        <div className="timeline-empty" role="status">
          正在加载请求历史…
        </div>
      ) : error && turns.length === 0 ? (
        <div className="timeline-empty timeline-empty-error" role="alert">
          <strong>请求历史暂时无法读取</strong>
          <span>{error}</span>
          {onRetry && (
            <button
              className="outline-button timeline-retry"
              data-testid="timeline-retry"
              type="button"
              onClick={onRetry}
            >
              {retryLabel}
            </button>
          )}
        </div>
      ) : turns.length === 0 ? (
        <div className="timeline-empty">
          还没有请求。使用下方 Composer 发起当前项目的第一项研究任务。
        </div>
      ) : (
        <div className="timeline-list">
          {turns.map((turn) => (
            <RequestTimelineItem
              key={turn.id}
              projectId={projectId}
              turn={turn}
              agents={agents}
              events={events}
              artifacts={artifacts}
              childThreadIds={childThreadIds}
              selectedAgentId={selectedAgentId}
              active={turn.id === activeTurnId}
              cancellingTurn={cancellingTurn}
              cancelError={turn.id === activeTurnId ? cancelError : undefined}
              onCancelActive={
                turn.id === activeTurnId ? onCancelActive : undefined
              }
              onAgentSelect={onAgentSelect}
              onOpenArtifact={onOpenArtifact}
              onOpenAgentDetail={onOpenAgentDetail}
              onContinueResearch={onContinueResearch}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function RequestTimelineItem({
  projectId,
  turn,
  agents,
  events,
  artifacts,
  childThreadIds,
  selectedAgentId,
  active,
  cancellingTurn,
  cancelError,
  onCancelActive,
  onAgentSelect,
  onOpenArtifact,
  onOpenAgentDetail,
  onContinueResearch,
}: {
  projectId: string;
  turn: CadenceTurn;
  agents: CadenceAgent[];
  events: CadenceAgentEvent[];
  artifacts: CadenceArtifact[];
  childThreadIds: ReadonlySet<string>;
  selectedAgentId?: string;
  active: boolean;
  cancellingTurn: boolean;
  cancelError?: string;
  onCancelActive?: () => void;
  onAgentSelect(agentId: string): void;
  onOpenArtifact(artifactId: string): void;
  onOpenAgentDetail(agentId: string): void;
  onContinueResearch?(turn: CadenceTurn, agentId: string): void;
}) {
  const activeStatus =
    !turn.submissionUncertain &&
    (turn.status === "pending" || turn.status === "running");
  const routedAgentIds = new Set(turn.routedAgentIds ?? []);
  const activityIds = new Set(turn.activityIds ?? []);
  const artifactIds = new Set(turn.artifactIds ?? []);
  const scopedEvents = events.filter((event) =>
    timelineActivityIncludesEvent(activityIds, event),
  );
  const scopedArtifacts = artifacts.filter((artifact) =>
    artifactIds.has(artifact.id),
  );
  const publicAgentReplies = timelinePublicAgentReplies(
    scopedEvents,
    agents,
    childThreadIds,
  );
  const completedReportProposal =
    turn.status === "completed"
      ? reportTextReplacementProposalFromUnknown(turn.output, {
          selection: reportSelectionFromUnknown(turn.artifactContext?.selection),
        })
      : undefined;
  const resultDetail = completedReportProposal
    ? "已生成报告文本替换提案。该提案尚未写入原文档；请在打开的 Artifact 中检查差异并点击“确认应用到 Artifact”。"
    : sanitizeUserFacingResearchText(
        turn.output.trim() || turn.resultSummary?.trim() || "",
      );
  const resultSections = resultDetail
    ? timelineResultSummarySections(resultDetail, turn.routedAgentIds ?? [])
    : [];
  const requestStatusMessage = turn.submissionUncertain
    ? "正在核对此次提交；请勿再次提交。"
    : turn.status === "interrupted_recoverable"
      ? `这次请求${turn.attachments.length ? "和附件" : ""}已保存。连接中断后，Cadence 尚未获得可显示的分析活动或结果；当前状态待核实，不会重复提交。`
      : undefined;
  const responseFallback = turn.status === "result_unknown"
    ? resultUnknownTurnFallback(turn)
    : turn.error
    ? "未形成综合回复。"
    : activeStatus
      ? "请求已提交，正在等待 Agent 返回公开结果。"
      : "请求已完成，但服务端未返回综合回复。请查看已记录的 Agent 公开回复、Artifact 或执行记录。";
  return (
    <>
      <OpenScienceConversationTurn
        active={active}
        agents={agents}
        artifacts={scopedArtifacts}
        cancelError={cancelError}
        cancellingTurn={cancellingTurn}
        events={externalEvidenceActivityEvents(scopedEvents)}
        onCancelActive={
          active && onCancelActive && timelineTurnCanCancel(turn)
            ? onCancelActive
            : undefined
        }
        onContinueResearch={onContinueResearch}
        onOpenArtifact={onOpenArtifact}
        projectId={projectId}
        publicReplies={publicAgentReplies as OpenSciencePublicAgentReply[]}
        requestStatusMessage={requestStatusMessage}
        responseFallback={responseFallback}
        resultSections={resultSections}
        selectedAgentId={selectedAgentId}
        turn={turn}
      />
    </>
  );
}

function resultUnknownTurnFallback(turn: CadenceTurn): string {
  if (turn.kind === "ordinary" || turn.kind === "ordinary_message") {
    return "此前的运行在服务重启前中断，系统无法验证其结果。Cadence 不会自动继续或重新执行此请求。你可以提交一个新的请求；已保存的 Artifact 仍可查看。";
  }
  return "该请求的运行结果目前无法由服务器权威记录证明。Cadence 已抑制自动恢复和重投；如后续收到精确匹配的实时或完成记录，会自动更新。";
}

const internalArtifactReferencePattern =
  /artifact:\/\/[A-Za-z0-9._~:@%+\-=\/]+|(?<![\w])(?:agents|outputs|artifacts)\/[A-Za-z0-9._~:@%+\-=\/]+/giu;
const internalArtifactMarkdownLinkPattern =
  /\[([^\]]+)\]\((?:artifact:\/\/|(?:agents|outputs|artifacts)\/)[^)]+\)/giu;

function sanitizeInternalArtifactReferences(value: string): string {
  if (!value) return "";
  const lines = value.replace(/\r/g, "").split("\n");
  const sanitized: string[] = [];
  for (const line of lines) {
    const linked = line.replace(internalArtifactMarkdownLinkPattern, "$1");
    const hadReference = internalArtifactReferencePattern.test(linked);
    internalArtifactReferencePattern.lastIndex = 0;
    const cleaned = linked
      .replace(internalArtifactReferencePattern, "")
      .replace(/[ \t]{2,}/g, " ")
      .trimEnd();
    internalArtifactReferencePattern.lastIndex = 0;
    const meaningful = cleaned
      .replace(/[`*_~>#()[\]{}:：.,，。；、\s-]/g, "")
      .trim();
    if (hadReference && (!meaningful || /[:：]\s*$/u.test(cleaned.trim()))) {
      if (sanitized.at(-1)?.trim().endsWith(":") ||
          sanitized.at(-1)?.trim().endsWith("：")) {
        sanitized.pop();
      }
      continue;
    }
    if (cleaned.trim()) {
      sanitized.push(cleaned);
    } else if (!hadReference) {
      // Preserve Markdown block boundaries while dropping path-only lines.
      sanitized.push("");
    }
  }
  return sanitized.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

const internalSearchLabelLinePattern =
  /^\s*(?:证据状态|evidence[_ ]?(?:id|status)|citation[_ ]?key|execution(?:[_ ]?(?:id|receipt))?|provider(?:[_ ]?(?:status|call))?|(?:credits?|tokens?|rounds?)\b|本次\s*Provider\s*用量|检索轮次|查询字符串|检索路由|工具调用|工具名称|调用参数)\s*[:：|]/iu;

/**
 * The Runtime needs machine identifiers to fetch and audit evidence, but a
 * normal research brief needs readable citations and conclusions. Keep this
 * last-resort client guard narrow: the server prompt is the primary contract,
 * while this projection prevents a misbehaving Agent from leaking receipt
 * labels or IDs into the ordinary conversation surface.
 */
function sanitizeUserFacingResearchText(value: string): string {
  const normalized = sanitizeInternalArtifactReferences(value)
    .replace(/\bev-[0-9a-f]{32}\b/giu, "")
    .replace(/\b(?:exa|citation)[-_][A-Za-z0-9._-]{6,}\b/giu, "")
    .replace(
      /\b(?:mcp__pingmedical__)?(?:search_external_evidence|query_science_source)\b/giu,
      "",
    )
    .replace(
      /\b(?:candidate-sources|fetched-candidate|provider-unavailable|source-fetch-unavailable|evidence-not-found|insufficient-or-conflicting)\b/giu,
      "",
    )
    .replace(/受管外部检索(?:候选证据)?/gu, "")
    .replace(/候选来源回执/gu, "");
  return normalized
    .split("\n")
    .filter((line) => !internalSearchLabelLinePattern.test(line))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

type TimelineAgentReply = {
  id: string;
  agentId: string;
  agentName: string;
  agentRole?: string;
  text: string;
  seq: number;
  tsMs?: number;
  sourceLabel: string;
};

const publicAgentReplyEventTypes = new Set([
  "agent_message",
  "agent_message_delta",
  "agent_message_content_delta",
]);

function timelinePublicAgentReplies(
  events: CadenceAgentEvent[],
  agents: CadenceAgent[],
  childThreadIds: ReadonlySet<string>,
): TimelineAgentReply[] {
  const sortedEvents = events
    // A VCode child thread has its own PASEO-compatible read-only timeline.
    // Do not flatten it beside the Planner/Lead response merely because it
    // shares the root ProjectTurn activity ID; that would hide the actual
    // delegation boundary and make several agents appear to be one reply.
    .filter(
      (event) =>
        !childThreadIds.has(event.threadId) &&
        publicAgentReplyEventTypes.has(event.event.type),
    )
    .sort(compareOrganizationAgentEvents);
  const fullReplySubmissionKeys = new Set<string>();
  const replies: TimelineAgentReply[] = [];
  const deltaGroups = new Map<
    string,
    {
      agentId: string;
      submissionId: string;
      text: string;
      type: "agent_message_delta" | "agent_message_content_delta";
      firstEvent: CadenceAgentEvent;
    }
  >();

  for (const event of sortedEvents) {
    const type = event.event.type;
    const agent = agents.find((candidate) => candidate.id === event.agentId);
    const agentName = agent?.name ?? `Agent ${event.agentId}`;
    const submissionKey = `${event.agentId}:${event.submissionId}`;
    const sourceLabel = event.source
      ? `${event.source} #${event.sourceIndex ?? event.seq}`
      : `seq ${event.seq}`;
    if (type === "agent_message") {
      const text = sanitizeUserFacingResearchText(
        eventText(
          eventRecordValue(event, "message") ??
            eventRecordValue(event, "summary"),
        ),
      );
      if (!text) continue;
      fullReplySubmissionKeys.add(submissionKey);
      replies.push({
        id: `full-${event.key}`,
        agentId: event.agentId,
        agentName,
        agentRole: agent?.role,
        text,
        seq: event.seq,
        tsMs: event.tsMs,
        sourceLabel,
      });
      continue;
    }
    // Keep delta whitespace until the full reply is assembled; trimming each
    // chunk can join words split across streaming events.
    const delta = eventDelta(eventRecordValue(event, "delta"));
    if (!delta) continue;
    const deltaType =
      type === "agent_message_content_delta"
        ? "agent_message_content_delta"
        : "agent_message_delta";
    const current = deltaGroups.get(submissionKey);
    if (current) {
      if (
        current.type === "agent_message_delta" &&
        deltaType === "agent_message_content_delta"
      ) {
        current.type = deltaType;
        current.text = delta;
        current.firstEvent = event;
      } else if (current.type === deltaType) {
        current.text += delta;
      }
    } else {
      deltaGroups.set(submissionKey, {
        agentId: event.agentId,
        submissionId: event.submissionId,
        text: delta,
        type: deltaType,
        firstEvent: event,
      });
    }
  }

  for (const group of deltaGroups.values()) {
    if (fullReplySubmissionKeys.has(`${group.agentId}:${group.submissionId}`)) {
      continue;
    }
    const agent = agents.find((candidate) => candidate.id === group.agentId);
    const event = group.firstEvent;
    const text = sanitizeUserFacingResearchText(group.text);
    if (!text) continue;
    replies.push({
      id: `delta-${event.key}`,
      agentId: group.agentId,
      agentName: agent?.name ?? group.agentId,
      agentRole: agent?.role,
      text,
      seq: event.seq,
      tsMs: event.tsMs,
      sourceLabel: event.source
        ? `${event.source} #${event.sourceIndex ?? event.seq}`
        : `seq ${event.seq}`,
    });
  }

  return replies.sort((left, right) => left.seq - right.seq);
}

function timelineResultSummarySections(
  content: string,
  routedAgentIds: string[],
): string[] {
  const agentIds = [...new Set(routedAgentIds.filter(Boolean))].sort(
    (left, right) => right.length - left.length,
  );
  if (!agentIds.length) return [content];

  const prefix = new RegExp(
    `^(${agentIds.map(escapeRegExp).join("|")}):[ \\t]*`,
    "gm",
  );
  const matches = [...content.matchAll(prefix)];
  if (!matches.length) return [content];

  // Keep older summaries readable without presenting their text prefix as provenance.
  const sections: string[] = [];
  const leading = content.slice(0, matches[0].index).trim();
  if (leading) sections.push(leading);
  matches.forEach((match, index) => {
    const start = (match.index ?? 0) + match[0].length;
    const end = matches[index + 1]?.index ?? content.length;
    const sectionContent = content.slice(start, end).trim();
    if (sectionContent) sections.push(sectionContent);
  });
  return sections.length ? sections : [content];
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function LiveActivityView({
  turns,
  evidence,
  warnings,
  residues,
  residuePositions,
  onToggleResidue,
  activeTurnId,
  onCancelTurn,
  cancellingTurn,
  cancelError,
}: {
  turns: CadenceTurn[];
  evidence: CadenceEvidence[];
  warnings: string[];
  residues: CadenceResidue[];
  residuePositions: number[];
  onToggleResidue(position: number): void;
  activeTurnId?: string;
  onCancelTurn?: () => void;
  cancellingTurn: boolean;
  cancelError?: string;
}) {
  const activeTurn =
    turns.find((turn) => turn.id === activeTurnId) ??
    turns.find(
      (turn) =>
        !turn.submissionUncertain &&
        (turn.status === "running" || turn.status === "pending"),
    );
  return (
    <div className="live-state-grid" data-testid="live-activity">
      <section className="surface-panel live-state-panel">
        <div className="panel-kicker">
          <span className="number-badge">
            <Activity size={14} />
          </span>
          <h2>运行活动</h2>
          <span className="panel-source">{turns.length} turns</span>
        </div>
        {activeTurn && (
          <div className="active-turn-card">
            <div>
              <strong>
                {activeTurn.status === "running"
                  ? "当前请求正在运行"
                  : "请求正在排队"}
              </strong>
              <p>{activeTurn.input || "附件请求"}</p>
            </div>
            {onCancelTurn && (
              <button
                className="outline-button"
                type="button"
                onClick={onCancelTurn}
                disabled={cancellingTurn}
              >
                {cancellingTurn ? "取消中…" : "取消当前运行"}
              </button>
            )}
          </div>
        )}
        {cancelError && (
          <p className="live-state-error" role="alert">
            {cancelError}
          </p>
        )}
        {turns.length ? (
          <div className="turn-list">
            {turns
              .slice(-8)
              .reverse()
              .map((turn) => (
                <article className="turn-row" key={turn.id}>
                  <span className={`turn-status turn-status-${turn.status}`}>
                    {turnStatusLabel(turn.status)}
                  </span>
                  <div>
                    <strong>{turn.input || "附件请求"}</strong>
                    {turn.output && <p>{turn.output}</p>}
                    {turn.error && (
                      <p className="live-state-error">{turn.error}</p>
                    )}
                  </div>
                  {turn.createdAt && <small>{String(turn.createdAt)}</small>}
                </article>
              ))}
          </div>
        ) : (
          <LiveEmptyState text="暂无运行活动。" />
        )}
      </section>
      <section className="surface-panel live-state-panel">
        <div className="panel-kicker">
          <span className="number-badge">
            <GitBranch size={14} />
          </span>
          <h2>当前摘要</h2>
          <span className="panel-source">仅显示后端字段</span>
        </div>
        <div className="live-summary-list">
          <span>
            <strong>{evidence.length}</strong> 条证据
          </span>
          <span>
            <strong>{warnings.length}</strong> 条警告
          </span>
          <span>
            <strong>
              {turns.filter((turn) => turn.status === "completed").length}
            </strong>{" "}
            个已完成 turn
          </span>
        </div>
        {warnings.length ? (
          <ul className="warning-list">
            {warnings.map((warning, index) => (
              <li key={`${warning}-${index}`}>{warning}</li>
            ))}
          </ul>
        ) : (
          <LiveEmptyState text="没有后端返回的警告。" compact />
        )}
      </section>
      {residues.length > 0 && (
        <section
          className="surface-panel residue-panel live-residue-context"
          data-testid="residue-selection"
        >
          <div className="panel-kicker">
            <span className="number-badge">
              <Dna size={14} />
            </span>
            <h2>结构选择</h2>
            <span className="panel-source">
              {residuePositions.length} selected
            </span>
          </div>
          <p className="panel-note">
            选择不会单独触发请求；提交研究请求时会作为本地上下文发送。
          </p>
          <div className="residue-strip">
            {residues.map((residue) => (
              <button
                data-testid={`protein-residue-${residue.position}`}
                className={`residue-chip ${residuePositions.includes(residue.position) ? "residue-selected" : ""}`}
                type="button"
                key={residue.position}
                onClick={() => onToggleResidue(residue.position)}
              >
                <b>{residue.aminoAcid}</b>
                <span>{residue.position}</span>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function LiveEvidenceView({
  evidence,
  artifactCount,
  artifactsLoading,
}: {
  evidence: CadenceEvidence[];
  artifactCount: number;
  artifactsLoading: boolean;
}) {
  const [filter, setFilter] = useState<"all" | CadenceEvidence["status"]>(
    "all",
  );
  const counts = {
    all: evidence.length,
    quarantined: evidence.filter((record) => record.status === "quarantined")
      .length,
    unverified: evidence.filter((record) => record.status === "unverified")
      .length,
    verified: evidence.filter((record) => record.status === "verified").length,
  };
  const riskOrder: Record<CadenceEvidence["status"], number> = {
    quarantined: 0,
    unverified: 1,
    verified: 2,
  };
  const orderedEvidence = evidence
    .slice()
    .sort((left, right) => riskOrder[left.status] - riskOrder[right.status]);
  const visibleEvidence =
    filter === "all"
      ? orderedEvidence
      : orderedEvidence.filter((record) => record.status === filter);
  return (
    <section
      className="surface-panel live-state-panel standalone"
      data-testid="live-evidence"
    >
      <div className="panel-kicker">
        <span className="number-badge">
          <GitBranch size={14} />
        </span>
        <h2>证据</h2>
        <span className="panel-source">{evidence.length} records</span>
      </div>
      {artifactsLoading ? (
        <LiveEmptyState
          text="正在加载项目产物与证据…"
          hint="加载完成后再判断当前项目是否已有可验证证据。"
        />
      ) : evidence.length ? (
        <>
          <div className="evidence-filter" data-testid="evidence-filter">
            {(["all", "quarantined", "unverified", "verified"] as const).map(
              (value) => (
                <button
                  key={value}
                  type="button"
                  className={`evidence-filter-button evidence-filter-${value}`}
                  aria-pressed={filter === value}
                  onClick={() => setFilter(value)}
                >
                  {value === "all" ? "全部" : evidenceStatusLabel(value)}{" "}
                  <span>{counts[value]}</span>
                </button>
              ),
            )}
          </div>
          <div className="evidence-record-list">
            {visibleEvidence.map((record) => (
              <article
                className={`evidence-record evidence-record-${record.status}`}
                key={record.id}
              >
                <div className="evidence-record-head">
                  <strong>{record.title}</strong>
                  <span className={`evidence-status evidence-${record.status}`}>
                    {evidenceStatusLabel(record.status)}
                  </span>
                </div>
                {record.summary && <p>{record.summary}</p>}
                <div className="evidence-record-meta">
                  <span>{evidenceOriginLabel(record.origin)}</span>
                  {record.source && <span>{record.source}</span>}
                  {record.confidence !== undefined && (
                    <span>置信度 {record.confidence}</span>
                  )}
                </div>
                {record.warnings.length > 0 && (
                  <ul className="warning-list">
                    {record.warnings.map((warning, index) => (
                      <li key={`${record.id}-warning-${index}`}>{warning}</li>
                    ))}
                  </ul>
                )}
              </article>
            ))}
          </div>
        </>
      ) : (
        <LiveEmptyState
          text={
            artifactCount
              ? `当前已有 ${artifactCount} 个研究产物，但尚未形成结构化、可验证的证据记录。`
              : "当前尚未形成结构化、可验证的证据记录。"
          }
          hint={
            artifactCount
              ? "产物不等同于已验证证据；请打开产物核对来源，或在 Composer 中请求专家生成证据记录。"
              : "请使用 Composer 发起研究请求或上传文件。"
          }
        />
      )}
    </section>
  );
}

function LivePermissionView({
  permissions,
  permissionError,
  onDecision,
  pendingRequestId,
}: {
  permissions: CadencePermissionRequest[];
  permissionError?: string;
  onDecision?: (
    request: CadencePermissionRequest,
    decision: "approve" | "deny",
  ) => void;
  pendingRequestId?: string;
}) {
  return (
    <section
      className="surface-panel live-state-panel standalone"
      data-testid="live-permissions"
    >
      <div className="panel-kicker">
        <span className="number-badge">
          <CircleDot size={14} />
        </span>
        <h2>权限审查</h2>
        <span className="panel-source">{permissions.length} requests</span>
      </div>
      {permissionError && (
        <p className="live-state-error" role="alert">
          {permissionError}
        </p>
      )}
      {permissions.length ? (
        <div className="permission-list">
          {permissions.map((request) => (
            <article className="permission-row" key={request.requestId}>
              <div>
                <strong>{request.target}</strong>
                <p>{request.reason}</p>
                {request.status && <small>{request.status}</small>}
              </div>
              {onDecision && (
                <div className="permission-actions">
                  <button
                    className="outline-button"
                    type="button"
                    onClick={() => onDecision(request, "deny")}
                    disabled={pendingRequestId === request.requestId}
                  >
                    拒绝
                  </button>
                  <button
                    className="new-project-button"
                    type="button"
                    onClick={() => onDecision(request, "approve")}
                    disabled={pendingRequestId === request.requestId}
                  >
                    {pendingRequestId === request.requestId
                      ? "保存中…"
                      : "批准"}
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      ) : (
        <LiveEmptyState text="暂无待审权限请求。" />
      )}
    </section>
  );
}

function LiveEmptyState({
  text,
  hint = "可在下方 Composer 中继续当前研究。",
  compact = false,
}: {
  text: string;
  hint?: string;
  compact?: boolean;
}) {
  return (
    <div className={`live-empty-content ${compact ? "compact" : ""}`}>
      <CircleDot size={20} />
      <strong>{text}</strong>
      <p>{hint}</p>
    </div>
  );
}

function turnStatusLabel(status: CadenceTurn["status"]) {
  return {
    pending: "排队中",
    running: "运行中",
    interrupted_recoverable: "提交状态待核实",
    result_unknown: "结果未知（已抑制自动恢复）",
    completed: "已完成",
    error: "异常",
    cancelled: "已取消",
  }[status];
}

function TimelineTurnStatus({
  status,
  submissionUncertain,
}: {
  status: CadenceTurn["status"];
  submissionUncertain?: boolean;
}) {
  if (submissionUncertain) {
    return (
      <span className="turn-status turn-status-pending">
        <CircleDot size={11} aria-hidden="true" />
        <span>待确认</span>
      </span>
    );
  }
  const Icon =
    status === "running"
      ? LoaderCircle
      : status === "completed"
        ? Check
        : status === "error" || status === "cancelled"
          ? X
          : CircleDot;
  return (
    <span className={`turn-status turn-status-${status}`}>
      <Icon
        className={status === "running" ? "logout-spinner" : undefined}
        size={11}
        aria-hidden="true"
      />
      <span>{turnStatusLabel(status)}</span>
    </span>
  );
}

function timelineAgentStatus(status: CadenceTurn["status"]): AgentStatus {
  const statusByTurn: Record<CadenceTurn["status"], AgentStatus> = {
    pending: "waiting",
    running: "working",
    interrupted_recoverable: "waiting",
    result_unknown: "unknown",
    completed: "complete",
    error: "error",
    cancelled: "paused",
  };
  return statusByTurn[status];
}

function timelineTimestampMs(value: string | number | undefined) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return undefined;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function timelineTurnCanCancel(turn: CadenceTurn) {
  return turn.kind === "ordinary" || turn.kind === "session_turn";
}

function formatTimelineTime(value: string | number) {
  const timestamp = timelineTimestampMs(value);
  if (timestamp === undefined) return String(value);
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(timestamp);
}

function formatTimelineDateTime(value: string | number) {
  const timestamp = timelineTimestampMs(value);
  if (timestamp === undefined) return String(value);
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(timestamp);
}

function timelineDateTime(value: string | number) {
  const timestamp = timelineTimestampMs(value);
  return timestamp === undefined ? undefined : new Date(timestamp).toISOString();
}

function timelineTurnProgressSignature(
  turn: CadenceTurn,
  events: CadenceAgentEvent[],
) {
  const activityIds = new Set(turn.activityIds ?? []);
  const scopedEvents = events
    .filter((event) => timelineActivityIncludesEvent(activityIds, event))
    .sort(compareOrganizationAgentEvents);
  const recentEventSignal = scopedEvents
    .slice(-3)
    .map(
      (event) =>
        `${event.submissionId}:${event.eventId ?? ""}:${event.source ?? "live"}:${event.sourceIndex ?? event.seq}:${event.event.type}:${boundedEventPayload(event.event)}`,
    )
    .join(",");
  return [
    turn.status,
    boundedTimelineText(turn.resultSummary, 256),
    boundedTimelineText(turn.error, 160),
    turn.activityIds?.join(",") ?? "",
    turn.artifactIds?.join(",") ?? "",
    turn.evidenceIds?.join(",") ?? "",
    scopedEvents.length,
    recentEventSignal,
  ].join("|");
}

function timelineActivityIncludesEvent(
  activityIds: Set<string>,
  event: CadenceAgentEvent,
) {
  return activityIds.has(event.submissionId);
}

function boundedTimelineText(value: string | undefined, limit: number) {
  return value?.slice(0, limit) ?? "";
}

function boundedEventPayload(value: Record<string, unknown>) {
  try {
    return JSON.stringify(value).slice(0, 256);
  } catch {
    return value.type;
  }
}

function evidenceStatusLabel(status: CadenceEvidence["status"]) {
  return {
    verified: "已验证",
    unverified: "未验证",
    quarantined: "已隔离",
  }[status];
}

function evidenceOriginLabel(origin: CadenceEvidence["origin"]) {
  return {
    local: "本地",
    network: "网络",
    demo: "Demo",
    unknown: "来源未声明",
  }[origin];
}

function ArtifactInquiryControls({
  artifact,
  revision,
  selection,
  detailState,
  detailError,
  turns,
  readOnly,
  composerDisabled,
  composerDisabledReason,
  onSubmit,
  onReportTextReplacementApply,
  onDetailRetry,
  onSelectionChange,
  onClearScope,
  onCollapse,
  onReturnToDraft,
}: {
  artifact: CadenceArtifact;
  revision?: number;
  selection?: ScientificSelection;
  detailState?: "idle" | "loading" | "ready" | "error";
  detailError?: string;
  turns: CadenceTurn[];
  readOnly: boolean;
  composerDisabled: boolean;
  composerDisabledReason?: string;
  onSubmit?(
    request: CadenceArtifactInquiryRequest,
  ): Promise<string | void> | string | void;
  onReportTextReplacementApply?(
    proposal: ReportTextReplacementProposal,
  ): Promise<{ content: string; contentSha256: string }>;
  onDetailRetry?(artifactId: string): void;
  onSelectionChange?(selection: ScientificSelection): void;
  onClearScope?(): void;
  onCollapse?(): void;
  onReturnToDraft?(artifactId: string): void;
}) {
  const scope = `${artifact.id}:${revision ?? "unversioned"}:${artifact.contentSha256 ?? "unhashed"}:${artifact.coordinateSha256 ?? "unhashed"}`;
  const [draft, setDraft] = useState("");
  const [draftScope, setDraftScope] = useState<string>();
  const [draftArtifactId, setDraftArtifactId] = useState<string>();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string>();
  const [applyingProposal, setApplyingProposal] = useState(false);
  const [appliedProposal, setAppliedProposal] = useState(false);
  const [submittedInquiry, setSubmittedInquiry] = useState<{
    requestId?: string;
    selection?: ScientificSelection;
  }>();
  const clearedRequestIdRef = useRef<string>();

  useEffect(() => {
    setError(undefined);
    clearedRequestIdRef.current = undefined;
  }, [
    artifact.contentSha256,
    artifact.coordinateSha256,
    artifact.id,
    revision,
  ]);
  useEffect(() => {
    setAppliedProposal(false);
  }, [artifact.id, revision]);

  const draftScopeMismatch = Boolean(draft.trim() && draftScope && draftScope !== scope);
  const reportSelection =
    selection?.renderer === "report" && selection.quote ? selection : undefined;
  const structuredSelection =
    selection?.renderer === "curve" || selection?.renderer === "flow";
  const structureSelection = selection?.renderer === "structure";
  const scientificUiSelection =
    selection?.renderer === "scientific-ui"
      ? scientificUiArtifactInquirySelection(selection, revision)
      : undefined;
  const invalidScientificUiSelection =
    selection?.renderer === "scientific-ui" && !scientificUiSelection;
  const proposalUnavailable =
    !reportSelection && (!structuredSelection || revision === undefined);
  const proposalBlockedReason = structureSelection
    ? "当前结构内容仅支持询问。"
    : reportSelection && !artifact.contentSha256
      ? "当前报告缺少服务器内容摘要，无法生成安全替换提案。"
      : proposalUnavailable
        ? "仅已版本化的 Curve/Flow 或报告选中文本支持受治理修改。"
        : undefined;
  const canRequestModificationPreview = !proposalBlockedReason;
  // Markdown/text changes do not need a client-side keyword classifier. The
  // user explicitly chooses whether to ask or request a non-mutating preview.
  // Curve/Flow keep their existing operation checks because the next step is a
  // concrete scientific operation rather than a text proposal.
  const canSubmitModificationPreview =
    canRequestModificationPreview &&
    (Boolean(reportSelection) ||
      isSupportedScientificModificationRequest(selection, draft));
  const modificationRequestUnsupportedReason =
    canRequestModificationPreview &&
    !reportSelection &&
    isExplicitScientificModificationAttempt(draft) &&
    !canSubmitModificationPreview
      ? unsupportedScientificModificationMessage(selection)
      : undefined;
  const modificationPreviewBlockedReason =
    proposalBlockedReason ?? modificationRequestUnsupportedReason;
  const proposalSubmitBlockedReason =
    modificationPreviewBlockedReason ??
    (draft.trim() && !canSubmitModificationPreview
      ? "请明确支持的修改：Curve 排除所选点，或调整所选 Flow gate。"
      : undefined);
  const primaryIntent: CadenceArtifactInquiryRequest["intent"] =
    structuredSelection && canSubmitModificationPreview
      ? "change_proposal"
      : "query";
  const secondaryIntent: CadenceArtifactInquiryRequest["intent"] =
    primaryIntent === "change_proposal" ? "query" : "change_proposal";
  const primaryActionLabel =
    primaryIntent === "change_proposal" ? "生成修改预览" : "询问";
  const secondaryActionLabel =
    secondaryIntent === "change_proposal" ? "生成修改预览" : "询问";
  const blockedReason =
    draftScopeMismatch
      ? "未发送草稿仍绑定原 Artifact；请返回原 Artifact 继续，或明确丢弃草稿。"
      : readOnly
      ? "演示模式仅供查看。切换到 Live 项目后可向产物 Owner 发送请求。"
      : composerDisabled
        ? (composerDisabledReason ?? "当前项目暂不能接收请求。")
        : detailState === "loading"
          ? "正在读取 Artifact 详情，暂不能安全发送。"
          : detailState === "error"
            ? (detailError ?? "Artifact 详情读取失败，暂不能安全发送。")
      : !ownerLabel(artifact.owner)
              ? "该 Artifact 未声明 producing Owner，系统不会猜测路由。"
              : reportSelection && !artifact.contentSha256
                ? "当前报告缺少服务器内容摘要，无法安全发送所选文本。"
                : structureSelection && !artifact.coordinateSha256
                  ? "当前结构缺少服务器坐标摘要，残基选择不能发送给 Agent。"
                : invalidScientificUiSelection
                  ? "当前图表选择不完整或已过期；请重新选择数据点后再询问。"
                : !onSubmit
                  ? "Artifact Owner 路由暂不可用。"
                  : undefined;
  // A submitted inquiry binds to the Owner requestId and immutable scope.
  // Current local selection is never used to discover or retarget a result.
  const submittedTurn = submittedInquiry?.requestId
    ? [...turns].reverse().find(
        (turn) =>
          turn.requestId === submittedInquiry.requestId &&
          artifactContextTargetsArtifact(
            turn.artifactContext,
            artifact,
            ownerLabel(artifact.owner),
          ) &&
          (appliedProposal ||
            !artifactContextHasStaleScope(
              turn.artifactContext,
              artifact,
              revision,
            )) &&
          artifactContextMatchesSelection(
            turn.artifactContext,
            submittedInquiry.selection,
          ),
      )
    : undefined;
  const matchingTurn = submittedTurn;
  const matchingTurnIsPrevious =
    Boolean(submittedInquiry?.selection) &&
    Boolean(selection) &&
    !sameScientificSelectionValue(selection, submittedInquiry?.selection);
  const selectionLabel = scientificSelectionSummary(selection);
  const matchingTurnIsChangeRequest =
    matchingTurn?.artifactContext?.intent === "propose_change";
  const matchingTurnHasUnresolvedDelivery = Boolean(
    matchingTurn &&
      (matchingTurn.submissionUncertain ||
        matchingTurn.status === "interrupted_recoverable" ||
        matchingTurn.status === "result_unknown"),
  );
  const matchingTurnRequestStatus = !matchingTurn
    ? undefined
    : matchingTurn.submissionUncertain
      ? "正在核对此次提交；请勿再次提交。"
      : matchingTurn.status === "interrupted_recoverable"
        ? "这次请求已保存。连接中断后，Cadence 尚未获得可显示的分析活动或结果；当前状态待核实，不会重复提交。"
        : matchingTurn.status === "result_unknown"
          ? resultUnknownTurnFallback(matchingTurn)
          : undefined;
  const reportProposal =
    matchingTurn?.status === "completed" &&
    matchingTurnIsChangeRequest &&
    (reportSelection ||
      submittedInquiry?.selection?.renderer === "report")
      ? reportTextReplacementProposalFromUnknown(matchingTurn.output, {
          selection:
            reportSelectionFromUnknown(matchingTurn.artifactContext?.selection) ??
            (submittedInquiry?.selection?.renderer === "report"
              ? submittedInquiry.selection
              : undefined),
        })
      : undefined;
  const reportProposalMatchesScope = Boolean(
    reportProposal &&
      reportProposal.artifact_id === artifact.id &&
      reportProposal.base_content_sha256 === artifact.contentSha256 &&
      reportProposal.selection.artifactId === artifact.id &&
      sameScientificSelectionValue(
        reportProposal.selection,
        submittedInquiry?.selection,
      ),
  );
  const canApplyReportProposal = Boolean(
    reportProposal &&
      reportProposalMatchesScope &&
      reportSelection &&
      onReportTextReplacementApply &&
      !appliedProposal,
  );
  useEffect(() => {
    if (
      !submittedInquiry?.requestId ||
      !submittedInquiry.selection ||
      !matchingTurn ||
      matchingTurn.requestId !== submittedInquiry.requestId ||
      !isTerminalArtifactInquiryStatus(matchingTurn.status) ||
      (matchingTurnIsChangeRequest &&
        matchingTurn.artifactContext?.selection &&
        (matchingTurn.artifactContext.selection as { renderer?: unknown })
          .renderer === "report") ||
      clearedRequestIdRef.current === submittedInquiry.requestId ||
      !sameScientificSelectionValue(selection, submittedInquiry.selection)
    ) {
      return;
    }
    clearedRequestIdRef.current = submittedInquiry.requestId;
    onSelectionChange?.(clearedScientificSelection(submittedInquiry.selection));
  }, [
    matchingTurn,
    matchingTurnIsChangeRequest,
    onSelectionChange,
    selection,
    submittedInquiry,
  ]);

  const submit = async (intent: CadenceArtifactInquiryRequest["intent"]) => {
    const input = draft.trim();
    if (
      !input ||
      blockedReason ||
      (intent === "change_proposal" &&
        proposalSubmitBlockedReason) ||
      sending ||
      !onSubmit
    ) {
      return;
    }
    const selectionSnapshot = snapshotScientificSelection(
      scientificUiSelection ?? selection,
    );
    setError(undefined);
    setSubmittedInquiry({ selection: selectionSnapshot });
    setSending(true);
    try {
      const requestId = await onSubmit({
        artifact,
        revision,
        selection: selectionSnapshot,
        intent,
        input,
      });
      setSubmittedInquiry(
        typeof requestId === "string"
          ? { requestId, selection: selectionSnapshot }
          : undefined,
      );
      setDraft("");
      setDraftScope(undefined);
      setDraftArtifactId(undefined);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Artifact 请求发送失败，请重试。",
      );
    } finally {
      setSending(false);
    }
  };

  const applyReportProposal = async () => {
    if (
      matchingTurn?.status !== "completed" ||
      !reportProposal ||
      !reportProposalMatchesScope ||
      !onReportTextReplacementApply
    ) {
      return;
    }
    setError(undefined);
    setAppliedProposal(true);
    setApplyingProposal(true);
    try {
      await onReportTextReplacementApply(reportProposal);
      onSelectionChange?.(clearedScientificSelection(reportProposal.selection));
    } catch (cause) {
      setAppliedProposal(false);
      setError(
        cause instanceof Error
          ? cause.message
          : "报告替换应用失败，原 Artifact 未改变。",
      );
    } finally {
      setApplyingProposal(false);
    }
  };

  return (
    <>
      <div className="artifact-inquiry-heading">
        {selection && (
          <span
            className="artifact-inquiry-selection"
            data-testid="artifact-inquiry-selection"
          >
            {selectionLabel}
          </span>
        )}
        {onCollapse && (
          <button
            className="artifact-inquiry-clear-scope"
            data-testid="artifact-inquiry-collapse"
            type="button"
            onClick={onCollapse}
          >
            <ChevronDown size={14} aria-hidden="true" />
            继续看图
          </button>
        )}
        {onClearScope && (
          <button
            className="artifact-inquiry-clear-scope"
            data-testid="artifact-inquiry-clear-scope"
            type="button"
            disabled={Boolean(draft.trim())}
            aria-label="返回项目范围"
            title={
              draft.trim()
                ? "请先发送或丢弃未发送草稿，再返回项目范围。"
                : "返回项目范围"
            }
            onClick={onClearScope}
          >
            <X size={14} aria-hidden="true" />
            返回项目范围
          </button>
        )}
      </div>
      <textarea
        data-testid="artifact-inquiry-input"
        value={draft}
        disabled={Boolean(blockedReason) || sending}
        onChange={(event) => {
          if (!event.target.value) {
            setDraft("");
            setDraftScope(undefined);
            setDraftArtifactId(undefined);
            return;
          }
          setDraft(event.target.value);
          if (!draftScope) {
            setDraftScope(scope);
            setDraftArtifactId(artifact.id);
          }
        }}
        onKeyDown={(event) => {
          if (
            event.key === "Enter" &&
            !event.shiftKey &&
            !event.nativeEvent.isComposing &&
            event.keyCode !== 229
          ) {
            event.preventDefault();
            void submit(primaryIntent);
          }
        }}
        rows={2}
        placeholder={
          blockedReason ??
          (selection
            ? "询问所选内容，或描述希望如何修改…"
            : "询问此 Artifact，或描述希望如何修改…")
        }
        aria-label="询问或编辑当前 Artifact"
      />
      <div className="artifact-inquiry-actions">
        <button
          className="artifact-inquiry-ask artifact-inquiry-primary"
          data-testid="artifact-inquiry-submit"
          type="button"
          onClick={() => void submit(primaryIntent)}
          disabled={
            Boolean(blockedReason) ||
            sending ||
            !draft.trim()
          }
        >
          {sending ? "提交中…" : primaryActionLabel}
          <Send size={15} aria-hidden="true" />
        </button>
        <button
          className="artifact-inquiry-edit"
          data-testid="artifact-inquiry-edit"
          type="button"
          title={
            secondaryIntent === "change_proposal"
              ? (proposalSubmitBlockedReason ??
                `由 ${ownerLabel(artifact.owner)} 基于 Artifact v${revision} 生成修改预览`)
              : "作为询问发送给 Artifact Owner"
          }
          onClick={() => void submit(secondaryIntent)}
          disabled={
            Boolean(blockedReason) ||
            (secondaryIntent === "change_proposal" &&
              Boolean(proposalSubmitBlockedReason)) ||
            sending ||
            !draft.trim()
          }
        >
          {secondaryActionLabel}
        </button>
      </div>
      {modificationRequestUnsupportedReason ? (
        <p
          className="artifact-inquiry-modification-hint"
          data-testid="artifact-inquiry-modification-hint"
          role="note"
        >
          {modificationRequestUnsupportedReason}
        </p>
      ) : canRequestModificationPreview ? (
        <p
          className="artifact-inquiry-modification-hint"
          data-testid="artifact-inquiry-modification-hint"
        >
          {canSubmitModificationPreview
            ? `将请求 ${ownerLabel(artifact.owner)} 基于 Artifact v${revision} 生成预览，不会直接更新 Artifact。`
            : `描述修改目标，由 ${ownerLabel(artifact.owner)} 基于 Artifact v${revision} 生成预览；确认应用后才会更新 Artifact。`}
        </p>
      ) : null}
      {blockedReason && (
        <p
          className="artifact-inquiry-disabled-reason"
          data-testid="artifact-inquiry-disabled-reason"
          role="note"
        >
          {blockedReason}
        </p>
      )}
      {detailState === "error" && onDetailRetry && (
        <button
          className="artifact-inquiry-retry-detail"
          data-testid="artifact-inquiry-retry-detail"
          type="button"
          onClick={() => onDetailRetry(artifact.id)}
        >
          重试读取 Artifact
        </button>
      )}
      {draftScopeMismatch && (
        <div className="artifact-inquiry-draft-guard" data-testid="artifact-inquiry-draft-guard">
          {draftArtifactId === artifact.id ? (
            <button
              type="button"
              onClick={() => {
                setDraftScope(scope);
              }}
            >
              针对最新完整 Artifact 继续
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (draftArtifactId) onReturnToDraft?.(draftArtifactId);
              }}
              disabled={!draftArtifactId || !onReturnToDraft}
            >
              返回原 Artifact 继续
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setDraft("");
              setDraftScope(undefined);
              setDraftArtifactId(undefined);
            }}
          >
            丢弃草稿
          </button>
        </div>
      )}
      {error && (
        <p
          className="artifact-inquiry-error"
          data-testid="artifact-inquiry-error"
          role="alert"
        >
          {error}
        </p>
      )}
      {matchingTurn && (
        <div
          className="artifact-inquiry-result"
          data-testid="artifact-inquiry-result"
          role="region"
          aria-label={
            matchingTurnHasUnresolvedDelivery
              ? "Artifact 请求状态"
              : "Artifact 回复"
          }
        >
          <div className="artifact-inquiry-result-heading">
            <strong data-testid="artifact-inquiry-result-label">
              {matchingTurnHasUnresolvedDelivery
                ? "请求状态"
                : matchingTurnIsPrevious
                ? "上一项处理"
                : matchingTurnIsChangeRequest
                  ? "修改建议"
                  : "回答"}
            </strong>
            <span data-testid="artifact-inquiry-result-status">
              {matchingTurn.optimistic ? "当前请求（本地关联）" : "当前请求"} ·{" "}
              {turnStatusLabel(matchingTurn.status)}
            </span>
          </div>
          <section className="artifact-inquiry-result-section">
            <h3>提交的问题</h3>
            <p data-testid="artifact-inquiry-result-question">
              {matchingTurn.input}
            </p>
          </section>
          {matchingTurnHasUnresolvedDelivery ? (
            <section
              className="artifact-inquiry-result-section artifact-inquiry-request-status"
              data-testid="artifact-inquiry-request-status"
              role="status"
            >
              <h3>当前状态</h3>
              <p>{matchingTurnRequestStatus}</p>
            </section>
          ) : (
            <section className="artifact-inquiry-result-section">
              <h3>{matchingTurnIsChangeRequest ? "修改建议" : "回答"}</h3>
              <p data-testid="artifact-inquiry-result-answer">
                {reportProposal
                  ? appliedProposal
                    ? "该报告文本替换已应用。"
                    : "已生成报告文本替换提案；请检查下方差异后确认应用。"
                  : matchingTurn.output.trim() ||
                    matchingTurn.error ||
                    "正在处理该请求。"}
              </p>
            </section>
          )}
          {matchingTurnIsChangeRequest && !matchingTurnHasUnresolvedDelivery && (
            <p className="artifact-inquiry-result-boundary">
              {reportProposal
                ? appliedProposal
                  ? "已应用到 Artifact，并刷新了当前文本。"
                  : reportProposalMatchesScope
                    ? "这是基于当前文本摘要的替换提案；预览不会修改 Artifact。"
                    : "替换提案与当前 Artifact 摘要或选区不一致，已禁止应用。"
                : typeof matchingTurn.artifactContext?.artifact_revision === "number"
                  ? "修改建议仅供审阅，不会直接修改 Artifact。"
                  : "该建议未修改 Artifact。"}
            </p>
          )}
          {reportProposal && (reportSelection || appliedProposal) && (
            <div
              className="artifact-inquiry-replacement"
              data-testid="artifact-inquiry-replacement"
            >
              <div className="artifact-inquiry-replacement-diff">
                <del>{reportProposal.selection.quote}</del>
                <ins>{reportProposal.replacement}</ins>
              </div>
              {canApplyReportProposal && (
                <button
                  type="button"
                  data-testid="artifact-inquiry-apply-replacement"
                  onClick={() => void applyReportProposal()}
                  disabled={applyingProposal}
                >
                  {applyingProposal ? "应用中…" : "确认应用到 Artifact"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

function scientificSelectionSummary(selection?: ScientificSelection): string {
  if (!selection) return "";
  if (selection.renderer === "report") return "已选 1 段文字";
  if (selection.renderer === "structure") {
    return `已选 ${selection.residues.length} 个残基`;
  }
  if (selection.renderer === "flow" && selection.gateId) {
    return `已选 gate · ${selection.gateId}`;
  }
  if (selection.renderer === "scientific-ui") {
    return `已选 ${selection.fidelity?.rowIds?.length ?? 0} 个数据点`;
  }
  return `已选 ${selection.ids.length} ${
    selection.renderer === "curve" ? "个点" : "个对象"
  }`;
}

function isExplicitScientificModificationAttempt(input: string): boolean {
  return /改成|改为|修改为|设置为|设为|删除|移除|排除|调整|替换|更新|重新拟合|重拟合|重新计算|重算|\b(?:remove|exclude|adjust|replace|update|set|refit|recalculate|recompute)\b/iu.test(
    input,
  );
}

function isSupportedScientificModificationRequest(
  selection: ScientificSelection | undefined,
  input: string,
): boolean {
  if (selection?.renderer === "curve") {
    return (
      /排除|\bexclude\b/iu.test(input) &&
      !/不\s*排除|不要\s*排除|勿\s*排除|别\s*排除|\b(?:do not|don't|without)\s+exclude\b/iu.test(
        input,
      )
    );
  }
  if (selection?.renderer === "flow") {
    return /调整.*(?:gate|门|多边形|polygon|边界|vertices)|(?:gate|门|多边形|polygon|边界|vertices).*调整|\badjust(?:ment)?\b.*\bgate\b|\bgate\b.*\badjust(?:ment)?\b/iu.test(
      input,
    );
  }
  return false;
}

function unsupportedScientificModificationMessage(
  selection: ScientificSelection | undefined,
): string {
  if (selection?.renderer === "curve") {
    return "当前 Curve 仅支持“排除所选点并重新拟合”；所述修改没有已验证的受治理操作，不会伪造 Artifact 更新。";
  }
  return "当前 Flow 仅支持调整所选 gate 并重新计算相关统计；所述修改没有已验证的受治理操作，不会伪造 Artifact 更新。";
}

function reportTextReplacementProposalFromUnknown(
  value: unknown,
  fallback?: {
    selection?: TextScientificSelection;
  },
  depth = 0,
): ReportTextReplacementProposal | undefined {
  if (depth > 10 || value === null || value === undefined) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    const agentAttributedJson = /^[A-Za-z0-9][A-Za-z0-9_.-]*:\s*([\[{][\s\S]*[\]}])$/u.exec(
      trimmed,
    )?.[1];
    const serializedProposal =
      trimmed.startsWith("{") || trimmed.startsWith("[")
        ? trimmed
        : agentAttributedJson;
    if (!serializedProposal) return undefined;
    try {
      return reportTextReplacementProposalFromUnknown(
        JSON.parse(serializedProposal),
        fallback,
        depth + 1,
      );
    } catch {
      return undefined;
    }
  }
  if (typeof value !== "object") return undefined;
  if (Array.isArray(value)) {
    for (const item of value) {
      const proposal = reportTextReplacementProposalFromUnknown(
        item,
        fallback,
        depth + 1,
      );
      if (proposal) return proposal;
    }
    return undefined;
  }
  const record = value as Record<string, unknown>;
  if (
    record.version === 1 &&
    record.operation === "report.replace_selection" &&
    typeof record.artifact_id === "string" &&
    typeof record.base_content_sha256 === "string" &&
    typeof record.replacement === "string" &&
    record.selection &&
    typeof record.selection === "object" &&
    !Array.isArray(record.selection)
  ) {
    const selection = record.selection as Record<string, unknown>;
    if (
      selection.renderer === "report" &&
      typeof selection.artifactId === "string" &&
      typeof selection.quote === "string" &&
      typeof selection.prefix === "string" &&
      typeof selection.suffix === "string"
    ) {
      return {
        version: 1,
        operation: "report.replace_selection",
        artifact_id: record.artifact_id,
        base_content_sha256: record.base_content_sha256,
        selection: {
          renderer: "report",
          artifactId: selection.artifactId,
          quote: selection.quote,
          prefix: selection.prefix,
          suffix: selection.suffix,
        },
        replacement: record.replacement,
      };
    }
  }
  if (
    record.operation === "report.replace_selection" &&
    record.status === "proposed" &&
    record.replacement_scope === "replace_quote_only" &&
    typeof record.artifact_id === "string" &&
    typeof record.base_content_sha256 === "string" &&
    typeof record.quote === "string" &&
    typeof record.replacement === "string" &&
    fallback?.selection &&
    record.quote === fallback.selection.quote
  ) {
    return {
      version: 1,
      operation: "report.replace_selection",
      artifact_id: record.artifact_id,
      base_content_sha256: record.base_content_sha256,
      selection: fallback.selection,
      replacement: record.replacement,
    };
  }
  for (const key of [
    "proposal",
    "change_proposal",
    "result",
    "output",
    "response",
    "data",
    "raw",
    "turn",
    "message",
    "content",
  ]) {
    const proposal = reportTextReplacementProposalFromUnknown(
      record[key],
      fallback,
      depth + 1,
    );
    if (proposal) return proposal;
  }
  return undefined;
}

function reportSelectionFromUnknown(
  value: unknown,
): TextScientificSelection | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  if (
    record.renderer !== "report" ||
    typeof record.artifactId !== "string" ||
    typeof record.quote !== "string" ||
    typeof record.prefix !== "string" ||
    typeof record.suffix !== "string"
  ) {
    return undefined;
  }
  return {
    renderer: "report",
    artifactId: record.artifactId,
    quote: record.quote,
    prefix: record.prefix,
    suffix: record.suffix,
  };
}

function snapshotScientificSelection(
  selection: ScientificSelection | undefined,
): ScientificSelection | undefined {
  return selection
    ? (JSON.parse(JSON.stringify(selection)) as ScientificSelection)
    : undefined;
}

function sameScientificSelectionValue(
  left: ScientificSelection | undefined,
  right: ScientificSelection | undefined,
) {
  return stableSelectionSignature(left) === stableSelectionSignature(right);
}

function clearedScientificSelection(
  selection: ScientificSelection,
): ScientificSelection {
  if (selection.renderer === "report") {
    return { ...selection, quote: "", prefix: "", suffix: "" };
  }
  if (selection.renderer === "structure") {
    return {
      ...selection,
      ids: [],
      residues: [],
      legacyResiduePositions: [],
    };
  }
  if (selection.renderer === "flow") {
    return {
      ...selection,
      ids: [],
      gateId: undefined,
      gate: undefined,
      geometry: undefined,
    };
  }
  if (selection.renderer === "scientific-ui") {
    return {
      ...selection,
      ids: [],
      fidelity: selection.fidelity
        ? { ...selection.fidelity, rowIds: [] }
        : undefined,
    };
  }
  return { ...selection, ids: [] };
}

function isTerminalArtifactInquiryStatus(status: CadenceTurnStatus) {
  return (
    status === "completed" || status === "error" || status === "cancelled"
  );
}

function artifactContextTargetsArtifact(
  context: Record<string, unknown> | undefined,
  artifact: CadenceArtifact,
  ownerId: string | undefined,
) {
  if (!context || context.artifact_id !== artifact.id) return false;
  return context.owner_agent_id === ownerId;
}

function artifactContextMatchesSelection(
  context: Record<string, unknown> | undefined,
  selection: ScientificSelection | undefined,
) {
  const persistedSelection = context?.selection;
  if (persistedSelection === undefined || persistedSelection === null) {
    return selection === undefined;
  }
  const currentSignature = stableSelectionSignature(selection);
  const persistedSignature = stableSelectionSignature(persistedSelection);
  return Boolean(
    currentSignature &&
      persistedSignature &&
      currentSignature === persistedSignature,
  );
}

function stableSelectionSignature(value: unknown): string | undefined {
  if (!value || typeof value !== "object") return undefined;
  return JSON.stringify(canonicalizeSelectionValue(value));
}

function canonicalizeSelectionValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => {
      const normalized = canonicalizeSelectionValue(item);
      return normalized === undefined ? null : normalized;
    });
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.keys(value as Record<string, unknown>)
        .sort()
        .flatMap((key) => {
          const normalized = canonicalizeSelectionValue(
            (value as Record<string, unknown>)[key],
          );
          return normalized === undefined ? [] : [[key, normalized]];
        }),
    );
  }
  return value === undefined ? undefined : value;
}

function artifactContextHasStaleScope(
  context: Record<string, unknown> | undefined,
  artifact: CadenceArtifact,
  currentRevision: number | undefined,
) {
  if (!context) return false;
  const submittedRevision =
    typeof context.artifact_revision === "number"
      ? context.artifact_revision
      : undefined;
  if (
    submittedRevision !== undefined &&
    currentRevision !== undefined &&
    submittedRevision !== currentRevision
  ) {
    return true;
  }
  const submittedDigest =
    typeof context.content_sha256 === "string"
      ? context.content_sha256
      : undefined;
  const submittedCoordinateDigest =
    typeof context.coordinate_sha256 === "string"
      ? context.coordinate_sha256
      : undefined;
  return (
    Boolean(
      submittedDigest &&
        artifact.contentSha256 &&
        submittedDigest !== artifact.contentSha256,
    ) ||
    Boolean(
      submittedCoordinateDigest &&
        artifact.coordinateSha256 &&
        submittedCoordinateDigest !== artifact.coordinateSha256,
    )
  );
}

function ArtifactPreview({
  artifact,
  artifacts,
  beside,
  artifactCount,
  openArtifactCount,
  onBrowseArtifacts,
  canOpenBeside,
  ownerAgent,
  onClose,
  onOpenBeside,
  maximized,
  onToggleMaximize,
  onArtifactDetailRequest,
  detailState,
  detailError,
  onArtifactDetailRetry,
  artifactDetailStates,
  artifactDetailErrors,
  scientificSelections,
  onStructureSelectionChange,
  onScientificSelectionChange,
  scientificOperation,
  onScientificApply,
}: {
  artifact: CadenceArtifact;
  artifacts: CadenceArtifact[];
  beside: boolean;
  artifactCount?: number;
  openArtifactCount?: number;
  onBrowseArtifacts?(): void;
  canOpenBeside: boolean;
  ownerAgent?: CadenceAgent;
  onClose(): void;
  onOpenBeside(): void;
  maximized?: boolean;
  onToggleMaximize?(): void;
  onArtifactDetailRequest?(artifactId: string): void;
  detailState?: "idle" | "loading" | "ready" | "error";
  detailError?: string;
  onArtifactDetailRetry?(artifactId: string): void;
  artifactDetailStates?: CadenceBioAppProps["artifactDetailStates"];
  artifactDetailErrors?: CadenceBioAppProps["artifactDetailErrors"];
  scientificSelections: Record<string, ScientificSelection>;
  onStructureSelectionChange(selection: StructureScientificSelection): void;
  onScientificSelectionChange(selection: ScientificSelection): void;
  scientificOperation?: ScientificOperationResult;
  onScientificApply?: (
    artifactId: string,
    baseRevision: number,
    document: ScientificArtifactDocument,
  ) => Promise<ScientificArtifactDocument>;
}) {
  const downloadUrl = safeArtifactUrl(artifact.downloadUrl);
  const canonicalRevision = artifactCanonicalRevision(artifact);
  const deliveryLabel = artifactDeliveryStateLabel(
    artifact.publishedPresentation?.deliveryState,
  );
  const [workbenchTab, setWorkbenchTab] = useState<
    "content" | "details" | "provenance"
  >("content");
  const detailsOpen = workbenchTab === "details";
  const provenanceOpen = workbenchTab === "provenance";
  const findArtifact = useCallback(
    (artifactRef: string) => {
      const normalizedRef = normalizeArtifactReference(artifactRef);
      return artifacts.find(
        (item) =>
          item.id === normalizedRef ||
          item.path === normalizedRef,
      );
    },
    [artifacts],
  );
  const resolveDataRows = useCallback(
    (artifactRef: string): ScientificUiRow[] | undefined => {
      const boundArtifact = findArtifact(artifactRef);
      return boundArtifact?.preview?.content
        ? scientificUiRowsFromViewArtifact(boundArtifact.preview.content)
        : undefined;
    },
    [findArtifact],
  );
  const requestArtifactDetail = useCallback(
    (artifactRef: string) => {
      const boundArtifact = findArtifact(artifactRef);
      if (boundArtifact) onArtifactDetailRequest?.(boundArtifact.id);
    },
    [findArtifact, onArtifactDetailRequest],
  );
  const renderArtifact = (artifactRef: string) => {
    const boundArtifact = findArtifact(artifactRef);
    if (!boundArtifact) {
      return (
        <p className="empty-preview">
          Artifact binding unavailable: {artifactRef}
        </p>
      );
    }
    return (
      <ArtifactPreviewBody
        artifact={boundArtifact}
        onArtifactDetailRequest={onArtifactDetailRequest}
        detailState={artifactDetailStates?.[boundArtifact.id]}
        detailError={artifactDetailErrors?.[boundArtifact.id]}
        onArtifactDetailRetry={onArtifactDetailRetry}
        scientificSelection={
          isImageArtifact(boundArtifact)
            ? undefined
            : scientificSelections[boundArtifact.id]
        }
        onStructureSelectionChange={onStructureSelectionChange}
        onScientificSelectionChange={onScientificSelectionChange}
        scientificOperation={scientificOperation}
        onScientificApply={onScientificApply}
        renderArtifact={renderArtifact}
        resolveDataRows={resolveDataRows}
        requestArtifactDetail={requestArtifactDetail}
      />
    );
  };
  return (
    <article
      className="artifact-preview"
      data-testid="artifact-preview"
      data-published-format={artifact.publishedPresentation?.format}
      data-source-revision={artifact.sourceRevision}
      data-descriptor-revision={artifact.descriptorRevision}
    >
      <div className="artifact-preview-head">
        <div className="artifact-preview-toolbar">
          <div className="artifact-preview-identity">
            <div className="artifact-preview-context">
              {onBrowseArtifacts && artifactCount !== undefined && (
                <button
                  className="artifact-browser-trigger"
                  data-testid="artifact-browser-toggle"
                  type="button"
                  aria-label={`浏览 ${artifactCount} 项项目 Artifact${openArtifactCount && openArtifactCount > 1 ? `，其中 ${openArtifactCount} 项已打开` : ""}`}
                  title="浏览项目 Artifact"
                  onClick={onBrowseArtifacts}
                >
                  <FileText size={14} aria-hidden="true" />
                  <span className="artifact-browser-trigger-label">全部产物</span>
                  <small>{artifactCount}</small>
                  <ChevronDown size={13} aria-hidden="true" />
                </button>
              )}
              <span
                className={`artifact-type artifact-${artifact.publishedPresentation?.format ?? artifact.type.toLowerCase()}`}
                data-published-format={artifact.publishedPresentation?.format}
              >
                {artifactPresentationLabel(artifact)}
              </span>
              {canonicalRevision !== undefined && (
                <span
                  className="artifact-version-chip"
                  data-testid="artifact-current-version"
                  title="当前由 Cadence Artifact 服务读取的版本"
                >
                  v{canonicalRevision}
                </span>
              )}
              {deliveryLabel && (
                <span
                  className={`artifact-delivery-state is-${artifact.publishedPresentation?.deliveryState}`}
                  data-testid="artifact-delivery-state"
                >
                  {deliveryLabel}
                </span>
              )}
            </div>
            <div className="artifact-preview-title">
              <div>
                <h3>{artifactDisplayName(artifact)}</h3>
                {artifact.description &&
                  artifact.description !== artifact.path &&
                  !/^Submission:\s*\S+$/u.test(artifact.description.trim()) && (
                  <p>{artifact.description}</p>
                )}
              </div>
            </div>
          </div>
          <div className="artifact-preview-actions">
            {downloadUrl ? (
              <a
                data-testid="artifact-download"
                className="icon-button"
                href={downloadUrl}
                download={artifact.name}
                title={`下载原文件 ${artifact.name}`}
                aria-label={`下载原文件 ${artifact.name}`}
              >
                <Download size={15} />
              </a>
            ) : (
              <button
                data-testid="artifact-download"
                className="icon-button"
                type="button"
                disabled
                title="当前 Artifact 暂无可下载原文件"
                aria-label="当前 Artifact 暂无可下载原文件"
              >
                <Download size={15} />
              </button>
            )}
            {(canOpenBeside || beside) && (
              <button
                data-testid="artifact-beside"
                className={`icon-button ${beside ? "is-active" : ""}`}
                type="button"
                onClick={onOpenBeside}
                title="在旁边打开已打开的产物"
                aria-label="在旁边打开已打开的产物"
                aria-pressed={beside}
              >
                <LayoutPanelLeft size={15} />
              </button>
            )}
            {onToggleMaximize && (
              <button
                data-testid="artifact-maximize"
                className={`icon-button ${maximized ? "is-active" : ""}`}
                type="button"
                onClick={onToggleMaximize}
                title={maximized ? "恢复双栏查看" : "最大化当前产物"}
                aria-label={maximized ? "恢复双栏查看" : "最大化当前产物"}
                aria-pressed={maximized}
              >
                {maximized ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
              </button>
            )}
            <button
              data-testid="artifact-close"
              className="icon-button"
              type="button"
              onClick={onClose}
              title="关闭产物"
              aria-label="关闭产物"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        {/* The workbench only exposes views backed by this Artifact's real
            content, metadata, or provenance. Do not turn absent Runtime data
            into decorative execution/review tabs just to imitate another UI. */}
        <div
          className="artifact-preview-tabs"
          role="tablist"
          aria-label={`${artifactDisplayName(artifact)} 工作区视图`}
        >
          <button
            type="button"
            role="tab"
            data-testid="artifact-content"
            aria-selected={workbenchTab === "content"}
            aria-controls={`artifact-content-${artifact.id}`}
            onClick={() => setWorkbenchTab("content")}
          >
            内容
          </button>
          <button
            type="button"
            role="tab"
            data-testid="artifact-details"
            aria-selected={detailsOpen}
            aria-controls={`artifact-details-${artifact.id}`}
            onClick={() => setWorkbenchTab("details")}
          >
            信息
          </button>
          {artifact.provenance?.length ? (
            <button
              type="button"
              role="tab"
              data-testid="artifact-provenance"
              aria-selected={provenanceOpen}
              aria-controls={`artifact-provenance-${artifact.id}`}
              onClick={() => setWorkbenchTab("provenance")}
            >
              溯源
            </button>
          ) : null}
        </div>
      </div>
      {detailsOpen ? (
        <div
          className="artifact-inspector-panel artifact-inspector-details"
          data-testid="artifact-details-panel"
          id={`artifact-details-${artifact.id}`}
          role="tabpanel"
        >
          <div className="artifact-meta-grid">
            <span data-testid="artifact-owner">
              <strong>负责人</strong>
              {ownerAgent?.name ?? ownerLabel(artifact.owner) ?? "未声明"}
              {!ownerLabel(artifact.owner) && (
                <small className="artifact-unknown-explanation">
                  后端未提供负责人字段，系统不会根据文件名猜测。
                </small>
              )}
            </span>
            {artifact.updatedAt && (
              <span>
                <strong>更新时间</strong>
                {artifact.updatedAt}
              </span>
            )}
            {artifact.size && (
              <span>
                <strong>大小</strong>
                {artifact.size}
              </span>
            )}
            <span>
              <strong>Artifact ID</strong>
              <code>{artifact.id}</code>
            </span>
            {artifact.executionId && (
              <span>
                <strong>Execution</strong>
                <code>{artifact.executionId}</code>
              </span>
            )}
            {artifact.contentSha256 && (
              <span>
                <strong>内容校验</strong>
                <code title={artifact.contentSha256}>
                  {artifact.contentSha256.slice(0, 16)}…
                </code>
              </span>
            )}
          </div>
          {artifact.versions?.length ? (
            <div className="artifact-lower-grid">
              <div className="artifact-section">
                <h4>版本</h4>
                {artifact.versions.map((version) => (
                  <div
                    className={`version-row ${
                      canonicalRevision !== undefined &&
                      (version.id === `v${canonicalRevision}` ||
                        version.label === `Revision ${canonicalRevision}`)
                        ? "is-current"
                        : ""
                    }`}
                    key={version.id}
                  >
                    <span>{version.label}</span>
                    <small>{version.createdAt}</small>
                  </div>
                ))}
                {artifact.versions.length > 1 && (
                  <p className="artifact-version-read-note" role="note">
                    当前 API 只允许读取已授权的当前内容；历史版本内容开放后，才会在这里启用切换与跨版本比较。
                  </p>
                )}
              </div>
            </div>
          ) : null}
        </div>
      ) : provenanceOpen && artifact.provenance?.length ? (
        <div
          className="artifact-inspector-panel artifact-provenance-panel"
          data-testid="artifact-provenance-panel"
          id={`artifact-provenance-${artifact.id}`}
          role="tabpanel"
        >
          <strong>来源</strong>
          <span>负责人：{ownerLabel(artifact.owner) ?? "未声明"}</span>
          {!ownerLabel(artifact.owner) && (
            <span className="artifact-unknown-explanation">
              后端未提供负责人字段，系统不会根据文件名猜测。
            </span>
          )}
          {artifact.executionId && <span>执行：{artifact.executionId}</span>}
          <span>
            来源链：
            {artifact.provenance.map((item) => item.source).join(" → ")}
          </span>
        </div>
      ) : null}
      {workbenchTab === "content" && (
        <div
          className="artifact-preview-body"
          id={`artifact-content-${artifact.id}`}
          role="tabpanel"
        >
          <ArtifactPreviewBody
            artifact={artifact}
            onArtifactDetailRequest={onArtifactDetailRequest}
            detailState={detailState}
            detailError={detailError}
            onArtifactDetailRetry={onArtifactDetailRetry}
            scientificSelection={
              isImageArtifact(artifact)
                ? undefined
                : scientificSelections[artifact.id]
            }
            onStructureSelectionChange={onStructureSelectionChange}
            onScientificSelectionChange={onScientificSelectionChange}
            scientificOperation={scientificOperation}
            onScientificApply={onScientificApply}
            renderArtifact={renderArtifact}
            resolveDataRows={resolveDataRows}
            requestArtifactDetail={requestArtifactDetail}
          />
        </div>
      )}
    </article>
  );
}

function normalizeArtifactReference(value: string): string {
  return value.trim().startsWith("artifact://")
    ? value.trim().slice("artifact://".length)
    : value.trim();
}

function reportContentForDisplay(artifact: CadenceArtifact, content: string) {
  if (!isAgentAnalysisReport(artifact)) return content;
  return content.replace(
    /^# Agent result\r?\n\r?\n(?:- Agent: `[^`\r\n]+`\r?\n)?- Submission: `[^`\r\n]+`\r?\n\r?\n/u,
    "",
  );
}

function ArtifactPreviewBody({
  artifact,
  onArtifactDetailRequest,
  detailState,
  detailError,
  onArtifactDetailRetry,
  scientificSelection,
  onStructureSelectionChange,
  onScientificSelectionChange,
  scientificOperation,
  onScientificApply,
  renderArtifact,
  resolveDataRows,
  requestArtifactDetail,
}: {
  artifact: CadenceArtifact;
  onArtifactDetailRequest?(artifactId: string): void;
  detailState?: "idle" | "loading" | "ready" | "error";
  detailError?: string;
  onArtifactDetailRetry?(artifactId: string): void;
  scientificSelection?: ScientificSelection;
  onStructureSelectionChange(selection: StructureScientificSelection): void;
  onScientificSelectionChange(selection: ScientificSelection): void;
  scientificOperation?: ScientificOperationResult;
  onScientificApply?: (
    artifactId: string,
    baseRevision: number,
    document: ScientificArtifactDocument,
  ) => Promise<ScientificArtifactDocument>;
  renderArtifact?(artifactRef: string): ReactNode;
  resolveDataRows?(artifactRef: string): ScientificUiRow[] | undefined;
  requestArtifactDetail?(artifactId: string): void;
}) {
  const imageArtifact = isImageArtifact(artifact);
  const coordinateStructureArtifact = isCoordinateStructureArtifact(artifact);
  const scientificInspectionDocument =
    artifact.scientificDocument ?? artifact.localScientificDocument;
  const detailRequestKey = [
    artifact.id,
    artifact.sourceRevision ?? "",
    artifact.descriptorRevision ?? "",
    artifact.updatedAt ?? "",
    artifact.contentSha256 ?? "",
    artifact.coordinateSha256 ?? "",
  ].join(":");
  const requestedDetailKeyRef = useRef<string>();
  useEffect(() => {
    if (
      artifact.rendererDocument ||
      artifact.scientificDocument ||
      artifact.localScientificDocument ||
      artifact.preview ||
      !onArtifactDetailRequest ||
      detailState === "loading" ||
      detailState === "error" ||
      requestedDetailKeyRef.current === detailRequestKey
    ) {
      return;
    }
    requestedDetailKeyRef.current = detailRequestKey;
    onArtifactDetailRequest(artifact.id);
  }, [
    artifact.id,
    artifact.preview,
    artifact.rendererDocument,
    artifact.scientificDocument,
    artifact.localScientificDocument,
    detailRequestKey,
    detailState,
    onArtifactDetailRequest,
  ]);

  if (detailState === "loading") {
    return (
      <div
        className="artifact-detail-state"
        data-testid="artifact-detail-loading"
        role="status"
        aria-busy="true"
      >
        <LoaderCircle size={17} aria-hidden="true" />
        正在读取 Artifact 详情…
      </div>
    );
  }
  if (detailState === "error") {
    return (
      <div
        className="artifact-detail-state artifact-detail-error"
        data-testid="artifact-detail-error"
        role="alert"
      >
        <strong>Artifact 详情读取失败</strong>
        <span>{detailError ?? "无法读取该 Artifact 的完整内容。"}</span>
        {onArtifactDetailRetry && (
          <button
            data-testid="artifact-detail-retry"
            type="button"
            onClick={() => onArtifactDetailRetry(artifact.id)}
          >
            重试读取
          </button>
        )}
      </div>
    );
  }

  if (artifact.publishedPresentation) {
    return (
      <PublishedArtifactPreview
        artifact={artifact}
        presentation={artifact.publishedPresentation}
        publishedContent={artifact.publishedContent}
        scientificSelection={scientificSelection}
        onScientificSelectionChange={onScientificSelectionChange}
      />
    );
  }
  if (isPdfArtifact(artifact)) {
    return <PdfArtifactPreview artifact={artifact} />;
  }
  if (imageArtifact) {
    return <ImageArtifactPreview artifact={artifact} />;
  }
  if (artifact.rendererDocument) {
    return (
      <ScientificUiKernel
        artifactId={artifact.id}
        document={artifact.rendererDocument}
        onSelectionChange={onScientificSelectionChange}
        renderArtifact={renderArtifact}
        resolveDataRows={resolveDataRows}
        requestArtifactDetail={requestArtifactDetail}
      />
    );
  }
  if (scientificInspectionDocument) {
    const localScientificContext = !artifact.scientificDocument;
    return (
      <ScientificArtifactStudio
        artifactId={artifact.id}
        document={scientificInspectionDocument}
        readOnly={localScientificContext}
        selection={scientificSelection}
        operation={localScientificContext ? undefined : scientificOperation}
        onSelectionChange={onScientificSelectionChange}
        onApply={localScientificContext ? undefined : onScientificApply}
      />
    );
  }
  const scientificViewRows = artifact.preview?.content
    ? scientificUiRowsFromViewArtifact(artifact.preview.content)
    : undefined;
  if (scientificViewRows) {
    return <ScientificViewTablePreview rows={scientificViewRows} />;
  }
  if (isParquetArtifact(artifact)) {
    return (
      <div
        className="artifact-table-fallback"
        data-testid="parquet-preview-fallback"
        role="status"
      >
        Parquet requires a prepared bounded table view. The browser will not
        decode the binary artifact directly.
      </div>
    );
  }
  if (artifact.preview?.kind === "table") {
    return <TablePreview content={artifact.preview.content} />;
  }
  if (
    artifact.preview?.kind === "markdown" ||
    artifact.preview?.kind === "text"
  ) {
    return (
      <DocumentReader
        artifactId={artifact.id}
        sourceContent={artifact.preview.content}
        content={reportContentForDisplay(artifact, artifact.preview.content)}
        selection={
          scientificSelection?.renderer === "report"
            ? scientificSelection
            : undefined
        }
        onSelectionChange={onScientificSelectionChange}
      />
    );
  }
  if (artifact.preview?.kind === "html") {
    return (
      <iframe
        className="artifact-html-preview"
        title={`${artifact.name} interactive viewer`}
        sandbox=""
        srcDoc={artifact.preview.content}
      />
    );
  }
  if (artifact.preview?.kind === "structure" && coordinateStructureArtifact) {
    const format =
      artifact.preview.format ?? structureFormatFromArtifact(artifact);
    return (
      <StructurePreview
        artifactId={artifact.id}
        artifactRef={
          artifact.path
            ? `artifact://${artifact.path}`
            : `artifact://${artifact.id}`
        }
        content={artifact.preview.content}
        format={format}
        coordinateSha256={artifact.coordinateSha256}
        sourceRevision={artifact.sourceRevision}
        descriptorRevision={artifact.descriptorRevision}
        selection={
          scientificSelection?.renderer === "structure"
            ? scientificSelection
            : undefined
        }
        onSelectionChange={onStructureSelectionChange}
      />
    );
  }
  return (
    <pre>
      {artifact.preview?.content ?? "No preview content returned by adapter."}
    </pre>
  );
}

function PublishedArtifactPreview({
  artifact,
  presentation,
  publishedContent,
  scientificSelection,
  onScientificSelectionChange,
}: {
  artifact: CadenceArtifact;
  presentation: CadencePublishedArtifactPresentation;
  publishedContent?: CadencePublishedArtifactContent;
  scientificSelection?: ScientificSelection;
  onScientificSelectionChange(selection: ScientificSelection): void;
}) {
  const content = artifact.preview?.content ?? "";
  switch (presentation.format) {
    case "document":
      return (
        <DocumentReader
          artifactId={artifact.id}
          sourceContent={content}
          content={content}
          selection={
            scientificSelection?.renderer === "report"
              ? scientificSelection
              : undefined
          }
          onSelectionChange={onScientificSelectionChange}
        />
      );
    case "table":
      return publishedContent?.format === "table" ? (
        <StructuredTablePreview content={publishedContent} />
      ) : (
        <TablePreview content={content} />
      );
    case "figure":
      return (
        <section className="published-figure-preview">
          <ImageArtifactPreview artifact={artifact} />
          {presentation.caption && <p>{presentation.caption}</p>}
          {(presentation.method || presentation.dataSource) && (
            <dl>
              {presentation.method && (
                <div>
                  <dt>Method</dt>
                  <dd>{presentation.method}</dd>
                </div>
              )}
              {presentation.dataSource && (
                <div>
                  <dt>Data source</dt>
                  <dd>{presentation.dataSource}</dd>
                </div>
              )}
            </dl>
          )}
        </section>
      );
    case "html":
      return publishedContent?.format === "html" ? (
        <section className="published-html-preview" data-testid="published-html-preview">
          <p className="published-html-note" role="note">
            静态 HTML 快照：不包含脚本、联网或筛选交互。需要交互式检查时，请使用 Artifact-backed Scientific UI。
          </p>
          <iframe
            className="artifact-html-preview"
            title={`${artifact.name} published HTML preview`}
            sandbox=""
            srcDoc={publishedContent.html}
          />
        </section>
      ) : (
        <p className="artifact-table-fallback" role="alert">
          已发布 HTML 内容不可用，无法安全展示。
        </p>
      );
    case "code":
      return (
        <CodeArtifactPreview
          content={content}
          structured={
            publishedContent?.format === "code" ? publishedContent : undefined
          }
        />
      );
    case "decision":
      return (
        <DecisionArtifactPreview
          presentation={presentation}
          content={
            publishedContent?.format === "decision"
              ? publishedContent
              : undefined
          }
        />
      );
  }
}

function CodeArtifactPreview({
  content,
  structured,
}: {
  content: string;
  structured?: CadencePublishedCodeContent;
}) {
  const [activePath, setActivePath] = useState(structured?.files[0]?.path);
  const activeFile = structured?.files.find((file) => file.path === activePath) ??
    structured?.files[0];
  if (structured && activeFile) {
    return (
      <section className="published-code-preview" data-testid="published-code-preview">
        <div className="published-code-files" role="tablist" aria-label="Published code files">
          {structured.files.map((file) => (
            <button
              key={file.path}
              type="button"
              role="tab"
              aria-selected={file.path === activeFile.path}
              onClick={() => setActivePath(file.path)}
            >
              {file.path}
            </button>
          ))}
        </div>
        <pre aria-label={`Read-only published code ${activeFile.path}`}>
          <code>{activeFile.text}</code>
        </pre>
        <p className="published-code-execution-note" role="note">
          该代码仅作为可复现交付发布；发布操作未执行其内容。请结合关联的 Execution 或输出 Artifact 判断运行结果。
        </p>
        <p><strong>How to run:</strong> {structured.howToRun}</p>
        {structured.outputs.length > 0 && (
          <p><strong>Expected outputs:</strong> {structured.outputs.join(", ")}</p>
        )}
      </section>
    );
  }
  return (
    <pre
      className="published-code-preview"
      data-testid="published-code-preview"
      aria-label="Read-only published code"
    >
      <code>{content || "No code content returned by the server."}</code>
    </pre>
  );
}

function DecisionArtifactPreview({
  presentation,
  content,
}: {
  presentation: CadencePublishedArtifactPresentation;
  content?: CadencePublishedDecisionContent;
}) {
  const rows = [
    ["Template", presentation.template],
    ["Confidence", presentation.confidence],
    ["Supersedes", presentation.supersedesArtifactId],
  ].filter(([, value]) => value !== undefined && value !== "");
  return (
    <section
      className="published-decision-preview"
      data-testid="published-decision-preview"
      aria-label="Read-only published decision"
    >
      <p className="published-decision-kicker">Published decision · Read-only</p>
      {presentation.summary && <p>{presentation.summary}</p>}
      {content ? (
        <>
          <h3>{content.question}</h3>
          <p><strong>Recommendation:</strong> {content.recommendation}</p>
          <h4>Criteria</h4>
          <ul>
            {content.criteria.map((criterion) => (
              <li key={criterion.name}>
                {criterion.met ? "Met" : "Not met"} · {criterion.name}
                {criterion.note ? ` — ${criterion.note}` : ""}
              </li>
            ))}
          </ul>
          <h4>Evidence Artifacts</h4>
          <ul>{content.basedOnArtifactIds.map((id) => <li key={id}>{id}</li>)}</ul>
          <h4>Risks</h4>
          <ul>{content.risks.map((risk) => <li key={risk}>{risk}</li>)}</ul>
          <h4>Next actions</h4>
          <ol>{content.nextActions.map((action) => <li key={action}>{action}</li>)}</ol>
        </>
      ) : (
        <p role="alert">Decision body is unavailable; only its attested metadata can be shown.</p>
      )}
      {rows.length > 0 && (
        <dl>
          {rows.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{String(value)}</dd>
            </div>
          ))}
        </dl>
      )}
      {presentation.citations?.length ? (
        <div className="published-decision-citations">
          <strong>Supporting citations</strong>
          <ol>
            {presentation.citations.map((citation) => (
              <li key={citation}>{citation}</li>
            ))}
          </ol>
        </div>
      ) : null}
    </section>
  );
}

function ImageArtifactPreview({ artifact }: { artifact: CadenceArtifact }) {
  const imageSource = safeImageSource(
    artifact.previewUrl ?? artifact.preview?.content ?? artifact.downloadUrl,
  );
  const fallbackUrl = safeArtifactUrl(
    artifact.downloadUrl ?? artifact.previewUrl,
  );
  const conceptual = isConceptualIllustrationArtifact(artifact);
  const publishedFigure = artifact.publishedPresentation?.format === "figure";
  return (
    <div
      className="artifact-image-preview"
      data-testid={
        publishedFigure ? "figure-artifact-preview" : "image-artifact-preview"
      }
    >
      <p
        className="conceptual-image-disclaimer"
        data-testid="conceptual-image-disclaimer"
        role="note"
      >
        {publishedFigure ? (
          <>已发布图：此处只读展示服务器归档的图像 Artifact。</>
        ) : conceptual ? (
          <>
          概念示意图：仅用于表达，不是坐标支持的结构证据，也不能用于 residue 选择或结构结论。
          </>
        ) : (
          <>
            图像预览：不是坐标支持的结构证据，不能用于 residue 选择或结构结论。
          </>
        )}
      </p>
      {imageSource ? (
        <img src={imageSource} alt={artifact.name} loading="lazy" />
      ) : (
        <div className="artifact-image-placeholder">
          <Dna size={28} />
          <span>当前没有可用的图像预览。</span>
        </div>
      )}
      {fallbackUrl && (
        <a
          className="artifact-open-fallback"
          href={fallbackUrl}
          target="_blank"
          rel="noreferrer"
          download={artifact.downloadUrl ? artifact.name : undefined}
        >
          {artifact.downloadUrl ? "下载原始图像" : "打开图像"}
        </a>
      )}
    </div>
  );
}

function PdfArtifactPreview({ artifact }: { artifact: CadenceArtifact }) {
  const sourceUrl = safeArtifactUrl(artifact.downloadUrl);
  if (!sourceUrl) {
    return (
      <div className="pdf-evidence-source-error" role="alert">
        <strong>无法打开原始 PDF</strong>
        <span>
          此 Artifact 未提供受控的原始文件地址，因此系统不会猜测或访问外部 URL。
        </span>
      </div>
    );
  }
  return (
    <Suspense
      fallback={
        <div className="artifact-detail-state" role="status" aria-busy="true">
          <LoaderCircle size={17} aria-hidden="true" />
          正在准备 PDF 原始证据阅读器…
        </div>
      }
    >
      <PdfEvidenceViewer
        artifactName={artifactDisplayName(artifact)}
        sourceUrl={sourceUrl}
      />
    </Suspense>
  );
}

function ScientificViewTablePreview({ rows }: { rows: ScientificUiRow[] }) {
  const columns = [
    ...new Set(rows.flatMap((row) => Object.keys(row.values))),
  ];
  if (!rows.length || !columns.length || columns.length > 100) {
    return (
      <p
        className="artifact-table-fallback"
        data-testid="table-preview-fallback"
        role="alert"
      >
        Prepared table view is empty or exceeds the 100 column browser preview
        limit.
      </p>
    );
  }
  return (
    <div
      className="artifact-table-wrap"
      data-testid="scientific-view-table-preview"
      role="region"
      aria-label="Prepared scientific table view"
    >
      <table>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              {columns.map((column) => {
                const value = row.values[column];
                return (
                  <td key={column}>
                    {value === undefined || value === null
                      ? ""
                      : typeof value === "object"
                        ? JSON.stringify(value)
                        : String(value)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function safeArtifactUrl(value?: string) {
  if (!value) return undefined;
  return value.startsWith("/api/") || value.startsWith("blob:")
    ? value
    : undefined;
}

function safeImageSource(value?: string) {
  if (!value) return undefined;
  if (value.startsWith("data:image/")) return value;
  return safeArtifactUrl(value);
}

function StructuredTablePreview({
  content,
}: {
  content: CadencePublishedTableContent;
}) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<{
    column: string;
    direction: "ascending" | "descending";
  }>();
  const [page, setPage] = useState(0);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const filteredRows = useMemo(() => {
    const rows = normalizedQuery
      ? content.rows.filter((row) =>
          content.columns.some((column) =>
            formatPublishedTableCell(row[column])
              .toLocaleLowerCase()
              .includes(normalizedQuery),
          ),
        )
      : content.rows;
    if (!sort) return rows;
    return [...rows].sort((left, right) => {
      const comparison = comparePublishedTableValues(
        left[sort.column],
        right[sort.column],
      );
      return sort.direction === "ascending" ? comparison : -comparison;
    });
  }, [content.columns, content.rows, normalizedQuery, sort]);
  const pageSize = 50;
  const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const pageStart = currentPage * pageSize;
  const visibleRows = filteredRows.slice(pageStart, pageStart + pageSize);
  const changeSort = (column: string) => {
    setPage(0);
    setSort((current) =>
      current?.column === column
        ? {
            column,
            direction:
              current.direction === "ascending" ? "descending" : "ascending",
          }
        : { column, direction: "ascending" },
    );
  };
  const exportCsv = () => {
    const csv = publishedTableCsv(content.columns, filteredRows);
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = "published-table.csv";
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(href);
  };
  return (
    <section className="published-table-preview" data-testid="published-table-preview">
      {content.notes && <p className="published-table-notes">{content.notes}</p>}
      <div className="published-table-controls">
        <label className="published-table-search">
          <span>检索表格</span>
          <input
            data-testid="published-table-search"
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(0);
            }}
            placeholder="检索当前表格"
          />
        </label>
        <div className="published-table-actions">
          <span data-testid="published-table-row-count">
            {filteredRows.length === content.rows.length
              ? `${content.rows.length} 行`
              : `${filteredRows.length} / ${content.rows.length} 行`}
          </span>
          <button
            className="published-table-export"
            data-testid="published-table-export-csv"
            type="button"
            onClick={exportCsv}
            title="导出当前检索和排序结果为 CSV"
          >
            <Download size={14} aria-hidden="true" />
            导出 CSV
          </button>
        </div>
      </div>
      <p className="published-table-download-note" role="note">
        导出 CSV 使用当前检索和排序结果；右上角下载按钮始终下载原始 JSON 产物。
      </p>
      <div
        className="artifact-table-wrap"
        data-testid="table-preview"
        data-delimiter="json"
        tabIndex={0}
        role="region"
        aria-label="已发布结构化表格"
      >
        <table>
          <thead>
            <tr>
              {content.columns.map((column) => {
                const isSorted = sort?.column === column;
                return (
                  <th
                    key={column}
                    aria-sort={
                      isSorted ? sort.direction : "none"
                    }
                  >
                    <button
                      className="published-table-sort"
                      type="button"
                      onClick={() => changeSort(column)}
                      title={`按 ${column} 排序`}
                    >
                      {column}
                      {isSorted && (
                        <span aria-hidden="true">
                          {sort.direction === "ascending" ? " ↑" : " ↓"}
                        </span>
                      )}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, rowIndex) => (
              <tr key={pageStart + rowIndex}>
                {content.columns.map((column) => (
                  <td key={column}>{formatPublishedTableCell(row[column])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {visibleRows.length === 0 && (
          <p className="published-table-empty" role="status">
            没有匹配的行。
          </p>
        )}
      </div>
      {filteredRows.length > pageSize && (
        <nav className="published-table-pagination" aria-label="表格分页">
          <button
            type="button"
            disabled={currentPage === 0}
            onClick={() => setPage((value) => Math.max(0, value - 1))}
          >
            上一页
          </button>
          <span>
            {currentPage + 1} / {pageCount}
          </span>
          <button
            type="button"
            disabled={currentPage >= pageCount - 1}
            onClick={() =>
              setPage((value) => Math.min(pageCount - 1, value + 1))
            }
          >
            下一页
          </button>
        </nav>
      )}
    </section>
  );
}

function formatPublishedTableCell(value: string | number | boolean | null) {
  if (value === null) return "";
  return typeof value === "boolean" ? (value ? "Yes" : "No") : String(value);
}

function comparePublishedTableValues(
  left: string | number | boolean | null,
  right: string | number | boolean | null,
) {
  if (left === right) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }
  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function publishedTableCsv(
  columns: string[],
  rows: Array<Record<string, string | number | boolean | null>>,
) {
  const records = [columns, ...rows.map((row) => columns.map((column) => row[column]))];
  return `${records.map((record) => record.map(publishedTableCsvCell).join(",")).join("\r\n")}\r\n`;
}

function publishedTableCsvCell(value: string | number | boolean | null) {
  let text = value === null ? "" : String(value);
  // Spreadsheet programs evaluate leading formula characters even when the
  // cell originated from an otherwise immutable publication. Escape them at
  // export time while preserving the canonical table rows in the Artifact.
  if (/^[\t\r\n ]*[=+\-@]/u.test(text)) text = `'${text}`;
  return `"${text.replaceAll('"', '""')}"`;
}

function TablePreview({ content }: { content: string }) {
  const result = parseDelimitedTable(content);
  if (!result.ok) {
    return (
      <p
        className="artifact-table-fallback"
        data-testid="table-preview-fallback"
        role="alert"
      >
        {result.error}
      </p>
    );
  }
  const [head, ...body] = result.rows;
  return (
    <div
      className="artifact-table-wrap"
      data-testid="table-preview"
      data-delimiter={result.delimiter === "\t" ? "tsv" : "csv"}
    >
      <table>
        <thead>
          <tr>
            {head.map((cell, index) => (
              <th key={`${cell}-${index}`}>{cell}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {head.map((_, index) => (
                <td key={index}>{row[index] ?? ""}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const DELIMITED_TABLE_LIMITS = {
  bytes: 256 * 1024,
  rows: 500,
  columns: 100,
  cellCharacters: 16 * 1024,
} as const;

type DelimitedTableResult =
  | { ok: true; delimiter: "," | "\t"; rows: string[][] }
  | { ok: false; error: string };

function parseDelimitedTable(content: string): DelimitedTableResult {
  if (new TextEncoder().encode(content).byteLength > DELIMITED_TABLE_LIMITS.bytes) {
    return {
      ok: false,
      error: "Table preview exceeds the 256 KB browser preview limit.",
    };
  }
  const source = content.replace(/^\uFEFF/u, "");
  const delimiter = delimitedTableDelimiter(source);
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  let closedQuote = false;

  const pushField = (): DelimitedTableResult | undefined => {
    if (field.length > DELIMITED_TABLE_LIMITS.cellCharacters) {
      return {
        ok: false,
        error: "A table cell exceeds the 16 KB browser preview limit.",
      };
    }
    row.push(field);
    field = "";
    closedQuote = false;
    if (row.length > DELIMITED_TABLE_LIMITS.columns) {
      return {
        ok: false,
        error: "Table preview exceeds the 100 column browser preview limit.",
      };
    }
    return undefined;
  };

  const pushRow = (): DelimitedTableResult | undefined => {
    const fieldError = pushField();
    if (fieldError) return fieldError;
    if (row.some((cell) => cell.length > 0)) rows.push(row);
    row = [];
    if (rows.length > DELIMITED_TABLE_LIMITS.rows) {
      return {
        ok: false,
        error: "Table preview exceeds the 500 row browser preview limit.",
      };
    }
    return undefined;
  };

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];
    if (quoted) {
      if (character === '"') {
        if (source[index + 1] === '"') {
          field += '"';
          index += 1;
        } else {
          quoted = false;
          closedQuote = true;
        }
      } else {
        field += character;
      }
      continue;
    }
    if (closedQuote && character !== delimiter && character !== "\r" && character !== "\n") {
      return {
        ok: false,
        error: "Table preview contains characters after a closing quote.",
      };
    }
    if (character === '"') {
      if (field.length > 0 || closedQuote) {
        return {
          ok: false,
          error: "Table preview contains an invalid quote in an unquoted field.",
        };
      }
      quoted = true;
      continue;
    }
    if (character === delimiter) {
      const fieldError = pushField();
      if (fieldError) return fieldError;
      continue;
    }
    if (character === "\r" || character === "\n") {
      if (character === "\r" && source[index + 1] === "\n") index += 1;
      const rowError = pushRow();
      if (rowError) return rowError;
      continue;
    }
    field += character;
  }

  if (quoted) {
    return {
      ok: false,
      error: "Table preview contains an unterminated quoted field.",
    };
  }
  if (field.length || row.length || closedQuote) {
    const rowError = pushRow();
    if (rowError) return rowError;
  }
  if (!rows.length) {
    return { ok: false, error: "No table rows returned." };
  }
  const columnCount = rows[0].length;
  if (rows.some((candidate) => candidate.length !== columnCount)) {
    return {
      ok: false,
      error: "Table preview contains rows with inconsistent column counts.",
    };
  }
  return { ok: true, delimiter, rows };
}

function delimitedTableDelimiter(content: string): "," | "\t" {
  let commas = 0;
  let tabs = 0;
  let quoted = false;
  for (let index = 0; index < content.length; index += 1) {
    const character = content[index];
    if (character === '"') {
      if (quoted && content[index + 1] === '"') {
        index += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }
    if (quoted) continue;
    if (character === "\r" || character === "\n") break;
    if (character === ",") commas += 1;
    if (character === "\t") tabs += 1;
  }
  return tabs > commas ? "\t" : ",";
}

function isParquetArtifact(artifact: CadenceArtifact): boolean {
  const mimeType = artifact.mimeType
    ?.split(";", 1)[0]
    .trim()
    .toLowerCase();
  if (
    mimeType === "application/vnd.apache.parquet" ||
    mimeType === "application/x-parquet" ||
    mimeType === "application/parquet"
  ) {
    return true;
  }
  if (artifact.kind?.trim().toLowerCase() === "parquet") return true;
  const name = (artifact.path ?? artifact.name)
    .trim()
    .toLowerCase()
    .split(/[?#]/u, 1)[0];
  return name.endsWith(".parquet");
}

function isPdfArtifact(artifact: Pick<CadenceArtifact, "mimeType">): boolean {
  return artifact.mimeType?.split(";", 1)[0].trim().toLowerCase() === "application/pdf";
}

const structureMimeFormats: Record<string, CadenceStructureFormat> = {
  "application/cif": "cif",
  "application/mmcif": "cif",
  "application/x-cif": "cif",
  "application/x-mmcif": "cif",
  "application/x-pdb": "pdb",
  "chemical/cif": "cif",
  "chemical/mmcif": "cif",
  "chemical/pdb": "pdb",
  "chemical/x-cif": "cif",
  "chemical/x-mmcif": "cif",
  "chemical/x-pdb": "pdb",
};

function structureFormatFromArtifact(
  artifact: CadenceArtifact,
): CadenceStructureFormat | undefined {
  const mimeType = artifact.mimeType
    ?.split(";", 1)[0]
    .trim()
    .toLowerCase();
  if (mimeType && structureMimeFormats[mimeType])
    return structureMimeFormats[mimeType];
  const kind = artifact.kind?.trim().toLowerCase();
  if (kind === "pdb") return "pdb";
  if (kind === "cif" || kind === "mmcif") return "cif";
  const name = (artifact.path ?? artifact.name)
    .trim()
    .toLowerCase()
    .split(/[?#]/u, 1)[0];
  if (name.endsWith(".pdb") || name.endsWith(".ent")) return "pdb";
  if (name.endsWith(".cif") || name.endsWith(".mmcif")) return "cif";
  return undefined;
}

function uniqueStableResidues(
  residues: CadenceStableResidue[],
): CadenceStableResidue[] {
  return [
    ...new Map(
      residues.map((residue) => [residue.canonical_key, residue]),
    ).values(),
  ];
}

function stableResidueLabel(residue: CadenceStableResidue): string {
  const name = residue.residue_name ?? "Residue";
  const chain = residue.chain_id || "blank chain";
  return `Model ${residue.model_num} · ${name} ${chain}:${residue.auth_seq_id}${residue.insertion_code ?? ""}`;
}

function stableResiduePosition(
  residue?: CadenceStableResidue,
): number | undefined {
  if (!residue) return undefined;
  const position = Number(residue.auth_seq_id);
  return Number.isInteger(position) ? position : undefined;
}

function stableResiduePositions(
  residues: CadenceStableResidue[],
): number[] {
  return [
    ...new Set(
      residues.flatMap((residue) => {
        const position = stableResiduePosition(residue);
        return position === undefined ? [] : [position];
      }),
    ),
  ];
}

function changedStableResidue(
  previous: CadenceStableResidue[],
  next: CadenceStableResidue[],
): CadenceStableResidue | undefined {
  const previousKeys = new Set(
    previous.map((residue) => residue.canonical_key),
  );
  const nextKeys = new Set(next.map((residue) => residue.canonical_key));
  return (
    next.find((residue) => !previousKeys.has(residue.canonical_key)) ??
    previous.find((residue) => !nextKeys.has(residue.canonical_key))
  );
}

function structureSelection(
  artifactId: string,
  artifactRef: string,
  format: CadenceStructureFormat | undefined,
  coordinateSha256: string | undefined,
  sourceRevision: number | undefined,
  descriptorRevision: number | undefined,
  residues: CadenceStableResidue[],
): StructureScientificSelection | undefined {
  if (!format) return undefined;
  const stableResidues = uniqueStableResidues(residues);
  return {
    renderer: "structure",
    artifactId,
    artifactRef,
    format,
    coordinateSha256,
    sourceRevision,
    descriptorRevision,
    ids: stableResidues.map((residue) => residue.canonical_key),
    residues: stableResidues,
    legacyResiduePositions: stableResiduePositions(stableResidues),
  };
}

function toggleStructureResidue(
  selection: StructureScientificSelection,
  residue: CadenceStableResidue,
): StructureScientificSelection {
  const selected = selection.ids.includes(residue.canonical_key);
  const residues = selected
    ? selection.residues.filter(
        (candidate) => candidate.canonical_key !== residue.canonical_key,
      )
    : [...selection.residues, residue];
  return {
    ...selection,
    ids: residues.map((candidate) => candidate.canonical_key),
    residues,
    legacyResiduePositions: stableResiduePositions(residues),
  };
}

function StructurePreview({
  artifactId,
  artifactRef,
  content,
  format,
  coordinateSha256,
  sourceRevision,
  descriptorRevision,
  selection,
  onSelectionChange,
}: {
  artifactId: string;
  artifactRef: string;
  content: string;
  format?: CadenceStructureFormat;
  coordinateSha256?: string;
  sourceRevision?: number;
  descriptorRevision?: number;
  selection?: StructureScientificSelection;
  onSelectionChange(selection: StructureScientificSelection): void;
}) {
  const selectedResidues = selection?.residues ?? [];
  const currentSelection = structureSelection(
    artifactId,
    artifactRef,
    format,
    coordinateSha256,
    sourceRevision,
    descriptorRevision,
    selectedResidues,
  );
  const toggleResidue = useCallback(
    (residue: CadenceStableResidue) => {
      if (!currentSelection) return;
      onSelectionChange(toggleStructureResidue(currentSelection, residue));
    },
    [currentSelection, onSelectionChange],
  );

  if (!format) {
    return (
      <div
        className="structure-preview"
        data-testid="structure-preview"
        data-structure-format="unknown"
        data-coordinate-sha256={coordinateSha256 ?? "unverified"}
        data-structure-renderer="molstar"
        data-render-status="error"
      >
        <span
          className="structure-fallback"
          data-testid="structure-fallback"
          role="alert"
        >
          Unsupported structure format. Provide an exact .pdb, .cif, or .mmcif
          extension, MIME type, or format kind.
        </span>
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div
          className="structure-preview"
          data-testid="structure-preview"
          data-structure-format={format}
          data-coordinate-sha256={coordinateSha256 ?? "unverified"}
          data-structure-renderer="molstar"
          data-render-status="loading"
          aria-busy="true"
        />
      }
    >
      <MolstarStructureViewer
        artifactId={artifactId}
        artifactRef={artifactRef}
        content={content}
        format={format}
        coordinateSha256={coordinateSha256}
        selectedResidues={selectedResidues}
        onResidueToggle={toggleResidue}
      />
    </Suspense>
  );
}

function DelegatedAgentDetail({
  agent,
  events,
  onClose,
}: {
  agent: CadenceDelegatedAgent;
  events: CadenceAgentEvent[];
  onClose(): void;
}) {
  const [remoteTimeline, setRemoteTimeline] = useState<CadenceAgentEvent[]>();
  const [timelineError, setTimelineError] = useState<string>();
  useEffect(() => {
    let cancelled = false;
    setRemoteTimeline(undefined);
    setTimelineError(undefined);
    void api
      .getProjectChildThreadTimeline(agent.projectId, agent.childThreadId)
      .then((response) => {
        if (cancelled) return;
        const records = Array.isArray(response.records) ? response.records : [];
        setRemoteTimeline(
          records.flatMap((record, index) => {
            const row = eventRecord(record);
            const event = eventRecord(row?.event);
            const type = eventText(event?.type);
            if (!event || !type) return [];
            const sourceIndex =
              typeof row?.source_index === "number" &&
              Number.isFinite(row.source_index)
                ? row.source_index
                : index;
            return [
              {
                key: `${agent.childThreadId}:${sourceIndex}:${type}`,
                seq: sourceIndex,
                source: "wecode.child_rollout",
                sourceIndex,
                projectId: agent.projectId,
                agentId: agent.rosterAgentId ?? agent.childThreadId,
                threadId: agent.childThreadId,
                submissionId: "child-thread-history",
                event: { ...event, type },
              } as CadenceAgentEvent,
            ];
          }),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setTimelineError("协作者运行详情暂时无法读取，请稍后重试。");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [agent.childThreadId, agent.projectId, agent.rosterAgentId]);
  const timeline = livePublicDetailEvents(
    remoteTimeline ?? events.filter((event) => event.threadId === agent.childThreadId),
  );
  return (
    <section
      className="delegated-agent-detail"
      data-testid={`delegated-agent-detail-${agent.childThreadId}`}
      aria-label="协作者只读时间线"
    >
      <div className="agent-detail-head">
        <div>
          <h3>
            <span className="agent-avatar agent-avatar-small">
              <Network size={14} aria-hidden="true" />
            </span>
            {agent.title}
          </h3>
          <p>{agent.subtitle ?? "VCode 子智能体"}</p>
        </div>
        <button
          className="icon-button"
          type="button"
          onClick={onClose}
          aria-label="关闭协作者时间线"
        >
          <X size={15} />
        </button>
      </div>
      {timeline.length ? (
        <ol className="delegated-agent-timeline">
          {timeline.map((event) => (
            <li key={event.key} data-event-type={event.event.type}>
              <strong>{liveActivityFromEvent(event) ?? event.event.type}</strong>
              <small>
                {event.source
                  ? `${event.source} #${event.sourceIndex ?? event.seq}`
                  : `事件 #${event.seq}`}
              </small>
            </li>
          ))}
        </ol>
      ) : timelineError ? (
        <p className="delegated-agent-detail-empty" role="alert">
          {timelineError}
        </p>
      ) : remoteTimeline === undefined ? (
        <p className="delegated-agent-detail-empty" role="status">
          正在读取协作者的公开运行详情…
        </p>
      ) : (
        <p className="delegated-agent-detail-empty" role="status">
          当前项目事件流尚未公开此协作者的时间线。不会使用父 Agent 摘要代替。
        </p>
      )}
    </section>
  );
}

function AgentDetail({
  agent,
  readOnly,
  residues,
  btwValue,
  selectedResidues,
  onResidueSelect,
  onBTWChange,
  onBTWSubmit,
  btwSending,
  btwResponse,
  btwSendError,
  onBTWResponseDismiss,
  onClose,
}: {
  agent: CadenceAgent;
  readOnly: boolean;
  residues: CadenceResidue[];
  btwValue: string;
  selectedResidues: number[];
  onResidueSelect(position: number): void;
  onBTWChange(value: string): void;
  onBTWSubmit(): void;
  btwSending: boolean;
  btwResponse?: CadenceBTWResponse;
  btwSendError?: string;
  onBTWResponseDismiss?: (requestId: string) => void;
  onClose(): void;
}) {
  const btwUnavailable = !readOnly && agent.btwAvailable !== true;
  const responseStatus = btwSendError ? "error" : btwResponse?.status;
  const responseLabel =
    responseStatus === "loading"
      ? `BTW · 正在向 ${agent.name} 查询中…`
      : responseStatus === "error"
        ? "BTW · 独立回答获取失败"
        : `BTW · ${agent.name} 的独立回答`;
  const responseText =
    btwSendError ??
    (btwResponse?.status === "loading"
      ? "正在获取独立回答…"
      : btwResponse?.status === "error"
        ? btwResponse.error
        : btwResponse?.text);
  return (
    <section className="agent-detail" data-testid={`agent-detail-${agent.id}`}>
      <div className="agent-detail-head">
        <div>
          <h3>
            <span
              className="agent-avatar agent-avatar-small"
              style={{ background: identityAccent(agent.id) }}
            >
              {agent.name.slice(0, 1)}
            </span>
            {agent.name}
          </h3>
        </div>
        <button
          className="icon-button"
          type="button"
          onClick={onClose}
          aria-label="关闭 Agent 详情"
        >
          <X size={15} />
        </button>
      </div>
      {agent.finding && (
        <div className="detail-finding">
          <strong>{agent.finding}</strong>
        </div>
      )}
      {residues.length ? (
        <div className="mini-protein" aria-label="选择相关蛋白残基">
          <div>
            {residues.slice(0, 6).map((residue) => (
              <button
                data-testid={`protein-residue-${residue.position}`}
                className={`mini-residue ${selectedResidues.includes(residue.position) ? "mini-residue-selected" : ""}`}
                type="button"
                key={residue.position}
                onClick={() => onResidueSelect(residue.position)}
              >
                <b>{residue.aminoAcid}</b>
                <small>{residue.position}</small>
              </button>
            ))}
          </div>
        </div>
      ) : null}
      <div className="btw-composer" data-testid={`btw-${agent.id}`}>
        <div className="btw-label">
          <MessageSquareMore size={14} />
          {readOnly
            ? "演示模式 · BTW 仅供查看"
            : btwUnavailable
              ? "BTW · 当前 Agent 不可用"
              : "BTW · 不会打断主任务"}
        </div>
        <textarea
          value={btwValue}
          disabled={readOnly || btwUnavailable}
          onChange={(event) => onBTWChange(event.target.value)}
          onKeyDown={(event) => {
            if (
              event.key === "Enter" &&
              (event.metaKey || event.ctrlKey) &&
              btwValue.trim() &&
              !btwSending &&
              btwResponse?.status !== "loading"
            ) {
              event.preventDefault();
              onBTWSubmit();
            }
          }}
          placeholder={
            readOnly
              ? "演示模式仅供查看"
              : btwUnavailable
                ? "当前 Agent 暂无可用的 BTW 通道"
                : `向 ${agent.name} 追问…`
          }
          rows={2}
          aria-label={`向 ${agent.name} 发送 BTW`}
        />
        <button
          data-testid="btw-submit"
          aria-label="发送 BTW"
          className="send-icon-button"
          type="button"
          onClick={onBTWSubmit}
          disabled={
            readOnly ||
            btwUnavailable ||
            !btwValue.trim() ||
            btwSending ||
            btwResponse?.status === "loading"
          }
        >
          <Send size={15} />
        </button>
        {responseStatus && responseText && (
          <div
            className={`btw-response btw-response-${responseStatus}`}
            data-testid="btw-response"
            role={responseStatus === "error" ? "alert" : "status"}
          >
            <span>{responseLabel}</span>
            <p>{responseText}</p>
            {btwResponse && onBTWResponseDismiss && (
              <button
                type="button"
                onClick={() => onBTWResponseDismiss(btwResponse.requestId)}
                aria-label="关闭 BTW 回答"
              >
                <X size={12} />
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

type CadenceComposerProps = {
  value: string;
  lastSent: string;
  sendError?: string;
  sending: boolean;
  running: boolean;
  steerAvailable: boolean;
  stopping: boolean;
  onStop?: () => void;
  context: CadenceContext;
  projectName?: string;
  agentName?: string;
  artifactName?: string;
  scopeNotice?: string;
  intent: CadenceComposerIntent;
  capabilities: CadenceComposerCapability[];
  availableAttachments: CadenceAttachment[];
  artifacts: CadenceArtifact[];
  agents: CadenceAgent[];
  turns: CadenceTurn[];
  references: CadenceComposerReference[];
  onClearAgent?(): void;
  attachments: CadenceAttachment[];
  uploading: boolean;
  uploadError?: string;
  onRetryUpload?: () => void;
  onChange(value: string): void;
  textAreaRef: RefObject<HTMLTextAreaElement>;
  onSubmit(): Promise<void> | void;
  onUploadFiles?: (files: File[]) => void;
  onAttachProjectInput?(attachmentId: string): void;
  onIntentChange(intent: CadenceComposerIntent): void;
  onAddReference(reference: CadenceComposerReference): void;
  onRemoveReference(referenceId: string): void;
  onRemoveAttachment?: (attachmentId: string) => void;
  onOpenAttachment?(attachment: CadenceAttachment): void;
  readOnly: boolean;
  disabled: boolean;
  disabledReason?: string;
  permissions: CadencePermissionRequest[];
  permissionError?: string;
  pendingPermissionRequestId?: string;
  onPermissionDecision?: (
    request: CadencePermissionRequest,
    decision: "approve" | "deny",
  ) => void;
  pinned?: boolean;
  compact?: boolean;
  onMobileFocusChange?(focused: boolean): void;
  artifactInquiry?: {
    artifact: CadenceArtifact;
    revision?: number;
    selection?: ScientificSelection;
    collapsed?: boolean;
    detailState?: "idle" | "loading" | "ready" | "error";
    detailError?: string;
    turns: CadenceTurn[];
    readOnly: boolean;
    composerDisabled: boolean;
    composerDisabledReason?: string;
    onSubmit?: CadenceBioAppProps["onArtifactComposerSubmit"];
    onReportTextReplacementApply?: CadenceBioAppProps["onReportTextReplacementApply"];
    onDetailRetry?: (artifactId: string) => void;
    onSelectionChange?: (selection: ScientificSelection) => void;
    onClearScope(): void;
    onExpand?(): void;
    onCollapse?(): void;
    onReturnToDraft?(artifactId: string): void;
  };
};

function CadenceComposer(props: CadenceComposerProps) {
  const {
    value,
    lastSent,
    sendError,
    sending,
    running,
    steerAvailable,
    stopping,
    onStop,
    context,
    projectName,
    agentName,
    artifactName,
    scopeNotice,
    intent,
    capabilities,
    availableAttachments,
    artifacts,
    agents,
    turns,
    references,
    onClearAgent,
    attachments,
    uploading,
    uploadError,
    onRetryUpload,
    onChange,
    textAreaRef,
    onSubmit,
    onUploadFiles,
    onAttachProjectInput,
    onIntentChange,
    onAddReference,
    onRemoveReference,
    onRemoveAttachment,
    onOpenAttachment,
    readOnly,
    disabled,
    disabledReason,
    permissions,
    permissionError,
    pendingPermissionRequestId,
    onPermissionDecision,
    pinned,
    compact,
    onMobileFocusChange,
    artifactInquiry,
  } = props;

  const inquiryContent = artifactInquiry ? (
    <div
      className={`artifact-inquiry-composer artifact-inquiry-composer-inline ${artifactInquiry.collapsed ? "artifact-inquiry-composer-collapsed" : ""}`}
      data-testid="artifact-inquiry-composer"
    >
      {artifactInquiry.collapsed && (
        <div className="artifact-inquiry-mobile-disclosure">
          <button
            data-testid="artifact-inquiry-expand"
            type="button"
            onClick={artifactInquiry.onExpand}
            aria-label={`展开 Artifact 询问：${scientificSelectionSummary(artifactInquiry.selection) || "当前图表选择"}`}
          >
            <span>{scientificSelectionSummary(artifactInquiry.selection) || "当前图表选择"}</span>
            <span>
              向 Owner 询问
              <Send size={15} aria-hidden="true" />
            </span>
          </button>
        </div>
      )}
      <div className="artifact-inquiry-content">
        <ArtifactInquiryControls
          artifact={artifactInquiry.artifact}
          revision={artifactInquiry.revision}
          selection={artifactInquiry.selection}
          detailState={artifactInquiry.detailState}
          detailError={artifactInquiry.detailError}
          onDetailRetry={artifactInquiry.onDetailRetry}
          turns={artifactInquiry.turns}
          readOnly={artifactInquiry.readOnly}
          composerDisabled={artifactInquiry.composerDisabled}
          composerDisabledReason={artifactInquiry.composerDisabledReason}
          onSubmit={artifactInquiry.onSubmit}
          onReportTextReplacementApply={artifactInquiry.onReportTextReplacementApply}
          onSelectionChange={artifactInquiry.onSelectionChange}
          onClearScope={artifactInquiry.onClearScope}
          onCollapse={artifactInquiry.onCollapse}
          onReturnToDraft={artifactInquiry.onReturnToDraft}
        />
      </div>
    </div>
  ) : undefined;

  const composerContext = artifactInquiry ? (
    <>
      <span className="composer-context-label">Artifact 请求</span>
      <span className="context-chip">产物 · {artifactDisplayName(artifactInquiry.artifact)}</span>
      <span className="context-chip" data-testid="artifact-inquiry-recipient">
        Owner · {ownerLabel(artifactInquiry.artifact.owner) ?? "未声明"}
      </span>
      {artifactInquiry.revision !== undefined && (
        <span className="context-chip" data-testid="artifact-inquiry-revision">
          版本 · {artifactInquiry.revision}
        </span>
      )}
    </>
  ) : (
    <>
      <span className="composer-context-label">项目研究请求</span>
      {disabled && disabledReason && <span className="composer-readonly-note" role="note">{disabledReason}</span>}
      {projectName && <span className="context-chip">项目 · {projectName}</span>}
      {scopeNotice && <span className="context-chip" data-testid="project-composer-scope">{scopeNotice}</span>}
      {agentName && (
        <span className="context-chip context-chip-agent">
          指定专家 · {agentName}
          {onClearAgent && (
            <button
              className="context-chip-clear"
              data-testid="composer-clear-agent"
              type="button"
              onClick={onClearAgent}
              aria-label={`取消指定专家 ${agentName}`}
            >
              取消
            </button>
          )}
        </span>
      )}
      {artifactName && <span className="context-chip">产物 · {artifactName}</span>}
      {context.artifactRevision !== undefined && <span className="context-chip" data-testid="scientific-context-chip">产物版本 · {context.artifactRevision}</span>}
      {context.artifactExecutionId && <span className="context-chip" data-testid="artifact-execution-context">执行 · {context.artifactExecutionId}</span>}
      {context.scientificSelection && (
        <span className="context-chip" data-testid="scientific-context-chip">
          {context.scientificSelection.renderer === "report"
            ? `报告文本 · ${context.scientificSelection.quote.slice(0, 36)}${context.scientificSelection.quote.length > 36 ? "…" : ""}`
            : context.scientificSelection.renderer === "structure"
              ? `蛋白结构残基 · 已选 ${context.scientificSelection.residues.length} 项`
              : `${scientificSelectionLabel(context.scientificSelection.renderer)} · 已选 ${context.scientificSelection.ids.length} 项`}
        </span>
      )}
      {context.stableResidues.map((residue) => (
        <span className="context-chip" data-testid="stable-residue-context" key={residue.canonical_key}>
          稳定残基 · {stableResidueLabel(residue)}
        </span>
      ))}
      {context.residuePositions.map((position) => <span className="context-chip" key={position}>残基 · {position}</span>)}
      {lastSent && <span className="composer-last" title={lastSent}>已发送</span>}
    </>
  );

  return (
    <OpenScienceComposer
      allowAttachmentOnly={intent.kind === "project_investigation"}
      attachments={attachments}
      agents={agents}
      artifacts={artifacts}
      auxiliaryContent={inquiryContent}
      availableAttachments={availableAttachments}
      capabilities={capabilities}
      compact={compact}
      context={composerContext}
      disabled={disabled}
      disabledReason={disabledReason}
      inputRef={textAreaRef}
      interaction={
        <ProjectInteractionPrompt
          error={permissionError}
          onDecision={onPermissionDecision}
          pendingRequestId={pendingPermissionRequestId}
          permissions={permissions}
        />
      }
      onChange={onChange}
      onAddReference={onAddReference}
      onAttachProjectInput={onAttachProjectInput}
      onFocusChange={onMobileFocusChange}
      onIntentChange={onIntentChange}
      onOpenAttachment={onOpenAttachment}
      onRemoveAttachment={onRemoveAttachment}
      onRemoveReference={onRemoveReference}
      onRetryUpload={onRetryUpload}
      onSubmit={onSubmit}
      onUploadFiles={onUploadFiles}
      pinned={pinned}
      references={references}
      sendError={sendError}
      sending={sending}
      running={running}
      steerAvailable={steerAvailable}
      stopping={stopping}
      onStop={onStop}
      uploadError={uploadError}
      uploading={uploading}
      value={value}
      intent={intent}
      turns={turns}
    />
  );
}

function scientificSelectionLabel(renderer: string): string {
  return (
    {
      curve: "剂量反应数据点",
      flow: "流式分析对象",
      structure: "蛋白结构残基",
      "scientific-ui": "科学视图对象",
      "custom-app": "自定义分析对象",
    }[renderer] ?? "科学分析对象"
  );
}

function formatResearchElapsed(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1_000));
  if (totalSeconds < 60) return `${totalSeconds} 秒`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes} 分 ${seconds} 秒`;
  const hours = Math.floor(minutes / 60);
  return `${hours} 小时 ${minutes % 60} 分`;
}
